/**
 * Five Pillars Execution Hub
 * ==========================
 * Main page for the 5 Pillars weekly meeting system.
 * 
 * Features:
 * - Daily pillar updates
 * - Weekly meeting mode (L10-style)
 * - Comment threads
 * - Action items → Tasks
 * - Dashboard builder (drag & resize)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import '../styles/grid-layout.css';
import { 
  Target, Calendar, ClipboardList, ShieldCheck, DollarSign, CheckSquare,
  Play, Square, ChevronLeft, ChevronRight, MessageSquare, Plus,
  CheckCircle, Circle, AlertTriangle, Clock, Users, ArrowRight,
  FileText, Loader2, Send, MoreVertical, Check, X, Flag,
  ListChecks, Lightbulb, ChevronDown, ChevronUp, ExternalLink, Pencil, Save,
  GripVertical, Maximize2, Lock, Unlock, Eye, Trash2, GitBranch, RefreshCw,
  ArrowUpDown, CornerDownRight, Rocket, FileCode, TestTube, Link, Sparkles, Key, Settings,
  Smartphone, Bug, Timer, XCircle, Layers, Accessibility, LayoutGrid, Bot, Link2
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { WeeklyRecapSection } from '../components/worklog/WeeklyRecapSection';
import AgentTaskModal from '../components/AgentTaskModal';
// Removed MVPProgressWidget import - progress is now tracked in the pillar cards
import { Layout } from '../components/Layout';

// Progress Slider Component with local state
const ProgressSlider = ({ initialValue = 0, onValueChange, disabled = false, className = "" }) => {
  const [localValue, setLocalValue] = useState(initialValue);
  
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);
  
  const handleValueChange = (values) => {
    const newValue = values[0];
    setLocalValue(newValue);
    if (onValueChange) {
      onValueChange(newValue);
    }
  };
  
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Slider
        value={[localValue]}
        onValueChange={handleValueChange}
        max={100}
        step={5}
        disabled={disabled}
        className="flex-1 h-2"
      />
      <span className={`text-[10px] font-bold w-8 text-right ${localValue >= 50 ? 'text-green-600' : 'text-blue-500'}`}>
        {localValue}%
      </span>
    </div>
  );
};
// Import custom hooks for state management
import { 
  useScopeManagement, 
  useCostManagement, 
  useTimelineManagement, 
  useMeetingManagement,
  MEETING_PAGES,
  PHASE_CONFIG
} from '../components/five-pillars/hooks';

// Import extracted dialogs
import { 
  UpdateDialog, 
  StartMeetingDialog,
  AddScopeItemDialog,
  AddExpenseDialog,
  AddSalaryDialog,
  AddTestPeriodDialog,
  AddMilestoneDialog,
  ScopeModal,
  EnhancedScopeModal,
  CostModal,
  MilestoneDetailDialog,
  TimelineModal,
  ImplementationModal,
  DataSecurityModal
} from '../components/five-pillars/dialogs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pillar icons mapping
const PILLAR_ICONS = {
  scope: Target,
  timeline: Calendar,
  implementation: ClipboardList,
  data_security: ShieldCheck,
  cost: DollarSign,
  tasks: CheckSquare
};

// Progress Slider with Save Button - Only saves when checkmark is clicked
const ProgressSliderWithSave = ({ value, onSave, disabled = false }) => {
  const [localValue, setLocalValue] = React.useState(value);
  const [hasChanged, setHasChanged] = React.useState(false);
  
  // Sync with external value when it changes
  React.useEffect(() => {
    setLocalValue(value);
    setHasChanged(false);
  }, [value]);
  
  const handleChange = (values) => {
    setLocalValue(values[0]);
    setHasChanged(values[0] !== value);
  };
  
  const handleSave = () => {
    if (hasChanged) {
      onSave(localValue);
      setHasChanged(false);
    }
  };
  
  const handleCancel = () => {
    setLocalValue(value);
    setHasChanged(false);
  };
  
  return (
    <div className="flex items-center gap-1.5 pl-5">
      <Slider
        value={[localValue]}
        onValueChange={handleChange}
        max={100}
        step={5}
        disabled={disabled}
        className="flex-1 h-2"
      />
      <span className={`text-[10px] font-bold w-8 text-right ${localValue >= 50 ? 'text-green-600' : 'text-blue-500'}`}>
        {localValue}%
      </span>
      {hasChanged && (
        <>
          <button
            onClick={handleSave}
            className="p-0.5 rounded bg-green-500 hover:bg-green-600 text-white transition-colors"
            title="Save progress"
          >
            <Check className="h-3 w-3" />
          </button>
          <button
            onClick={handleCancel}
            className="p-0.5 rounded bg-slate-400 hover:bg-slate-500 text-white transition-colors"
            title="Cancel"
          >
            <X className="h-3 w-3" />
          </button>
        </>
      )}
    </div>
  );
};

// Pillar colors
const PILLAR_COLORS = {
  scope: 'indigo',
  timeline: 'blue',
  implementation: 'emerald',
  data_security: 'red',
  cost: 'amber',
  tasks: 'purple'
};

const getColorClasses = (color) => {
  const colors = {
    indigo: { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-600 dark:text-indigo-400', border: 'border-indigo-200 dark:border-indigo-800' },
    blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
    emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
    red: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
    amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
    purple: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' }
  };
  return colors[color] || colors.indigo;
};

// Meeting pages structure
// Helper to parse date strings as local dates (not UTC)
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  // For date-only strings like "2026-06-01", parse as local date
  // by appending T12:00:00 to avoid timezone shift issues
  if (dateStr.length === 10 && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return new Date(dateStr + 'T12:00:00');
  }
  return new Date(dateStr);
};

// Sortable Milestone Row Component for drag-and-drop reordering
const SortableMilestoneRow = ({ milestone, phaseConfig, onUpdate, onEdit, onDelete, canEdit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: milestone.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getMilestoneColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      case 'delayed': return 'bg-red-500';
      default: return 'bg-slate-400';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg ${isDragging ? 'ring-2 ring-blue-500 shadow-lg' : ''}`}
    >
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded touch-none"
        >
          <GripVertical className="h-4 w-4 text-slate-400" />
        </button>
        <span className="text-sm">{phaseConfig?.icon || '💻'}</span>
        <div className={`w-3 h-3 rounded-full ${getMilestoneColor(milestone.status)}`} />
        <div>
          <p className="font-medium text-sm">{milestone.name}</p>
          <p className="text-xs text-muted-foreground">
            {milestone.start_date && `${format(parseLocalDate(milestone.start_date), 'MMM d')} → `}
            {format(parseLocalDate(milestone.target_date), 'MMM d, yyyy')}
            {milestone.hub && ` • ${milestone.hub}`}
          </p>
        </div>
      </div>
      {canEdit && (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => onEdit(milestone)} className="h-7 w-7 p-0 text-blue-500">
            <Pencil className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(milestone.id)} className="h-7 w-7 p-0 text-red-500">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

const FivePillarsHub = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [viewingMeeting, setViewingMeeting] = useState(null);
  const [showMeetingPrep, setShowMeetingPrep] = useState(false);
  const [showPrepPicker, setShowPrepPicker] = useState(false);
  const [savedPreps, setSavedPreps] = useState([]);
  const [meetingPrep, setMeetingPrep] = useState({
    agenda: [], completed: [], issues: [], focus_this_week: [], discussion_topics: [], presenter_notes: '', name: ''
  }); // Read-only past meeting view

  // Load meeting from URL params
  useEffect(() => {
    const meetingId = searchParams.get('meeting');
    if (meetingId && !activeMeeting) {
      const loadMeeting = async () => {
        try {
          const res = await axios.get(
            `${API}/five-pillars-hub/meetings/${meetingId}`,
            { headers: getHeaders() }
          );
          setViewingMeeting(res.data.meeting);
        } catch (e) {
          console.error('Failed to load meeting:', e);
        }
      };
      loadMeeting();
    }
  }, [searchParams]);
  
  // State
  const [loading, setLoading] = useState(true);
  const [pillars, setPillars] = useState([]);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [selectedPillar, setSelectedPillar] = useState(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [users, setUsers] = useState([]);
  const [editingUpdate, setEditingUpdate] = useState(null); // For edit mode
  
  // Update form state
  const [updateForm, setUpdateForm] = useState({
    what_worked_on: '',
    whats_next: '',
    blockers: '',
    links: [],
    todos: []
  });
  
  // Meeting state
  const [meetingNotes, setMeetingNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('note'); // note, action, decision, blocker
  
  // Comments state (pillar-specific)
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState({});
  
  // Dashboard builder state
  const [isEditMode, setIsEditMode] = useState(false);
  const [gridLayout, setGridLayout] = useState(() => {
    // Try to load from localStorage
    const saved = localStorage.getItem('five_pillars_layout');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved layout:', e);
      }
    }
    // Default layout - 3 column grid with 6 pillars
    return [
      { i: 'scope', x: 0, y: 0, w: 4, h: 4, minW: 3, minH: 3 },
      { i: 'timeline', x: 4, y: 0, w: 4, h: 4, minW: 3, minH: 3 },
      { i: 'implementation', x: 8, y: 0, w: 4, h: 4, minW: 3, minH: 3 },
      { i: 'data_security', x: 0, y: 4, w: 4, h: 4, minW: 3, minH: 3 },
      { i: 'cost', x: 4, y: 4, w: 4, h: 4, minW: 3, minH: 3 },
      { i: 'tasks', x: 8, y: 4, w: 4, h: 4, minW: 3, minH: 3 },
      { i: 'weekly_recap', x: 0, y: 8, w: 4, h: 4, minW: 4, minH: 3 },
      { i: 'recent_meetings', x: 4, y: 8, w: 8, h: 4, minW: 4, minH: 3 }
    ];
  });
  const [containerWidth, setContainerWidth] = useState(1200);
  
  // Scope Management State
  const [showScopeModal, setShowScopeModal] = useState(false);
  const [scopeData, setScopeData] = useState({ mvp_items: [], future_items: [], items: [], metrics: {} });
  const [scopeLoading, setScopeLoading] = useState(false);
  const [showAddScopeItem, setShowAddScopeItem] = useState(false);
  const [editingScopeItem, setEditingScopeItem] = useState(null);
  const [scopeFormTab, setScopeFormTab] = useState('definition');
  const [generatingSpec, setGeneratingSpec] = useState(false);
  const [showAIPromptDialog, setShowAIPromptDialog] = useState(false);
  const [aiPrompt, setAIPrompt] = useState('');
  const [scopeForm, setScopeForm] = useState({
    name: '',
    description: '',
    category: 'mvp',
    status: 'pending',
    hub: 'none',
    // Feature Specification Fields
    acceptance_criteria: '',
    technical_requirements: '',
    related_files: [],
    testing_instructions: '',
    test_scenarios: '',
    success_indicators: '',
    dependencies_notes: '',
    reference_links: [],
    // Setup & Credentials Fields
    api_keys_needed: '',
    env_variables: '',
    third_party_credentials: '',
    setup_instructions: '',
    config_notes: '',
    // Security & Data Fields
    roles_permissions: '',
    data_access: '',
    auth_requirements: '',
    authorization_rules: '',
    data_validation: '',
    pii_handling: '',
    rate_limiting: '',
    audit_logging: '',
    // UI/UX Specs Fields
    wireframe_links: '',
    ui_components: '',
    responsive_requirements: '',
    accessibility_level: '',
    user_flow: '',
    error_states_ui: '',
    loading_states: '',
    empty_states: '',
    // Error Handling Fields
    error_scenarios: '',
    error_messages: '',
    fallback_behaviors: '',
    retry_logic: '',
    timeout_handling: '',
    logging_requirements: '',
    graceful_degradation: ''
  });
  
  // Implementation Plan State
  const [implementationData, setImplementationData] = useState({ items: [], metrics: {} });
  const [showImplementationModal, setShowImplementationModal] = useState(false);
  const [selectedImplementationItem, setSelectedImplementationItem] = useState(null);
  
  // Data & Security State
  const [dataSecurityData, setDataSecurityData] = useState({ items: [], metrics: {}, system_security: {} });
  const [showDataSecurityModal, setShowDataSecurityModal] = useState(false);
  const [selectedDataSecurityItem, setSelectedDataSecurityItem] = useState(null);
  
  // Selected Scope Item for detail view
  const [selectedScopeItem, setSelectedScopeItem] = useState(null);
  const [pendingScopeProgress, setPendingScopeProgress] = useState(null); // For progress slider with save button
  
  // Navigation collapse state for responsive cards
  const [navCollapsed, setNavCollapsed] = useState(false);
  
  // Mobile detection - disable drag/resize on touch devices
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    // Detect mobile/touch device
    const checkMobile = () => {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(isTouchDevice || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Cost Management State
  const [showCostModal, setShowCostModal] = useState(false);
  const [costData, setCostData] = useState({ expenses: [], salaries: [], totals: {} });
  const [costLoading, setCostLoading] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddSalary, setShowAddSalary] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    name: '',
    category: 'services',
    amount: '',
    frequency: 'one_time',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [salaryForm, setSalaryForm] = useState({
    name: '',
    role: '',
    amount: '',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    is_active: true
  });
  
  // Tasks Management State
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [tasksData, setTasksData] = useState({ tasks: [], stats: {} });
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [editingTask, setEditingTask] = useState(null); // For edit mode
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    assignee: '',
    due_date: ''
  });
  
  // Timeline Management State
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineData, setTimelineData] = useState({ milestones: [], mvp_deadline: '', project_start: '', testing_periods: [] });
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null); // For edit mode
  const [reorderMode, setReorderMode] = useState(false); // For drag-drop reordering
  const [milestoneForm, setMilestoneForm] = useState({
    name: '',
    description: '',
    start_date: '',
    target_date: '',
    status: 'pending',
    phase_type: 'development',
    hub: 'none',
    is_mvp_milestone: false,
    dependencies: [],
    parent_id: null,  // For sub-milestones
    // Phase-specific dates
    phase_dates: {
      planning: { start: '', end: '' },
      design: { start: '', end: '' },
      development: { start: '', end: '' },
      internal_testing: { start: '', end: '' },
      staff_testing: { start: '', end: '' },
      deploy_ready: { start: '', end: '' },
      full_testing: { start: '', end: '' }
    }
  });
  const [hoveredMilestone, setHoveredMilestone] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null); // For detail view modal
  const [editingDates, setEditingDates] = useState(false);
  const [dateForm, setDateForm] = useState({ project_start: '', mvp_deadline: '' });
  const [showAddTestPeriod, setShowAddTestPeriod] = useState(false);
  const [editingTestPeriod, setEditingTestPeriod] = useState(null); // For edit mode
  const [testPeriodForm, setTestPeriodForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    description: ''
  });
  const [showDependencies, setShowDependencies] = useState(true); // Toggle dependency arrows
  const [showCriticalPath, setShowCriticalPath] = useState(false); // Toggle critical path highlighting
  const [criticalPathIds, setCriticalPathIds] = useState([]); // Milestone IDs in critical path
  const [showTestingPeriods, setShowTestingPeriods] = useState(true); // Toggle testing periods visibility in Gantt
  const [expandedMilestones, setExpandedMilestones] = useState(new Set()); // Track expanded parent milestones
  const [showTestingPeriodsSection, setShowTestingPeriodsSection] = useState(false); // Toggle Testing Periods section (bottom) - default collapsed
  const [showAllMilestonesSection, setShowAllMilestonesSection] = useState(false); // Toggle All Milestones section - default collapsed
  const [expandedMilestonesInList, setExpandedMilestonesInList] = useState(new Set()); // Track expanded milestones in bottom list
  const [expandedCardMilestone, setExpandedCardMilestone] = useState(null); // Track expanded milestone in card mini Gantt
  const [deadlineFilter, setDeadlineFilter] = useState('all'); // Filter for deadlines: 'all', 'week', 'month', 'urgent'
  
  // User role state (for permission checking)
  const [userRole, setUserRole] = useState('');
  // Only admins can edit content
  const canEdit = ['super_admin', 'admin'].includes(userRole);
  // Layout editing restricted to admins only
  const canEditLayout = ['super_admin', 'admin'].includes(userRole);
  // CEO and President have read-only view with comment permissions
  const isExecutiveView = ['ceo', 'president', 'CEO', 'President'].includes(userRole);
  // CEO and President can add/reply to comments
  const canComment = ['super_admin', 'admin', 'ceo', 'president', 'CEO', 'President'].includes(userRole);

  // Executive Comments state (separate from pillar-specific comments)
  const [execComments, setExecComments] = useState([]);
  const [showExecComments, setShowExecComments] = useState(false);
  const [newExecComment, setNewExecComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [execCommentsLoading, setExecCommentsLoading] = useState(false);

  // Fetch executive comments
  const fetchExecComments = async () => {
    try {
      setExecCommentsLoading(true);
      const response = await axios.get(`${API}/five-pillars-comments`);
      setExecComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setExecCommentsLoading(false);
    }
  };

  // Add executive comment
  const addExecComment = async (pillarType = 'general') => {
    if (!newExecComment.trim()) return;
    try {
      await axios.post(`${API}/five-pillars-comments`, {
        content: newExecComment,
        pillar_type: pillarType
      });
      setNewExecComment('');
      fetchExecComments();
      toast.success('Comment added');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  // Reply to executive comment
  const addExecReply = async (parentCommentId, pillarType) => {
    if (!replyContent.trim()) return;
    try {
      await axios.post(`${API}/five-pillars-comments`, {
        content: replyContent,
        pillar_type: pillarType,
        parent_comment_id: parentCommentId
      });
      setReplyContent('');
      setReplyingTo(null);
      fetchExecComments();
      toast.success('Reply added');
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error('Failed to add reply');
    }
  };

  // Delete executive comment
  const deleteExecComment = async (commentId) => {
    try {
      await axios.delete(`${API}/five-pillars-comments/${commentId}`);
      fetchExecComments();
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  // Load executive comments when panel opens
  useEffect(() => {
    if (showExecComments) {
      fetchExecComments();
    }
  }, [showExecComments]);

  // Toggle parent milestone expansion in bottom list
  const toggleMilestoneInListExpansion = (milestoneId) => {
    setExpandedMilestonesInList(prev => {
      const next = new Set(prev);
      if (next.has(milestoneId)) {
        next.delete(milestoneId);
      } else {
        next.add(milestoneId);
      }
      return next;
    });
  };

  // Toggle parent milestone expansion
  const toggleMilestoneExpansion = (milestoneId) => {
    setExpandedMilestones(prev => {
      const next = new Set(prev);
      if (next.has(milestoneId)) {
        next.delete(milestoneId);
      } else {
        next.add(milestoneId);
      }
      return next;
    });
  };

  // Calculate sub-milestone completion progress (weighted by individual progress)
  const getSubMilestoneProgress = (subMilestones) => {
    if (!subMilestones?.length) return { completed: 0, total: 0, percent: 0, avgProgress: 0 };
    
    // Count fully completed
    const completed = subMilestones.filter(s => s.status === 'completed').length;
    const total = subMilestones.length;
    
    // Calculate average progress (considering individual progress sliders)
    // Completed = 100%, others use their progress value
    const totalProgress = subMilestones.reduce((sum, s) => {
      if (s.status === 'completed') return sum + 100;
      return sum + (s.progress || 0);
    }, 0);
    const avgProgress = Math.round(totalProgress / total);
    
    return { completed, total, percent: avgProgress, avgProgress };
  };

  // Calculate OVERALL project progress (for MVP milestone)
  // This aggregates ALL milestones (parent + sub) using their progress values
  const getOverallProjectProgress = useCallback(() => {
    if (!timelineData?.all_milestones?.length && !timelineData?.milestones?.length) {
      return { completed: 0, total: 0, percent: 0 };
    }
    
    // Use all_milestones (flat list) if available, otherwise flatten milestones
    let allMilestones = timelineData.all_milestones || [];
    if (!allMilestones.length && timelineData.milestones) {
      // Flatten hierarchical structure
      allMilestones = [];
      timelineData.milestones.forEach(m => {
        allMilestones.push(m);
        if (m.sub_milestones?.length) {
          allMilestones.push(...m.sub_milestones);
        }
      });
    }
    
    const completed = allMilestones.filter(m => m.status === 'completed').length;
    const total = allMilestones.length;
    
    // Calculate weighted progress (considering individual progress values)
    const totalProgress = allMilestones.reduce((sum, m) => {
      if (m.status === 'completed') return sum + 100;
      return sum + (m.progress || 0);
    }, 0);
    const avgPercent = total > 0 ? Math.round(totalProgress / total) : 0;
    
    return { 
      completed, 
      total, 
      percent: avgPercent
    };
  }, [timelineData]);

  // Calculate critical path (longest dependency chain)
  const calculateCriticalPath = useCallback((milestones) => {
    if (!milestones?.length) return [];
    
    // Build dependency graph
    const graph = {};
    const inDegree = {};
    
    milestones.forEach(m => {
      graph[m.id] = [];
      inDegree[m.id] = 0;
    });
    
    // Populate edges (dependency -> milestone)
    milestones.forEach(m => {
      if (m.dependencies?.length) {
        m.dependencies.forEach(depId => {
          if (graph[depId]) {
            graph[depId].push(m.id);
            inDegree[m.id] = (inDegree[m.id] || 0) + 1;
          }
        });
      }
    });
    
    // Calculate longest path using DFS with memoization
    const memo = {};
    const pathMemo = {};
    
    const getLongestPath = (nodeId) => {
      if (memo[nodeId] !== undefined) return memo[nodeId];
      
      const neighbors = graph[nodeId] || [];
      if (neighbors.length === 0) {
        memo[nodeId] = 0;
        pathMemo[nodeId] = [nodeId];
        return 0;
      }
      
      let maxLength = 0;
      let bestPath = [nodeId];
      
      neighbors.forEach(neighbor => {
        const length = 1 + getLongestPath(neighbor);
        if (length > maxLength) {
          maxLength = length;
          bestPath = [nodeId, ...(pathMemo[neighbor] || [])];
        }
      });
      
      memo[nodeId] = maxLength;
      pathMemo[nodeId] = bestPath;
      return maxLength;
    };
    
    // Find the starting node with longest path
    let longestPath = [];
    let maxLength = -1;
    
    Object.keys(graph).forEach(nodeId => {
      const length = getLongestPath(nodeId);
      if (length > maxLength) {
        maxLength = length;
        longestPath = pathMemo[nodeId];
      }
    });
    
    return longestPath;
  }, []);
  
  // Update critical path when milestones change
  useEffect(() => {
    if (timelineData.milestones?.length > 0) {
      const path = calculateCriticalPath(timelineData.milestones);
      setCriticalPathIds(path);
    }
  }, [timelineData.milestones, calculateCriticalPath]);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [pillarsRes, meetingsRes, activeRes, usersRes] = await Promise.all([
        axios.get(`${API}/five-pillars-hub/pillars`, { headers: getHeaders() }),
        axios.get(`${API}/five-pillars-hub/meetings?limit=10`, { headers: getHeaders() }),
        axios.get(`${API}/five-pillars-hub/meetings/active`, { headers: getHeaders() }),
        axios.get(`${API}/users`, { headers: getHeaders() }).catch(() => ({ data: [] }))
      ]);
      
      setPillars(pillarsRes.data.pillars || []);
      setMeetings(meetingsRes.data.meetings || []);
      setActiveMeeting(activeRes.data.meeting);
      setUsers(usersRes.data || []);
      
      // Get current user role from token
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUserRole(payload.role || '');
        } catch (e) {
          console.error('Error parsing token:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load 5 Pillars data');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchData();
    // Also fetch all pillar data on initial load for card metrics
    fetchTimelineData();
    fetchScopeData();
    fetchCostData();
    fetchImplementationData();
    fetchDataSecurityData();
    fetchTasksData();
  }, [fetchData]);

  // Fetch pillar-specific data when in meeting mode
  useEffect(() => {
    if (activeMeeting) {
      // Load scope, timeline, and cost data for meeting view
      fetchScopeData();
      fetchTimelineData();
      fetchCostData();
      fetchImplementationData();
      fetchDataSecurityData();
    }
  }, [activeMeeting?.id]);

  // Get container width on resize
  useEffect(() => {
    const updateWidth = () => {
      const container = document.getElementById('pillars-grid-container');
      if (container) {
        setContainerWidth(container.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Ensure gridLayout includes all pillars (fix for missing 'tasks' in saved layouts)
  useEffect(() => {
    if (pillars.length > 0) {
      const existingIds = gridLayout.map(item => item.i);
      const missingPillars = pillars.filter(p => !existingIds.includes(p.id));
      
      if (missingPillars.length > 0) {
        // Calculate next available row position
        const maxY = Math.max(...gridLayout.map(item => item.y + item.h), 0);
        const newLayouts = missingPillars.map((pillar, index) => ({
          i: pillar.id,
          x: (index * 4) % 12,
          y: maxY,
          w: 4,
          h: 4,
          minW: 3,
          minH: 3
        }));
        
        setGridLayout(prev => [...prev, ...newLayouts]);
        // Also update localStorage with the new layout
        const updatedLayout = [...gridLayout, ...newLayouts];
        localStorage.setItem('five_pillars_layout', JSON.stringify(updatedLayout));
      }
    }
  }, [pillars]);

  // ==================== SCOPE MANAGEMENT ====================
  const fetchScopeData = async () => {
    try {
      setScopeLoading(true);
      const response = await axios.get(
        `${API}/governance/five-pillars/scope`,
        { headers: getHeaders() }
      );
      setScopeData(response.data);
    } catch (error) {
      console.error('Error fetching scope data:', error);
      toast.error('Failed to load scope data');
    } finally {
      setScopeLoading(false);
    }
  };
  
  const fetchImplementationData = async () => {
    try {
      const response = await axios.get(
        `${API}/governance/five-pillars/implementation`,
        { headers: getHeaders() }
      );
      setImplementationData(response.data);
    } catch (error) {
      console.error('Error fetching implementation data:', error);
    }
  };
  
  const fetchDataSecurityData = async () => {
    try {
      const response = await axios.get(
        `${API}/governance/five-pillars/data-security`,
        { headers: getHeaders() }
      );
      setDataSecurityData(response.data);
    } catch (error) {
      console.error('Error fetching data security data:', error);
    }
  };

  // Load meeting preps when dialog opens
  useEffect(() => {
    if (showMeetingPrep || showPrepPicker) {
      axios.get(`${API}/five-pillars-hub/meeting-prep`, { headers: getHeaders() })
        .then(res => {
          const preps = res.data.preps || [];
          setSavedPreps(preps);
          // If editing and no current prep loaded, load the latest
          if (showMeetingPrep && preps.length > 0 && !meetingPrep.id) {
            setMeetingPrep(preps[0]);
          }
        })
        .catch(() => {});
    }
  }, [showMeetingPrep, showPrepPicker]);

  const saveMeetingPrep = async () => {
    try {
      await axios.put(`${API}/five-pillars-hub/meeting-prep`, meetingPrep, { headers: getHeaders() });
      toast.success('Meeting prep saved!');
      setShowMeetingPrep(false);
    } catch (e) {
      toast.error('Failed to save');
    }
  };
  
  const importTimelineToScope = async () => {
    try {
      const response = await axios.post(
        `${API}/governance/five-pillars/scope/import-from-timeline`,
        {},
        { headers: getHeaders() }
      );
      toast.success(`Imported ${response.data.imported_count} items from Timeline`);
      fetchScopeData();
      fetchImplementationData();
      fetchDataSecurityData();
    } catch (error) {
      console.error('Error importing from timeline:', error);
      toast.error('Failed to import from timeline');
    }
  };
  
  const updateImplementationItem = async (itemId, updates) => {
    try {
      await axios.patch(
        `${API}/governance/five-pillars/implementation/${itemId}`,
        updates,
        { headers: getHeaders() }
      );
      toast.success('Implementation item updated');
      fetchImplementationData();
      if (selectedImplementationItem?.id === itemId) {
        setSelectedImplementationItem({ ...selectedImplementationItem, ...updates });
      }
    } catch (error) {
      console.error('Error updating implementation item:', error);
      toast.error('Failed to update implementation item');
    }
  };
  
  const updateDataSecurityItem = async (itemId, updates) => {
    try {
      await axios.patch(
        `${API}/governance/five-pillars/data-security/${itemId}`,
        updates,
        { headers: getHeaders() }
      );
      toast.success('Data & Security item updated');
      fetchDataSecurityData();
      if (selectedDataSecurityItem?.id === itemId) {
        setSelectedDataSecurityItem({ ...selectedDataSecurityItem, ...updates });
      }
    } catch (error) {
      console.error('Error updating data security item:', error);
      toast.error('Failed to update data security item');
    }
  };
  
  const updateSystemSecurity = async (updates) => {
    try {
      await axios.patch(
        `${API}/governance/five-pillars/data-security/system/config`,
        updates,
        { headers: getHeaders() }
      );
      toast.success('System security updated');
      fetchDataSecurityData();
    } catch (error) {
      console.error('Error updating system security:', error);
      toast.error('Failed to update system security');
    }
  };

  const addScopeItem = async () => {
    try {
      await axios.post(
        `${API}/governance/five-pillars/scope`,
        scopeForm,
        { headers: getHeaders() }
      );
      toast.success('Scope item added');
      setShowAddScopeItem(false);
      setScopeForm({ 
        name: '', description: '', category: 'mvp', status: 'pending', hub: 'none',
        acceptance_criteria: '', technical_requirements: '', related_files: [],
        testing_instructions: '', test_scenarios: '', success_indicators: '',
        dependencies_notes: '', reference_links: [],
        api_keys_needed: '', env_variables: '', third_party_credentials: '',
        setup_instructions: '', config_notes: ''
      });
      setScopeFormTab('definition');
      fetchScopeData();
    } catch (error) {
      console.error('Error adding scope item:', error);
      toast.error('Failed to add scope item');
    }
  };

  // Open AI prompt dialog
  const openAIPromptDialog = () => {
    if (!scopeForm.name.trim()) {
      toast.error('Please enter a feature name first');
      return;
    }
    setAIPrompt('');
    setShowAIPromptDialog(true);
  };

  // AI-powered feature specification generator
  const generateFeatureSpec = async () => {
    if (!scopeForm.name.trim()) {
      toast.error('Please enter a feature name first');
      return;
    }
    
    setShowAIPromptDialog(false);
    setGeneratingSpec(true);
    
    try {
      const response = await axios.post(
        `${API}/governance/five-pillars/scope/generate-spec`,
        {
          feature_name: scopeForm.name,
          feature_prompt: aiPrompt,
          hub: scopeForm.hub,
          category: scopeForm.category
        },
        { headers: getHeaders() }
      );
      
      if (response.data.success && response.data.specification) {
        const spec = response.data.specification;
        setScopeForm(prev => ({
          ...prev,
          description: spec.description || prev.description,
          acceptance_criteria: spec.acceptance_criteria || prev.acceptance_criteria,
          technical_requirements: spec.technical_requirements || prev.technical_requirements,
          related_files: spec.related_files || prev.related_files,
          testing_instructions: spec.testing_instructions || prev.testing_instructions,
          test_scenarios: spec.test_scenarios || prev.test_scenarios,
          success_indicators: spec.success_indicators || prev.success_indicators,
          dependencies_notes: spec.dependencies_notes || prev.dependencies_notes
        }));
        toast.success('Feature specification generated!');
        setAIPrompt('');
      }
    } catch (error) {
      console.error('Error generating spec:', error);
      toast.error('Failed to generate specification. Please try again.');
    } finally {
      setGeneratingSpec(false);
    }
  };

  const updateScopeItem = async (itemId, updates) => {
    try {
      await axios.patch(
        `${API}/governance/five-pillars/scope/${itemId}`,
        updates,
        { headers: getHeaders() }
      );
      toast.success('Scope item updated');
      fetchScopeData();
      // If progress was updated, refresh timeline data to sync
      if ('progress' in updates) {
        fetchTimelineData();
      }
    } catch (error) {
      console.error('Error updating scope item:', error);
      toast.error('Failed to update scope item');
    }
  };

  const deleteScopeItem = async (itemId) => {
    if (!window.confirm('Delete this scope item?')) return;
    try {
      await axios.delete(
        `${API}/governance/five-pillars/scope/${itemId}`,
        { headers: getHeaders() }
      );
      toast.success('Scope item deleted');
      fetchScopeData();
    } catch (error) {
      console.error('Error deleting scope item:', error);
      toast.error('Failed to delete scope item');
    }
  };

  // ==================== COST MANAGEMENT ====================
  const fetchCostData = async () => {
    try {
      setCostLoading(true);
      const response = await axios.get(
        `${API}/governance/five-pillars/costs`,
        { headers: getHeaders() }
      );
      setCostData(response.data);
    } catch (error) {
      console.error('Error fetching cost data:', error);
      toast.error('Failed to load cost data');
    } finally {
      setCostLoading(false);
    }
  };

  const addExpense = async () => {
    try {
      await axios.post(
        `${API}/governance/five-pillars/costs/expense`,
        { ...expenseForm, amount: parseFloat(expenseForm.amount) },
        { headers: getHeaders() }
      );
      toast.success('Expense added');
      setShowAddExpense(false);
      setExpenseForm({ name: '', category: 'services', amount: '', frequency: 'one_time', description: '', date: new Date().toISOString().split('T')[0] });
      fetchCostData();
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Failed to add expense');
    }
  };

  const deleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(
        `${API}/governance/five-pillars/costs/expense/${expenseId}`,
        { headers: getHeaders() }
      );
      toast.success('Expense deleted');
      fetchCostData();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const addSalary = async () => {
    try {
      await axios.post(
        `${API}/governance/five-pillars/costs/salary`,
        { ...salaryForm, amount: parseFloat(salaryForm.amount) },
        { headers: getHeaders() }
      );
      toast.success('Salary entry added');
      setShowAddSalary(false);
      setSalaryForm({ name: '', role: '', amount: '', frequency: 'monthly', start_date: new Date().toISOString().split('T')[0], end_date: '', is_active: true });
      fetchCostData();
    } catch (error) {
      console.error('Error adding salary:', error);
      toast.error('Failed to add salary entry');
    }
  };

  const deleteSalary = async (salaryId) => {
    if (!window.confirm('Delete this salary entry?')) return;
    try {
      await axios.delete(
        `${API}/governance/five-pillars/costs/salary/${salaryId}`,
        { headers: getHeaders() }
      );
      toast.success('Salary entry deleted');
      fetchCostData();
    } catch (error) {
      console.error('Error deleting salary:', error);
      toast.error('Failed to delete salary entry');
    }
  };

  // ==================== TASKS MANAGEMENT ====================
  const fetchTasksData = async () => {
    try {
      setTasksLoading(true);
      const response = await axios.get(
        `${API}/five-pillars-hub/tasks`,
        { headers: getHeaders() }
      );
      setTasksData(response.data);
    } catch (error) {
      console.error('Error fetching tasks data:', error);
      toast.error('Failed to load tasks data');
    } finally {
      setTasksLoading(false);
    }
  };

  const addTask = async () => {
    try {
      await axios.post(
        `${API}/five-pillars-hub/tasks`,
        taskForm,
        { headers: getHeaders() }
      );
      toast.success('Task added');
      setShowAddTask(false);
      setTaskForm({ title: '', description: '', status: 'pending', priority: 'medium', assignee: '', due_date: '' });
      fetchTasksData();
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error('Failed to add task');
    }
  };

  // New unified save function for AgentTaskModal
  const saveAgentTask = async (formData, taskId = null) => {
    try {
      if (taskId) {
        // Update existing task
        await axios.put(
          `${API}/five-pillars-hub/tasks/${taskId}`,
          formData,
          { headers: getHeaders() }
        );
        toast.success('Task updated');
      } else {
        // Create new task
        await axios.post(
          `${API}/five-pillars-hub/tasks`,
          formData,
          { headers: getHeaders() }
        );
        toast.success('Task created');
      }
      setEditingTask(null);
      fetchTasksData();
    } catch (error) {
      console.error('Error saving task:', error);
      throw error;
    }
  };

  // Open task for editing
  const openTaskForEdit = (task) => {
    setEditingTask(task);
    setShowAddTask(true);
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(
        `${API}/five-pillars-hub/tasks/${taskId}`,
        { status: newStatus },
        { headers: getHeaders() }
      );
      toast.success('Task updated');
      fetchTasksData();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(
        `${API}/five-pillars-hub/tasks/${taskId}`,
        { headers: getHeaders() }
      );
      toast.success('Task deleted');
      fetchTasksData();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  // ==================== TIMELINE MANAGEMENT ====================
  const fetchTimelineData = async () => {
    try {
      setTimelineLoading(true);
      const response = await axios.get(
        `${API}/governance/five-pillars/timeline`,
        { headers: getHeaders() }
      );
      setTimelineData(response.data);
    } catch (error) {
      console.error('Error fetching timeline data:', error);
      toast.error('Failed to load timeline data');
    } finally {
      setTimelineLoading(false);
    }
  };

  const defaultPhaseDates = {
    planning: { start: '', end: '' },
    design: { start: '', end: '' },
    development: { start: '', end: '' },
    internal_testing: { start: '', end: '' },
    staff_testing: { start: '', end: '' },
    deploy_ready: { start: '', end: '' },
    full_testing: { start: '', end: '' }
  };

  const addMilestone = async () => {
    try {
      const submitData = {
        ...milestoneForm,
        hub: milestoneForm.hub === 'none' ? '' : milestoneForm.hub
      };
      await axios.post(
        `${API}/governance/five-pillars/timeline`,
        submitData,
        { headers: getHeaders() }
      );
      toast.success('Milestone added');
      setShowAddMilestone(false);
      setMilestoneForm({ name: '', description: '', start_date: '', target_date: '', status: 'pending', phase_type: 'development', hub: 'none', is_mvp_milestone: false, dependencies: [], parent_id: null, phase_dates: defaultPhaseDates });
      fetchTimelineData();
    } catch (error) {
      console.error('Error adding milestone:', error);
      toast.error('Failed to add milestone');
    }
  };

  // Save edited milestone (full edit mode)
  const saveEditedMilestone = async () => {
    if (!editingMilestone) return;
    try {
      const submitData = {
        ...milestoneForm,
        hub: milestoneForm.hub === 'none' ? '' : milestoneForm.hub
      };
      await axios.patch(
        `${API}/governance/five-pillars/timeline/${editingMilestone.id}`,
        submitData,
        { headers: getHeaders() }
      );
      toast.success('Milestone updated');
      setShowAddMilestone(false);
      setEditingMilestone(null);
      setMilestoneForm({ name: '', description: '', start_date: '', target_date: '', status: 'pending', phase_type: 'development', hub: 'none', is_mvp_milestone: false, dependencies: [], parent_id: null, phase_dates: defaultPhaseDates });
      fetchTimelineData();
    } catch (error) {
      console.error('Error updating milestone:', error);
      toast.error('Failed to update milestone');
    }
  };

  // Open edit dialog for a milestone
  const openEditMilestone = (milestone) => {
    setEditingMilestone(milestone);
    setMilestoneForm({
      name: milestone.name || '',
      description: milestone.description || '',
      start_date: milestone.start_date || '',
      target_date: milestone.target_date || '',
      status: milestone.status || 'pending',
      phase_type: milestone.phase_type || 'development',
      hub: milestone.hub || 'none',
      is_mvp_milestone: milestone.is_mvp_milestone || false,
      dependencies: milestone.dependencies || [],
      parent_id: milestone.parent_id || null,
      phase_dates: milestone.phase_dates || defaultPhaseDates
    });
    setShowAddMilestone(true);
  };

  const updateMilestone = async (milestoneId, updates) => {
    try {
      await axios.patch(
        `${API}/governance/five-pillars/timeline/${milestoneId}`,
        updates,
        { headers: getHeaders() }
      );
      toast.success('Milestone updated');
      fetchTimelineData();
    } catch (error) {
      console.error('Error updating milestone:', error);
      toast.error('Failed to update milestone');
    }
  };

  const deleteMilestone = async (milestoneId) => {
    if (!window.confirm('Delete this milestone?')) return;
    try {
      await axios.delete(
        `${API}/governance/five-pillars/timeline/${milestoneId}`,
        { headers: getHeaders() }
      );
      toast.success('Milestone deleted');
      fetchTimelineData();
    } catch (error) {
      console.error('Error deleting milestone:', error);
      toast.error('Failed to delete milestone');
    }
  };

  // Update project dates (start and MVP deadline)
  const updateProjectDates = async () => {
    try {
      await axios.patch(
        `${API}/governance/five-pillars/timeline/dates`,
        dateForm,
        { headers: getHeaders() }
      );
      toast.success('Project dates updated');
      setEditingDates(false);
      fetchTimelineData();
    } catch (error) {
      console.error('Error updating dates:', error);
      toast.error('Failed to update dates');
    }
  };

  // Add testing period
  const addTestingPeriod = async () => {
    try {
      await axios.post(
        `${API}/governance/five-pillars/timeline/testing-period`,
        testPeriodForm,
        { headers: getHeaders() }
      );
      toast.success('Testing period added');
      setShowAddTestPeriod(false);
      setEditingTestPeriod(null);
      setTestPeriodForm({ name: '', start_date: '', end_date: '', description: '' });
      fetchTimelineData();
    } catch (error) {
      console.error('Error adding testing period:', error);
      toast.error('Failed to add testing period');
    }
  };

  // Save edited testing period
  const saveEditedTestPeriod = async () => {
    if (!editingTestPeriod) return;
    try {
      await axios.patch(
        `${API}/governance/five-pillars/timeline/testing-period/${editingTestPeriod.id}`,
        testPeriodForm,
        { headers: getHeaders() }
      );
      toast.success('Testing period updated');
      setShowAddTestPeriod(false);
      setEditingTestPeriod(null);
      setTestPeriodForm({ name: '', start_date: '', end_date: '', description: '' });
      fetchTimelineData();
    } catch (error) {
      console.error('Error updating testing period:', error);
      toast.error('Failed to update testing period');
    }
  };

  // Open edit dialog for testing period
  const openEditTestPeriod = (period) => {
    setEditingTestPeriod(period);
    setTestPeriodForm({
      name: period.name || '',
      start_date: period.start_date || '',
      end_date: period.end_date || '',
      description: period.description || ''
    });
    setShowAddTestPeriod(true);
  };

  // Delete testing period
  const deleteTestingPeriod = async (periodId) => {
    if (!window.confirm('Delete this testing period?')) return;
    try {
      await axios.delete(
        `${API}/governance/five-pillars/timeline/testing-period/${periodId}`,
        { headers: getHeaders() }
      );
      toast.success('Testing period deleted');
      fetchTimelineData();
    } catch (error) {
      console.error('Error deleting testing period:', error);
      toast.error('Failed to delete testing period');
    }
  };

  // DnD sensors for milestone reordering
  const dndSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Reorder milestones via drag and drop
  const reorderMilestones = async (milestoneIds) => {
    try {
      const response = await axios.post(
        `${API}/governance/five-pillars/timeline/reorder`,
        { milestone_ids: milestoneIds },
        { headers: getHeaders() }
      );
      
      // Update local state with new order
      setTimelineData(prev => ({
        ...prev,
        milestones: response.data.milestones
      }));
      
      toast.success('Milestones reordered');
    } catch (error) {
      console.error('Error reordering milestones:', error);
      toast.error('Failed to reorder milestones');
      fetchTimelineData();
    }
  };

  // Handle drag end for milestone reordering
  const handleMilestoneDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = timelineData.milestones.findIndex(m => m.id === active.id);
      const newIndex = timelineData.milestones.findIndex(m => m.id === over.id);
      
      const newOrder = arrayMove(timelineData.milestones, oldIndex, newIndex);
      const milestoneIds = newOrder.map(m => m.id);
      
      reorderMilestones(milestoneIds);
    }
  };

  // Calculate timeline position for a date
  const getTimelinePosition = (dateStr) => {
    if (!timelineData.project_start || !timelineData.mvp_deadline) return 50;
    const start = new Date(timelineData.project_start).getTime();
    const end = new Date(timelineData.mvp_deadline).getTime();
    const date = new Date(dateStr).getTime();
    const position = ((date - start) / (end - start)) * 100;
    return Math.max(0, Math.min(100, position));
  };

  // Get status color for milestone
  const getMilestoneColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      case 'delayed': return 'bg-red-500';
      default: return 'bg-slate-400';
    }
  };

  // Phase type colors and labels
  const PHASE_CONFIG = {
    planning: { color: 'bg-indigo-500', lightColor: 'bg-indigo-200 dark:bg-indigo-800', label: 'Planning', icon: '📋' },
    design: { color: 'bg-pink-500', lightColor: 'bg-pink-200 dark:bg-pink-800', label: 'Design', icon: '🎨' },
    development: { color: 'bg-blue-500', lightColor: 'bg-blue-200 dark:bg-blue-800', label: 'Development', icon: '💻' },
    internal_testing: { color: 'bg-amber-500', lightColor: 'bg-amber-200 dark:bg-amber-800', label: 'Internal Testing', icon: '🧪' },
    staff_testing: { color: 'bg-orange-500', lightColor: 'bg-orange-200 dark:bg-orange-800', label: 'Staff Testing', icon: '👥' },
    deploy_ready: { color: 'bg-cyan-500', lightColor: 'bg-cyan-200 dark:bg-cyan-800', label: 'Deploy Ready', icon: '🚀' },
    full_testing: { color: 'bg-purple-500', lightColor: 'bg-purple-200 dark:bg-purple-800', label: 'Full Testing', icon: '✅' },
    mvp_ready: { color: 'bg-green-600', lightColor: 'bg-green-200 dark:bg-green-800', label: 'MVP Ready', icon: '🎯' }
  };

  const getPhaseColor = (phaseType, isLight = false) => {
    const config = PHASE_CONFIG[phaseType] || PHASE_CONFIG.development;
    return isLight ? config.lightColor : config.color;
  };

  const getPhaseLabel = (phaseType) => {
    return PHASE_CONFIG[phaseType]?.label || 'Development';
  };

  // Fetch pillar comments
  const fetchPillarComments = async (pillarId) => {
    try {
      const response = await axios.get(
        `${API}/five-pillars-hub/pillars/${pillarId}/comments`,
        { headers: getHeaders() }
      );
      setComments(prev => ({ ...prev, [pillarId]: response.data.comments || [] }));
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  // Create or Edit pillar update
  const createUpdate = async () => {
    if (!selectedPillar) return;
    
    try {
      if (editingUpdate) {
        // Edit existing update
        await axios.patch(
          `${API}/five-pillars-hub/pillars/${selectedPillar.id}/updates/${editingUpdate.id}`,
          updateForm,
          { headers: getHeaders() }
        );
        toast.success('Update saved successfully');
      } else {
        // Create new update
        await axios.post(
          `${API}/five-pillars-hub/pillars/${selectedPillar.id}/updates`,
          {
            pillar_id: selectedPillar.id,
            ...updateForm
          },
          { headers: getHeaders() }
        );
        toast.success('Update created successfully');
      }
      
      setShowUpdateDialog(false);
      setEditingUpdate(null);
      setUpdateForm({ what_worked_on: '', whats_next: '', blockers: '', links: [], todos: [] });
      fetchData();
    } catch (error) {
      console.error('Error saving update:', error);
      toast.error(error.response?.data?.detail || 'Failed to save update');
    }
  };

  // Open edit dialog for a pillar update
  const openEditDialog = (pillar) => {
    if (pillar.latest_update) {
      setSelectedPillar(pillar);
      setEditingUpdate(pillar.latest_update);
      setUpdateForm({
        what_worked_on: pillar.latest_update.what_worked_on || '',
        whats_next: pillar.latest_update.whats_next || '',
        blockers: pillar.latest_update.blockers || '',
        links: pillar.latest_update.links || [],
        todos: pillar.latest_update.todos || []
      });
      setShowUpdateDialog(true);
    }
  };

  // Add comment
  const addComment = async (pillarId, content, parentId = null) => {
    try {
      await axios.post(
        `${API}/five-pillars-hub/pillars/${pillarId}/comments`,
        { content, parent_id: parentId },
        { headers: getHeaders() }
      );
      
      fetchPillarComments(pillarId);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  // Start meeting - delegates to hook but accepts prepId
  const startMeetingWithPrep = async (selectedPrepId) => {
    try {
      const response = await axios.post(
        `${API}/five-pillars-hub/meetings/start`,
        { title: `BIZOPS Weekly Update - ${format(new Date(), 'MMMM d, yyyy')}` },
        { headers: getHeaders() }
      );
      
      const meeting = response.data.meeting;
      
      // Load meeting prep if selected
      if (selectedPrepId) {
        try {
          const prepRes = await axios.get(`${API}/five-pillars-hub/meeting-prep`, { headers: getHeaders() });
          const preps = prepRes.data.preps || [];
          const prep = preps.find(p => p.id === selectedPrepId);
          if (prep) {
            meeting.agenda = prep.agenda || [];
            meeting.weekly_recap = {
              completed: prep.completed || [],
              issues: prep.issues || [],
              focus_this_week: prep.focus_this_week || [],
              last_meeting_notes: [],
              unfinished_from_last: []
            };
            meeting.presenter_notes = prep.presenter_notes || '';
            meeting.discussion_topics = prep.discussion_topics || [];
          }
        } catch (e) {
          console.log('Could not load meeting prep:', e);
        }
      }

      // Auto-populate from last meeting
      try {
        const meetingsRes = await axios.get(`${API}/five-pillars-hub/meetings?limit=5`, { headers: getHeaders() });
        const pastMeetings = (meetingsRes.data.meetings || []).filter(m => m.id !== meeting.id && m.status === 'completed');
        const lastMeeting = pastMeetings[0];
        if (lastMeeting) {
          const unfinished = [
            ...(lastMeeting.action_items || []).filter(a => a.status !== 'completed').map(a => a.content || ''),
            ...(lastMeeting.tasks || []).filter(t => t.status !== 'completed').map(t => t.content || t.title || '')
          ].filter(Boolean);
          const lastNotes = (lastMeeting.notes || []).filter(n => n.type === 'note' || n.type === 'decision').slice(-5).map(n => n.content || '');
          
          meeting.weekly_recap = {
            ...meeting.weekly_recap,
            completed: meeting.weekly_recap?.completed || [],
            issues: meeting.weekly_recap?.issues || [],
            focus_this_week: meeting.weekly_recap?.focus_this_week || [],
            last_meeting_notes: lastNotes,
            unfinished_from_last: unfinished
          };
        }
      } catch (e) {}

      if (!meeting.agenda) meeting.agenda = [];
      if (!meeting.weekly_recap) meeting.weekly_recap = { completed: [], issues: [], focus_this_week: [], last_meeting_notes: [], unfinished_from_last: [] };
      
      // Save prep data to backend
      if (meeting.agenda.length > 0) {
        axios.patch(`${API}/five-pillars-hub/meetings/${meeting.id}/agenda`, { agenda: meeting.agenda }, { headers: getHeaders() }).catch(() => {});
      }
      if (meeting.weekly_recap) {
        axios.patch(`${API}/five-pillars-hub/meetings/${meeting.id}/weekly-recap`, meeting.weekly_recap, { headers: getHeaders() }).catch(() => {});
      }
      
      setActiveMeeting(meeting);
      toast.success('Meeting started!');
    } catch (error) {
      console.error('Error starting meeting:', error);
      toast.error(error.response?.data?.detail || 'Failed to start meeting');
    }
  };

  // Navigate meeting pages
  const navigateMeeting = async (pageId) => {
    if (!activeMeeting) return;
    
    try {
      await axios.patch(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}/navigate`,
        { page_id: pageId },
        { headers: getHeaders() }
      );
      
      setActiveMeeting(prev => ({ ...prev, current_page: pageId }));
    } catch (error) {
      console.error('Error navigating:', error);
    }
  };

  // Add meeting note
  const addMeetingNote = async () => {
    if (!activeMeeting || !newNote.trim()) return;
    
    const currentPage = MEETING_PAGES.find(p => p.id === activeMeeting.current_page);
    
    try {
      // If it's a task, create it in the main tasks system first
      if (noteType === 'task') {
        // Create the task in the main tasks collection
        const taskRes = await axios.post(
          `${API}/tasks`,
          {
            title: newNote,
            description: `Created from 5 Pillars meeting: ${activeMeeting.title}`,
            status: 'pending',
            priority: 'medium',
            tags: ['rhino-1', 'five_pillars'],
            source: 'five_pillars_meeting',
            project_id: 'rhino-1',
            meeting_id: activeMeeting.id
          },
          { headers: getHeaders() }
        );
        
        // Also record it in the meeting notes
        await axios.post(
          `${API}/five-pillars-hub/meetings/${activeMeeting.id}/notes`,
          {
            content: newNote,
            page_id: activeMeeting.current_page,
            pillar_id: currentPage?.pillar_id || null,
            is_task: true,
            task_id: taskRes.data.id
          },
          { headers: getHeaders() }
        );
        
        toast.success('Task created and synced to your profile!');
      } else {
        // Regular note/action/decision/blocker
        await axios.post(
          `${API}/five-pillars-hub/meetings/${activeMeeting.id}/notes`,
          {
            content: newNote,
            page_id: activeMeeting.current_page,
            pillar_id: currentPage?.pillar_id || null,
            is_action_item: noteType === 'action',
            is_decision: noteType === 'decision',
            is_blocker: noteType === 'blocker'
          },
          { headers: getHeaders() }
        );
        toast.success('Note added');
      }
      
      setNewNote('');
      setNoteType('note');
      
      // Refresh meeting data
      const meetingRes = await axios.get(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}`,
        { headers: getHeaders() }
      );
      setActiveMeeting(meetingRes.data.meeting);
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add ' + (noteType === 'task' ? 'task' : 'note'));
    }
  };

  // Convert action to task
  const convertToTask = async (actionId, actionContent) => {
    if (!activeMeeting) return;
    
    try {
      await axios.post(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}/action-items/${actionId}/convert-to-task`,
        { title: actionContent },
        { headers: getHeaders() }
      );
      
      toast.success('Task created!');
      
      // Refresh meeting
      const meetingRes = await axios.get(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}`,
        { headers: getHeaders() }
      );
      setActiveMeeting(meetingRes.data.meeting);
    } catch (error) {
      console.error('Error converting to task:', error);
      toast.error('Failed to create task');
    }
  };

  // Toggle task status (bi-directional sync)
  const toggleMeetingTask = async (taskId, currentStatus) => {
    if (!activeMeeting) return;
    
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    
    try {
      await axios.patch(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}/tasks/${taskId}`,
        { status: newStatus },
        { headers: getHeaders() }
      );
      
      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task reopened');
      
      // Refresh meeting
      const meetingRes = await axios.get(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}`,
        { headers: getHeaders() }
      );
      setActiveMeeting(meetingRes.data.meeting);
    } catch (error) {
      console.error('Error toggling task:', error);
      toast.error('Failed to update task');
    }
  };

  // Finish meeting
  const finishMeeting = async () => {
    if (!activeMeeting) return;
    
    try {
      await axios.post(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}/finish`,
        { wrap_up_notes: '' },
        { headers: getHeaders() }
      );
      
      toast.success('Meeting finished!');
      setActiveMeeting(null);
      fetchData();
    } catch (error) {
      console.error('Error finishing meeting:', error);
      toast.error('Failed to finish meeting');
    }
  };

  // Get current page index
  const getCurrentPageIndex = () => {
    if (!activeMeeting) return 0;
    return MEETING_PAGES.findIndex(p => p.id === activeMeeting.current_page);
  };

  // Navigate to next/prev page
  const goToNextPage = () => {
    const currentIndex = getCurrentPageIndex();
    if (currentIndex < MEETING_PAGES.length - 1) {
      navigateMeeting(MEETING_PAGES[currentIndex + 1].id);
    }
  };

  const goToPrevPage = () => {
    const currentIndex = getCurrentPageIndex();
    if (currentIndex > 0) {
      navigateMeeting(MEETING_PAGES[currentIndex - 1].id);
    }
  };

  if (loading) {
    return (
      <Layout>
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
      </Layout>
    );
  }

  // Read-only Past Meeting View
  if (viewingMeeting) {
    return (
      <Layout>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
          <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 sticky top-[73px] z-10">
            <div className="max-w-5xl mx-auto px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold dark:text-white">{viewingMeeting.title}</h1>
                  <p className="text-sm text-muted-foreground">
                    {viewingMeeting.started_at ? format(new Date(viewingMeeting.started_at), 'PPpp') : ''} • Read Only
                  </p>
                </div>
                <Button variant="outline" onClick={() => { setViewingMeeting(null); navigate('/five-pillars'); }}>
                  ← Back to 5 Pillars
                </Button>
              </div>
            </div>
          </div>
          <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
            {/* Agenda */}
            {(viewingMeeting.agenda || []).length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">📋 Agenda</CardTitle></CardHeader>
                <CardContent>
                  <ol className="list-decimal list-inside space-y-1">
                    {viewingMeeting.agenda.map((item, i) => <li key={i} className="text-sm">{item}</li>)}
                  </ol>
                </CardContent>
              </Card>
            )}
            {/* Weekly Recap */}
            {viewingMeeting.weekly_recap && (
              <Card>
                <CardHeader><CardTitle className="text-base">📊 Weekly Recap</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {(viewingMeeting.weekly_recap.completed || []).length > 0 && (
                    <div><h4 className="text-sm font-semibold text-green-600 mb-1">✅ Completed</h4>
                      {viewingMeeting.weekly_recap.completed.map((item, i) => <p key={i} className="text-sm ml-4">• {item}</p>)}</div>
                  )}
                  {(viewingMeeting.weekly_recap.issues || []).length > 0 && (
                    <div><h4 className="text-sm font-semibold text-red-600 mb-1">⚠️ Issues</h4>
                      {viewingMeeting.weekly_recap.issues.map((item, i) => <p key={i} className="text-sm ml-4">• {item}</p>)}</div>
                  )}
                  {(viewingMeeting.weekly_recap.focus_this_week || []).length > 0 && (
                    <div><h4 className="text-sm font-semibold text-purple-600 mb-1">🎯 Focus This Week</h4>
                      {viewingMeeting.weekly_recap.focus_this_week.map((item, i) => <p key={i} className="text-sm ml-4">• {item}</p>)}</div>
                  )}
                </CardContent>
              </Card>
            )}
            {/* Notes */}
            {(viewingMeeting.notes || []).length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">📝 Notes & Decisions</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {viewingMeeting.notes.map((note, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded text-sm">
                        <Badge variant="outline" className="text-[10px] shrink-0">{note.type}</Badge>
                        <span>{note.content}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {/* Action Items & Tasks */}
            {((viewingMeeting.action_items || []).length > 0 || (viewingMeeting.tasks || []).length > 0) && (
              <Card>
                <CardHeader><CardTitle className="text-base">✅ Action Items & Tasks</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(viewingMeeting.action_items || []).map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                        <Badge variant="outline" className="text-[10px]">Action</Badge>
                        <span className="flex-1">{item.content}</span>
                        <Badge className="text-[10px]">{item.status || 'open'}</Badge>
                      </div>
                    ))}
                    {(viewingMeeting.tasks || []).map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm p-2 bg-green-50 dark:bg-green-900/20 rounded">
                        <Badge variant="outline" className="text-[10px]">Task</Badge>
                        <span className="flex-1">{item.content || item.title}</span>
                        <Badge className="text-[10px]">{item.status || 'open'}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  // Meeting Mode View
  if (activeMeeting) {
    const currentPage = MEETING_PAGES.find(p => p.id === activeMeeting.current_page) || MEETING_PAGES[0];
    const currentIndex = getCurrentPageIndex();
    const isFirstPage = currentIndex === 0;
    const isLastPage = currentIndex === MEETING_PAGES.length - 1;
    const pillarUpdate = currentPage.pillar_id ? activeMeeting.pillar_updates?.[currentPage.pillar_id] : null;
    const pageNotes = (activeMeeting.notes || []).filter(n => n.page_id === currentPage.id);

    return (
      <Layout>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        {/* Meeting Header - offset for Layout header (h-[73px]) */}
        <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 sticky top-[73px] z-10">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-sm font-medium text-red-600 dark:text-red-400">LIVE</span>
                </div>
                <h1 className="text-xl font-bold dark:text-white">{activeMeeting.title}</h1>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="text-sm">
                  {currentIndex + 1} / {MEETING_PAGES.length}
                </Badge>
                <Button variant="destructive" size="sm" onClick={finishMeeting}>
                  <Square className="h-4 w-4 mr-1" />
                  End Meeting
                </Button>
              </div>
            </div>
            
            {/* Page Navigation Pills - Horizontal Flow */}
            <div className="flex items-center gap-1 mt-4 overflow-x-auto pb-2 px-1">
              {MEETING_PAGES.map((page, idx) => (
                <React.Fragment key={page.id}>
                  <button
                    onClick={() => navigateMeeting(page.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      currentPage.id === page.id
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                        : idx < currentIndex
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {page.name}
                  </button>
                  {idx < MEETING_PAGES.length - 1 && (
                    <ChevronRight className={`h-4 w-4 flex-shrink-0 ${
                      idx < currentIndex 
                        ? 'text-green-400' 
                        : 'text-slate-300 dark:text-slate-600'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Meeting Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content Area */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {currentPage.pillar_id && (() => {
                      const Icon = PILLAR_ICONS[currentPage.pillar_id];
                      return Icon ? <Icon className="h-5 w-5" /> : null;
                    })()}
                    {currentPage.name}
                  </CardTitle>
                  {currentPage.type === 'overview' && (
                    <CardDescription>Review the current state of all 5 pillars</CardDescription>
                  )}
                  {currentPage.type === 'priorities' && (
                    <CardDescription>Capture this week&apos;s key priorities and action items</CardDescription>
                  )}
                  {currentPage.type === 'wrap_up' && (
                    <CardDescription>Review decisions, action items, and blockers</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {/* Welcome/Start Screen */}
                  {currentPage.type === 'welcome' && (
                    <div className="flex flex-col items-center justify-center py-16 space-y-6">
                      <img src="/rhino-shield-logo.png" alt="Rhino Shield" className="h-24 w-24 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                      <div className="text-center space-y-2">
                        <h1 className="text-4xl font-bold text-primary">BIZOPS Weekly Update</h1>
                        <p className="text-xl text-muted-foreground">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        <p className="text-sm text-muted-foreground mt-4">Presented by {activeMeeting.created_by_name || 'Ryan March'}</p>
                      </div>
                      <Button size="lg" className="mt-8 gap-2" onClick={() => navigateMeeting('agenda')}>
                        <Play className="h-5 w-5" /> Start Meeting
                      </Button>
                    </div>
                  )}

                  {/* Agenda Page */}
                  {currentPage.type === 'agenda' && (
                    <div className="space-y-4">
                      <CardDescription>Set the agenda for today's meeting</CardDescription>
                      <div className="space-y-2">
                        {(activeMeeting.agenda || []).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg group">
                            <span className="text-sm font-bold text-primary w-6 shrink-0">{idx + 1}.</span>
                            <Input
                              value={item}
                              onChange={(e) => {
                                const updated = [...(activeMeeting.agenda || [])];
                                updated[idx] = e.target.value;
                                setActiveMeeting(prev => ({ ...prev, agenda: updated }));
                              }}
                              className="flex-1 text-sm"
                              placeholder="Agenda item..."
                            />
                            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100" onClick={() => {
                              const updated = (activeMeeting.agenda || []).filter((_, i) => i !== idx);
                              setActiveMeeting(prev => ({ ...prev, agenda: updated }));
                            }}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <Button variant="outline" size="sm" className="gap-1 w-full border-dashed" onClick={() => {
                        setActiveMeeting(prev => ({ ...prev, agenda: [...(prev.agenda || []), ''] }));
                      }}>
                        <Plus className="h-4 w-4" /> Add Agenda Item
                      </Button>
                      {(activeMeeting.agenda || []).length === 0 && (
                        <p className="text-center text-sm text-muted-foreground py-4">No agenda items yet. Click "Add Agenda Item" to get started.</p>
                      )}
                    </div>
                  )}

                  {/* Weekly Recap Page */}
                  {currentPage.type === 'recap' && (
                    <div className="space-y-6">
                      <CardDescription>Overview of the previous week's work and this week's focus</CardDescription>
                      
                      {/* Unfinished from Last Meeting */}
                      {(activeMeeting.weekly_recap?.unfinished_from_last || []).length > 0 && (
                        <div className="space-y-2">
                          <h3 className="font-semibold flex items-center gap-2 text-orange-600"><Clock className="h-4 w-4" /> Unfinished from Last Meeting</h3>
                          {activeMeeting.weekly_recap.unfinished_from_last.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded border-l-2 border-orange-400">
                              <CheckCircle className="h-4 w-4 text-orange-400 shrink-0 cursor-pointer hover:text-green-500" title="Mark as done" onClick={() => {
                                const updated = (activeMeeting.weekly_recap?.unfinished_from_last || []).filter((_, i) => i !== idx);
                                const completed = [...(activeMeeting.weekly_recap?.completed || []), item];
                                setActiveMeeting(prev => ({ ...prev, weekly_recap: { ...prev.weekly_recap, unfinished_from_last: updated, completed } }));
                              }} />
                              <span className="text-sm flex-1">{item}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Last Meeting Notes */}
                      {(activeMeeting.weekly_recap?.last_meeting_notes || []).length > 0 && (
                        <div className="space-y-2">
                          <h3 className="font-semibold flex items-center gap-2 text-blue-600"><MessageSquare className="h-4 w-4" /> From Our Last Meeting</h3>
                          {activeMeeting.weekly_recap.last_meeting_notes.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-sm">
                              <span className="text-blue-400">•</span>
                              <span className="flex-1">{item}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Completed This Week */}
                      <div className="space-y-2">
                        <h3 className="font-semibold flex items-center gap-2 text-green-600"><CheckCircle className="h-4 w-4" /> Completed This Week</h3>
                        {(activeMeeting.weekly_recap?.completed || []).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded group">
                            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                            <Input value={item} onChange={(e) => {
                              const updated = [...(activeMeeting.weekly_recap?.completed || [])];
                              updated[idx] = e.target.value;
                              setActiveMeeting(prev => ({ ...prev, weekly_recap: { ...prev.weekly_recap, completed: updated } }));
                            }} className="flex-1 text-sm bg-transparent border-0 h-8" placeholder="What was completed..." />
                            <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => {
                              const updated = (activeMeeting.weekly_recap?.completed || []).filter((_, i) => i !== idx);
                              setActiveMeeting(prev => ({ ...prev, weekly_recap: { ...prev.weekly_recap, completed: updated } }));
                            }}><X className="h-3 w-3" /></Button>
                          </div>
                        ))}
                        <Button variant="ghost" size="sm" className="text-green-600 gap-1 w-full border border-dashed border-green-300 dark:border-green-800" onClick={() => {
                          setActiveMeeting(prev => ({ ...prev, weekly_recap: { ...prev.weekly_recap, completed: [...(prev.weekly_recap?.completed || []), ''] } }));
                        }}><Plus className="h-3 w-3" /> Add Completed Item</Button>
                      </div>

                      {/* Issues Found */}
                      <div className="space-y-2">
                        <h3 className="font-semibold flex items-center gap-2 text-red-600"><AlertTriangle className="h-4 w-4" /> Issues Found</h3>
                        {(activeMeeting.weekly_recap?.issues || []).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 rounded group">
                            <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                            <Input value={item} onChange={(e) => {
                              const updated = [...(activeMeeting.weekly_recap?.issues || [])];
                              updated[idx] = e.target.value;
                              setActiveMeeting(prev => ({ ...prev, weekly_recap: { ...prev.weekly_recap, issues: updated } }));
                            }} className="flex-1 text-sm bg-transparent border-0 h-8" placeholder="Issue description..." />
                            <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => {
                              const updated = (activeMeeting.weekly_recap?.issues || []).filter((_, i) => i !== idx);
                              setActiveMeeting(prev => ({ ...prev, weekly_recap: { ...prev.weekly_recap, issues: updated } }));
                            }}><X className="h-3 w-3" /></Button>
                          </div>
                        ))}
                        <Button variant="ghost" size="sm" className="text-red-600 gap-1 w-full border border-dashed border-red-300 dark:border-red-800" onClick={() => {
                          setActiveMeeting(prev => ({ ...prev, weekly_recap: { ...prev.weekly_recap, issues: [...(prev.weekly_recap?.issues || []), ''] } }));
                        }}><Plus className="h-3 w-3" /> Add Issue</Button>
                      </div>

                      {/* This Week's Focus */}
                      <div className="space-y-2">
                        <h3 className="font-semibold flex items-center gap-2 text-purple-600"><Target className="h-4 w-4" /> This Week's Focus</h3>
                        {(activeMeeting.weekly_recap?.focus_this_week || []).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-purple-50 dark:bg-purple-900/20 rounded group">
                            <Target className="h-4 w-4 text-purple-500 shrink-0" />
                            <Input value={item} onChange={(e) => {
                              const updated = [...(activeMeeting.weekly_recap?.focus_this_week || [])];
                              updated[idx] = e.target.value;
                              setActiveMeeting(prev => ({ ...prev, weekly_recap: { ...prev.weekly_recap, focus_this_week: updated } }));
                            }} className="flex-1 text-sm bg-transparent border-0 h-8" placeholder="Focus area for this week..." />
                            <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => {
                              const updated = (activeMeeting.weekly_recap?.focus_this_week || []).filter((_, i) => i !== idx);
                              setActiveMeeting(prev => ({ ...prev, weekly_recap: { ...prev.weekly_recap, focus_this_week: updated } }));
                            }}><X className="h-3 w-3" /></Button>
                          </div>
                        ))}
                        <Button variant="ghost" size="sm" className="text-purple-600 gap-1 w-full border border-dashed border-purple-300 dark:border-purple-800" onClick={() => {
                          setActiveMeeting(prev => ({ ...prev, weekly_recap: { ...prev.weekly_recap, focus_this_week: [...(prev.weekly_recap?.focus_this_week || []), ''] } }));
                        }}><Plus className="h-3 w-3" /> Add Focus Item</Button>
                      </div>
                    </div>
                  )}

                  {/* Overview Page (legacy) */}
                  {currentPage.type === 'overview' && (
                    <div className="space-y-4">
                      {pillars.map(pillar => {
                        const Icon = PILLAR_ICONS[pillar.id];
                        const colorClasses = getColorClasses(PILLAR_COLORS[pillar.id]);
                        return (
                          <div key={pillar.id} className={`p-4 rounded-lg border ${colorClasses.border} ${colorClasses.bg}`}>
                            <div className="flex items-center gap-3">
                              {Icon && <Icon className={`h-5 w-5 ${colorClasses.text}`} />}
                              <span className="font-medium dark:text-white">{pillar.name}</span>
                              {pillar.latest_update?.blockers && (
                                <Badge variant="destructive" className="ml-auto">Has Blockers</Badge>
                              )}
                            </div>
                            {pillar.latest_update && (
                              <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                                {pillar.latest_update.what_worked_on}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Pillar Page */}
                  {currentPage.type === 'pillar' && (
                    <div className="space-y-6">
                      {/* Status Update Section */}
                      {pillarUpdate ? (
                        <>
                          {/* Edit button in meeting mode */}
                          <div className="flex justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const pillar = pillars.find(p => p.id === currentPage.pillar_id);
                                if (pillar) openEditDialog(pillar);
                              }}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Pencil className="h-3 w-3 mr-1" />
                              Edit Content
                            </Button>
                          </div>
                          <div>
                            <Label className="text-sm text-muted-foreground">What was worked on</Label>
                            <p className="mt-1 dark:text-white">{pillarUpdate.what_worked_on}</p>
                          </div>
                          <div>
                            <Label className="text-sm text-muted-foreground">What&apos;s next</Label>
                            <p className="mt-1 dark:text-white">{pillarUpdate.whats_next}</p>
                          </div>
                          {pillarUpdate.blockers && (
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                              <Label className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4" />
                                Blockers
                              </Label>
                              <p className="mt-1 text-red-700 dark:text-red-300">{pillarUpdate.blockers}</p>
                            </div>
                          )}
                          {pillarUpdate.todos && pillarUpdate.todos.length > 0 && (
                            <div>
                              <Label className="text-sm text-muted-foreground">To-dos</Label>
                              <div className="mt-2 space-y-2">
                                {pillarUpdate.todos.map((todo, idx) => (
                                  <div key={idx} className="flex items-center gap-2">
                                    {todo.completed ? (
                                      <CheckCircle className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <Circle className="h-4 w-4 text-slate-400" />
                                    )}
                                    <span className={todo.completed ? 'line-through text-muted-foreground' : 'dark:text-white'}>
                                      {todo.text}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Updated {formatDistanceToNow(new Date(pillarUpdate.created_at), { addSuffix: true })} by {pillarUpdate.created_by_name}
                          </p>
                        </>
                      ) : (
                        <div className="text-center py-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                          <p className="text-muted-foreground mb-2">No status update for this pillar.</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const pillar = pillars.find(p => p.id === currentPage.pillar_id);
                              if (pillar) {
                                setSelectedPillar(pillar);
                                setEditingUpdate(null);
                                setUpdateForm({ what_worked_on: '', whats_next: '', blockers: '', links: [], todos: [] });
                                setShowUpdateDialog(true);
                              }
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Update
                          </Button>
                        </div>
                      )}

                      {/* Pillar-specific Data from Hub */}
                      <Separator className="my-4" />
                      
                      {/* Scope Data */}
                      {currentPage.pillar_id === 'scope' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              <Flag className="h-4 w-4 text-orange-500" />
                              MVP Deliverables
                            </Label>
                            <Badge variant="secondary">{scopeData.mvp_completed || 0}/{scopeData.mvp_total || 0}</Badge>
                          </div>
                          {scopeData.mvp_items?.length > 0 ? (
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {scopeData.mvp_items.slice(0, 10).map(item => (
                                <div key={item.id} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded">
                                  {item.status === 'completed' ? (
                                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                                  ) : item.status === 'in_progress' ? (
                                    <Clock className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                  ) : (
                                    <Circle className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                  )}
                                  <span className={`text-sm ${item.status === 'completed' ? 'line-through text-muted-foreground' : 'dark:text-white'}`}>
                                    {item.name}
                                  </span>
                                  {item.hub && <Badge variant="outline" className="text-xs ml-auto">{item.hub}</Badge>}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">No MVP items defined</p>
                          )}
                          <Button variant="outline" size="sm" onClick={() => setShowScopeModal(true)}>
                            View Full Scope
                          </Button>
                        </div>
                      )}

                      {/* Timeline Data - Features from Scope */}
                      {currentPage.pillar_id === 'timeline' && (
                        <div className="space-y-4">
                          {/* Header with progress */}
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-blue-500" />
                              Development Timeline
                            </Label>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">
                                {scopeData.items?.filter(i => i.status === 'completed').length || 0}/{scopeData.items?.length || 0} Features
                              </Badge>
                            </div>
                          </div>
                          
                          {/* MVP Deadline countdown */}
                          <div className="p-3 bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-500/20 dark:to-purple-500/20 rounded-lg border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium dark:text-white">MVP Target</span>
                              <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                                {timelineData.mvp_deadline ? format(parseLocalDate(timelineData.mvp_deadline), 'MMM d, yyyy') : 'Not set'}
                              </span>
                            </div>
                            {timelineData.mvp_deadline && (
                              <div className="mt-2">
                                <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all"
                                    style={{ 
                                      width: `${Math.min(100, Math.max(0, 
                                        ((new Date() - parseLocalDate(timelineData.project_start || '2025-10-01')) / 
                                        (parseLocalDate(timelineData.mvp_deadline) - parseLocalDate(timelineData.project_start || '2025-10-01'))) * 100
                                      ))}%` 
                                    }}
                                  />
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {Math.ceil((parseLocalDate(timelineData.mvp_deadline) - new Date()) / (1000 * 60 * 60 * 24))} days remaining
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Features from Scope grouped by status */}
                          {scopeData.items?.length > 0 ? (
                            <div className="space-y-3">
                              {/* In Progress Features */}
                              {scopeData.items.filter(i => i.status === 'in_progress').length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-2 flex items-center gap-1">
                                    <Clock className="h-3 w-3" /> In Progress ({scopeData.items.filter(i => i.status === 'in_progress').length})
                                  </p>
                                  <div className="space-y-1.5 max-h-24 overflow-y-auto">
                                    {scopeData.items.filter(i => i.status === 'in_progress').slice(0, 5).map(item => (
                                      <div key={item.id} className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-sm">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                          <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse flex-shrink-0" />
                                          <span className="truncate dark:text-white">{item.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                          {item.progress > 0 && (
                                            <span className="text-xs text-blue-600 dark:text-blue-400">{item.progress}%</span>
                                          )}
                                          {item.hub && <Badge variant="outline" className="text-[10px]">{item.hub}</Badge>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Upcoming Features (not started) */}
                              {scopeData.items.filter(i => i.status === 'not_started' || !i.status).length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1">
                                    <Circle className="h-3 w-3" /> Upcoming ({scopeData.items.filter(i => i.status === 'not_started' || !i.status).length})
                                  </p>
                                  <div className="space-y-1.5 max-h-24 overflow-y-auto">
                                    {scopeData.items.filter(i => i.status === 'not_started' || !i.status).slice(0, 5).map(item => (
                                      <div key={item.id} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded text-sm">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                          <div className="h-2 w-2 rounded-full bg-slate-300 dark:bg-slate-600 flex-shrink-0" />
                                          <span className="truncate dark:text-white">{item.name}</span>
                                        </div>
                                        {item.target_date && (
                                          <span className="text-xs text-muted-foreground flex-shrink-0">
                                            {format(parseLocalDate(item.target_date), 'MMM d')}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Recently Completed */}
                              {scopeData.items.filter(i => i.status === 'completed').length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-2 flex items-center gap-1">
                                    <CheckCircle className="h-3 w-3" /> Completed ({scopeData.items.filter(i => i.status === 'completed').length})
                                  </p>
                                  <div className="space-y-1.5 max-h-20 overflow-y-auto">
                                    {scopeData.items.filter(i => i.status === 'completed').slice(0, 3).map(item => (
                                      <div key={item.id} className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded text-sm">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                          <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                                          <span className="truncate text-muted-foreground line-through">{item.name}</span>
                                        </div>
                                        {item.hub && <Badge variant="outline" className="text-[10px] flex-shrink-0">{item.hub}</Badge>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              No features in scope yet. Add features to track development progress.
                            </p>
                          )}

                          <Button variant="outline" size="sm" onClick={() => setShowTimelineModal(true)}>
                            View Full Timeline
                          </Button>
                        </div>
                      )}

                      {/* Cost Data */}
                      {currentPage.pillar_id === 'cost' && (
                        <div className="space-y-4">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-amber-500" />
                            Cost Summary
                          </Label>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                              <p className="text-xs text-muted-foreground">Monthly Expenses</p>
                              <p className="text-lg font-bold text-blue-600">${costData.totals?.monthly_expenses?.toLocaleString() || 0}</p>
                            </div>
                            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                              <p className="text-xs text-muted-foreground">Monthly Salary</p>
                              <p className="text-lg font-bold text-purple-600">${costData.totals?.monthly_salary?.toLocaleString() || 0}</p>
                            </div>
                            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                              <p className="text-xs text-muted-foreground">One-Time Costs</p>
                              <p className="text-lg font-bold text-green-600">${costData.totals?.one_time_costs?.toLocaleString() || 0}</p>
                            </div>
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                              <p className="text-xs text-muted-foreground">Monthly Burn Rate</p>
                              <p className="text-lg font-bold text-red-600">${((costData.totals?.monthly_expenses || 0) + (costData.totals?.monthly_salary || 0)).toLocaleString()}</p>
                            </div>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => setShowCostModal(true)}>
                            View Full Costs
                          </Button>
                        </div>
                      )}

                      {/* Implementation & Data Security - show a simple status */}
                      {/* Implementation - Show actual items */}
                      {currentPage.pillar_id === 'implementation' && (
                        <div className="space-y-3">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <ClipboardList className="h-4 w-4 text-green-500" />
                            Implementation Items
                          </Label>
                          {(implementationData.items || []).length > 0 ? (
                            (implementationData.items || []).map((item, idx) => (
                              <div key={item.id || idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border dark:border-slate-700">
                                <div className="flex items-center gap-3 flex-1">
                                  <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                                    item.status === 'completed' ? 'bg-green-500' : 
                                    item.status === 'in_progress' ? 'bg-blue-500' : 'bg-slate-300'
                                  }`} />
                                  <div>
                                    <p className="text-sm font-medium dark:text-white">{item.name}</p>
                                    {item.hub && <p className="text-xs text-muted-foreground">{item.hub}</p>}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {item.progress > 0 && (
                                    <span className="text-xs text-muted-foreground">{item.progress}%</span>
                                  )}
                                  <Badge variant={item.status === 'completed' ? 'default' : 'secondary'} className="text-[10px]">
                                    {item.status || 'pending'}
                                  </Badge>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground text-center py-4">No implementation items yet</p>
                          )}
                        </div>
                      )}

                      {/* Data & Security - Show actual items */}
                      {currentPage.pillar_id === 'data_security' && (
                        <div className="space-y-3">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <ShieldCheck className="h-4 w-4 text-cyan-500" />
                            Data & Security Items
                          </Label>
                          {dataSecurityData.items?.length > 0 ? (
                            dataSecurityData.items.map((item, idx) => (
                              <div key={item.id || idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border dark:border-slate-700">
                                <div className="flex items-center gap-3 flex-1">
                                  <ShieldCheck className={`h-4 w-4 shrink-0 ${
                                    item.status === 'reviewed' || item.status === 'completed' ? 'text-green-500' : 
                                    item.status === 'in_progress' ? 'text-blue-500' : 'text-amber-500'
                                  }`} />
                                  <div>
                                    <p className="text-sm font-medium dark:text-white">{item.name}</p>
                                    {item.description && <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>}
                                  </div>
                                </div>
                                <Badge variant={item.status === 'reviewed' || item.status === 'completed' ? 'default' : 'secondary'} className="text-[10px]">
                                  {item.status || 'pending review'}
                                </Badge>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground text-center py-4">No data & security items yet</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Priorities Page */}
                  {currentPage.type === 'priorities' && (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Capture key priorities, action items, and decisions for this week.
                      </p>
                      {(activeMeeting.action_items || []).length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Action Items</Label>
                          {activeMeeting.action_items.map(item => (
                            <div key={item.id} className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                              <div className="flex items-center gap-2">
                                <Flag className="h-4 w-4 text-amber-600" />
                                <span className="dark:text-white">{item.content}</span>
                                {item.owner_name && (
                                  <Badge variant="outline" className="ml-2">{item.owner_name}</Badge>
                                )}
                              </div>
                              {!item.task_id ? (
                                <Button size="sm" variant="outline" onClick={() => convertToTask(item.id, item.content)}>
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add to Tasks
                                </Button>
                              ) : (
                                <Badge className="bg-green-600">Task Created</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Wrap-up Page */}
                  {currentPage.type === 'wrap_up' && (
                    <div className="space-y-6">
                      {/* Decisions */}
                      <div>
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <Lightbulb className="h-4 w-4 text-yellow-500" />
                          Decisions Made
                        </Label>
                        {(activeMeeting.decisions || []).length > 0 ? (
                          <div className="mt-2 space-y-2">
                            {activeMeeting.decisions.map((d, idx) => (
                              <div key={idx} className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border-l-4 border-yellow-500">
                                <p className="dark:text-white">{d.content}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-muted-foreground">No decisions recorded</p>
                        )}
                      </div>

                      {/* Action Items Summary */}
                      <div>
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <ListChecks className="h-4 w-4 text-amber-500" />
                          Action Items ({(activeMeeting.action_items || []).length})
                        </Label>
                        {(activeMeeting.action_items || []).length > 0 ? (
                          <div className="mt-2 space-y-2">
                            {activeMeeting.action_items.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                                <span className="dark:text-white">{item.content}</span>
                                <div className="flex items-center gap-2">
                                  {item.owner_name && <Badge variant="outline">{item.owner_name}</Badge>}
                                  {item.task_id && <Badge className="bg-green-600">Task</Badge>}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-muted-foreground">No action items</p>
                        )}
                      </div>

                      {/* Blockers */}
                      <div>
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          Blockers
                        </Label>
                        {(activeMeeting.blockers || []).length > 0 ? (
                          <div className="mt-2 space-y-2">
                            {activeMeeting.blockers.map((b, idx) => (
                              <div key={idx} className="p-2 bg-red-50 dark:bg-red-900/20 rounded border-l-4 border-red-500">
                                <p className="dark:text-white">{b.content}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-muted-foreground">No blockers recorded</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Page Notes */}
                  {pageNotes.length > 0 && (
                    <div className="mt-6 pt-6 border-t dark:border-slate-700">
                      <Label className="text-sm font-medium">Meeting Notes</Label>
                      <div className="mt-2 space-y-2">
                        {pageNotes.map(note => (
                          <div key={note.id} className={`p-2 rounded text-sm ${
                            note.is_action_item ? 'bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500' :
                            note.is_decision ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500' :
                            note.is_blocker ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500' :
                            'bg-slate-50 dark:bg-slate-800'
                          }`}>
                            <p className="dark:text-white">{note.content}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {note.created_by_name} • {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={goToPrevPage}
                  disabled={isFirstPage}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                
                {isLastPage ? (
                  <Button onClick={finishMeeting} className="bg-green-600 hover:bg-green-700">
                    <Check className="h-4 w-4 mr-1" />
                    Finish Meeting
                  </Button>
                ) : (
                  <Button onClick={goToNextPage}>
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>

            {/* Notes Sidebar */}
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Add Note</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant={noteType === 'note' ? 'default' : 'outline'}
                      onClick={() => setNoteType('note')}
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      Note
                    </Button>
                    <Button
                      size="sm"
                      variant={noteType === 'action' ? 'default' : 'outline'}
                      onClick={() => setNoteType('action')}
                      className={noteType === 'action' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                    >
                      <Flag className="h-3 w-3 mr-1" />
                      Action
                    </Button>
                    <Button
                      size="sm"
                      variant={noteType === 'decision' ? 'default' : 'outline'}
                      onClick={() => setNoteType('decision')}
                      className={noteType === 'decision' ? 'bg-green-600 hover:bg-green-700' : ''}
                    >
                      <Lightbulb className="h-3 w-3 mr-1" />
                      Decision
                    </Button>
                    <Button
                      size="sm"
                      variant={noteType === 'task' ? 'default' : 'outline'}
                      onClick={() => setNoteType('task')}
                      className={noteType === 'task' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                    >
                      <ListChecks className="h-3 w-3 mr-1" />
                      Task
                    </Button>
                  </div>
                  <Textarea
                    placeholder={noteType === 'task' ? 'Describe the task...' : 'Type your note...'}
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="min-h-[80px]"
                  />
                  {noteType === 'task' && (
                    <p className="text-xs text-purple-500">
                      <ListChecks className="h-3 w-3 inline mr-1" />
                      This will create a synced task in your RHINO-1 project
                    </p>
                  )}
                  <Button onClick={addMeetingNote} disabled={!newNote.trim()} className="w-full">
                    <Plus className="h-4 w-4 mr-1" />
                    Add {noteType === 'action' ? 'Action Item' : noteType === 'decision' ? 'Decision' : noteType === 'task' ? 'Task' : 'Note'}
                  </Button>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Notes</span>
                      <span className="font-medium dark:text-white">{(activeMeeting.notes || []).length}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Action Items</span>
                      <span className="font-medium dark:text-white">{(activeMeeting.action_items || []).length}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Decisions</span>
                      <span className="font-medium dark:text-white">{(activeMeeting.decisions || []).length}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Tasks Created</span>
                      <span className="font-medium text-purple-500">{(activeMeeting.tasks || []).length}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Blockers</span>
                      <span className="font-medium dark:text-white">{(activeMeeting.blockers || []).length}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tasks List with Bi-directional Sync */}
              {(activeMeeting.tasks || []).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <ListChecks className="h-4 w-4 text-purple-500" />
                      Meeting Tasks
                      <Badge variant="secondary" className="ml-auto text-xs">
                        Synced to Profile
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {(activeMeeting.tasks || []).map((task) => (
                        <div 
                          key={task.id} 
                          data-testid={`meeting-task-${task.id}`}
                          className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${
                            task.status === 'completed' ? 'bg-green-50 dark:bg-green-900/20' : ''
                          }`}
                          onClick={() => toggleMeetingTask(task.id, task.status)}
                        >
                          {task.status === 'completed' ? (
                            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          ) : (
                            <Circle className="h-4 w-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          )}
                          <span className={`text-sm flex-1 ${
                            task.status === 'completed' ? 'line-through text-muted-foreground' : 'dark:text-white'
                          }`}>
                            {task.content}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                      <RefreshCw className="h-3 w-3" />
                      Click to toggle • Changes sync to your profile
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
      </Layout>
    );
  }

  // Handle layout change
  const onLayoutChange = (newLayout) => {
    setGridLayout(newLayout);
    localStorage.setItem('five_pillars_layout', JSON.stringify(newLayout));
  };

  // Reset layout to default
  const resetLayout = () => {
    const defaultLayout = [
      { i: 'scope', x: 0, y: 0, w: 4, h: 4, minW: 3, minH: 3 },
      { i: 'timeline', x: 4, y: 0, w: 4, h: 4, minW: 3, minH: 3 },
      { i: 'implementation', x: 8, y: 0, w: 4, h: 4, minW: 3, minH: 3 },
      { i: 'data_security', x: 0, y: 4, w: 4, h: 4, minW: 3, minH: 3 },
      { i: 'cost', x: 4, y: 4, w: 4, h: 4, minW: 3, minH: 3 },
      { i: 'weekly_recap', x: 8, y: 4, w: 4, h: 4, minW: 4, minH: 3 },
      { i: 'recent_meetings', x: 0, y: 8, w: 6, h: 4, minW: 4, minH: 3 }
    ];
    setGridLayout(defaultLayout);
    localStorage.setItem('five_pillars_layout', JSON.stringify(defaultLayout));
    toast.success('Layout reset to default');
  };

  // Render pillar card content
  const renderPillarCard = (pillar) => {
    const Icon = PILLAR_ICONS[pillar.id];
    const colorClasses = getColorClasses(PILLAR_COLORS[pillar.id]);
    const hasComments = showComments[pillar.id];
    const isScope = pillar.id === 'scope';
    const isTimeline = pillar.id === 'timeline';
    const isCost = pillar.id === 'cost';
    const isTasks = pillar.id === 'tasks';
    
    return (
      <Card className={`h-full hover:shadow-lg transition-shadow border-2 ${colorClasses.border} ${isEditMode ? 'cursor-move' : ''} flex flex-col max-h-[600px]`}>
        <CardHeader className={`${colorClasses.bg} rounded-t-lg flex-shrink-0`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isEditMode && <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />}
              {Icon && <Icon className={`h-5 w-5 ${colorClasses.text}`} />}
              <CardTitle className={`text-base ${colorClasses.text}`}>{pillar.name}</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              {/* View Scope Button */}
              {isScope && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setShowScopeModal(true);
                    fetchScopeData();
                  }}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  View Scope
                </Button>
              )}
              {/* View Timeline Button */}
              {isTimeline && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setShowTimelineModal(true);
                    fetchTimelineData();
                  }}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  View Timeline
                </Button>
              )}
              {/* View Costs Button */}
              {isCost && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setShowCostModal(true);
                    fetchCostData();
                  }}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  View Costs
                </Button>
              )}
              {/* View Implementation Plan Button */}
              {pillar.id === 'implementation' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setShowImplementationModal(true);
                    fetchImplementationData();
                  }}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  View Plan
                </Button>
              )}
              {/* View Data & Security Button */}
              {pillar.id === 'data_security' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setShowDataSecurityModal(true);
                    fetchDataSecurityData();
                  }}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  View Security
                </Button>
              )}
              {/* View Tasks Button */}
              {isTasks && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setShowTasksModal(true);
                    fetchTasksData();
                  }}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  View Tasks
                </Button>
              )}
              {pillar.unread_comments > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {pillar.unread_comments} new
                </Badge>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canEdit && pillar.latest_update && (
                    <DropdownMenuItem onClick={() => openEditDialog(pillar)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit Update
                    </DropdownMenuItem>
                  )}
                  {canEdit && (
                    <DropdownMenuItem onClick={() => {
                      setSelectedPillar(pillar);
                      setEditingUpdate(null);
                      setUpdateForm({ what_worked_on: '', whats_next: '', blockers: '', links: [], todos: [] });
                      setShowUpdateDialog(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Update
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => {
                    setShowComments(prev => ({ ...prev, [pillar.id]: !prev[pillar.id] }));
                    if (!comments[pillar.id]) fetchPillarComments(pillar.id);
                  }}>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    {hasComments ? 'Hide Comments' : 'Show Comments'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-3 overflow-y-auto flex-1">
          {/* Timeline Metrics Section - At a glance */}
          {isTimeline && timelineData.milestones?.length > 0 && (() => {
            const projectProgress = getOverallProjectProgress();
            
            // Get ALL milestones (flat list including sub-milestones)
            const allMilestones = timelineData.all_milestones || [];
            // Fallback: flatten hierarchical structure if all_milestones not available
            const flatMilestones = allMilestones.length > 0 ? allMilestones : 
              timelineData.milestones.reduce((acc, m) => {
                acc.push(m);
                if (m.sub_milestones?.length) acc.push(...m.sub_milestones);
                return acc;
              }, []);
            
            const totalMilestones = flatMilestones.length;
            const completedMilestones = flatMilestones.filter(m => m.status === 'completed').length;
            const inProgressMilestones = flatMilestones.filter(m => m.status === 'in_progress').length;
            const pendingMilestones = flatMilestones.filter(m => m.status === 'pending').length;
            
            // Get next 10 upcoming milestones for mini Gantt (non-completed with future target dates)
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Start of today
            const upcomingMilestones = flatMilestones
              .filter(m => m.status !== 'completed' && m.target_date && new Date(m.target_date) >= today)
              .sort((a, b) => new Date(a.target_date) - new Date(b.target_date))
              .slice(0, 10);
            
            // Get next 10 deadlines for the deadline list
            const upcomingDeadlines = flatMilestones
              .filter(m => m.status !== 'completed' && m.target_date && new Date(m.target_date) >= today)
              .sort((a, b) => new Date(a.target_date) - new Date(b.target_date))
              .slice(0, 10);
            
            // Calculate mini Gantt date range (today to furthest upcoming milestone)
            const miniGanttStart = today;
            const miniGanttEnd = upcomingMilestones.length > 0 
              ? new Date(Math.max(...upcomingMilestones.map(m => new Date(m.target_date))))
              : new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days from now
            const miniGanttRange = miniGanttEnd - miniGanttStart;
            
            // Phase colors for mini Gantt bars
            const getPhaseColor = (phase) => {
              const colors = {
                planning: 'bg-indigo-500',
                design: 'bg-pink-500',
                development: 'bg-blue-500',
                internal_testing: 'bg-amber-500',
                staff_testing: 'bg-orange-500',
                deploy_ready: 'bg-cyan-500',
                full_testing: 'bg-purple-500',
                mvp_ready: 'bg-green-500'
              };
              return colors[phase] || 'bg-slate-500';
            };
            
            return (
              <div className="mb-4 space-y-3">
                {/* MVP Progress Bar */}
                <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300">MVP Progress</span>
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{projectProgress.percent}%</span>
                  </div>
                  <div className="h-2 bg-blue-100 dark:bg-blue-900/40 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500"
                      style={{ width: `${projectProgress.percent}%` }}
                    />
                  </div>
                </div>
                
                {/* Quick Stats Row */}
                <div className="grid grid-cols-4 gap-1.5">
                  <div className="text-center p-1.5 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-base font-bold text-green-600 dark:text-green-400">{completedMilestones}</p>
                    <p className="text-[9px] text-green-700 dark:text-green-300">Done</p>
                  </div>
                  <div className="text-center p-1.5 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <p className="text-base font-bold text-yellow-600 dark:text-yellow-400">{inProgressMilestones}</p>
                    <p className="text-[9px] text-yellow-700 dark:text-yellow-300">Active</p>
                  </div>
                  <div className="text-center p-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    <p className="text-base font-bold text-slate-500 dark:text-slate-400">{pendingMilestones}</p>
                    <p className="text-[9px] text-slate-500 dark:text-slate-400">Pending</p>
                  </div>
                  <div className="text-center p-1.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                    <p className="text-base font-bold text-indigo-600 dark:text-indigo-400">{totalMilestones}</p>
                    <p className="text-[9px] text-indigo-700 dark:text-indigo-300">Total</p>
                  </div>
                </div>
                
                {/* Mini Gantt Preview - Next 3 milestones */}
                {upcomingMilestones.length > 0 && (
                  <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-medium text-slate-600 dark:text-slate-400">Upcoming Milestones</span>
                      <span className="text-[9px] text-slate-500">
                        {format(today, 'MMM d')} → {format(miniGanttEnd, 'MMM d')}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {upcomingMilestones.map((m, idx) => {
                        const startDate = m.start_date ? new Date(m.start_date) : today;
                        const endDate = new Date(m.target_date);
                        const barStart = Math.max(0, ((startDate - miniGanttStart) / miniGanttRange) * 100);
                        const barEnd = Math.min(100, ((endDate - miniGanttStart) / miniGanttRange) * 100);
                        const barWidth = Math.max(8, barEnd - barStart);
                        const isExpanded = expandedCardMilestone === m.id;
                        const milestoneProgress = m.status === 'completed' ? 100 : (m.progress || 0);
                        
                        return (
                          <div key={m.id || idx}>
                            {/* Clickable Milestone Row */}
                            <div 
                              className={`flex items-center gap-2 cursor-pointer rounded transition-colors ${isExpanded ? 'bg-slate-200/50 dark:bg-slate-700/50 -mx-1 px-1' : 'hover:bg-slate-100 dark:hover:bg-slate-700/30'}`}
                              onClick={() => setExpandedCardMilestone(isExpanded ? null : m.id)}
                            >
                              <div className="w-20 truncate flex items-center gap-1">
                                <ChevronRight className={`h-3 w-3 text-slate-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                                <span className="text-[9px] text-slate-600 dark:text-slate-300 font-medium truncate">{m.name}</span>
                              </div>
                              <div className="flex-1 h-4 bg-slate-200 dark:bg-slate-700 rounded relative overflow-hidden">
                                <div 
                                  className={`absolute h-full ${getPhaseColor(m.phase_type)} rounded opacity-90 transition-all`}
                                  style={{ 
                                    left: `${barStart}%`, 
                                    width: `${barWidth}%`
                                  }}
                                />
                                {/* Progress overlay */}
                                {milestoneProgress > 0 && (
                                  <div 
                                    className="absolute h-full bg-green-400/50 rounded-l"
                                    style={{ 
                                      left: `${barStart}%`, 
                                      width: `${barWidth * milestoneProgress / 100}%`
                                    }}
                                  />
                                )}
                              </div>
                              <span className="text-[9px] text-slate-500 w-10 text-right">
                                {format(endDate, 'M/d')}
                              </span>
                            </div>
                            
                            {/* Expanded Details Panel */}
                            {isExpanded && (
                              <div className="ml-4 mt-1.5 mb-2 p-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 space-y-2 animate-in slide-in-from-top-1 duration-200">
                                {/* Progress Bar */}
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-[9px] text-slate-500">Progress</span>
                                    <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300">{milestoneProgress}%</span>
                                  </div>
                                  <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full transition-all"
                                      style={{ width: `${milestoneProgress}%` }}
                                    />
                                  </div>
                                </div>
                                
                                {/* Status & Dates Row */}
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                                    m.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                    m.status === 'in_progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                    m.status === 'delayed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                    'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                                  }`}>
                                    {m.status?.replace('_', ' ') || 'pending'}
                                  </span>
                                  {m.phase_type && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 font-medium">
                                      {m.phase_type?.replace('_', ' ')}
                                    </span>
                                  )}
                                </div>
                                
                                {/* Dates */}
                                <div className="flex items-center gap-3 text-[9px] text-slate-500">
                                  <span>📅 Start: {m.start_date ? format(new Date(m.start_date), 'MMM d') : 'Not set'}</span>
                                  <span>🎯 Target: {format(endDate, 'MMM d, yyyy')}</span>
                                </div>
                                
                                {/* Description */}
                                {m.description && (
                                  <p className="text-[10px] text-slate-600 dark:text-slate-400 line-clamp-2">
                                    {m.description}
                                  </p>
                                )}
                                
                                {/* View Full Details Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowTimelineModal(true);
                                    fetchTimelineData();
                                  }}
                                  className="text-[9px] text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                                >
                                  <Eye className="h-3 w-3" />
                                  View in Timeline
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Helper text */}
                    <p className="text-[8px] text-slate-400 mt-1.5 text-center">Click milestone for details</p>
                  </div>
                )}
                
                {/* Deadlines List with Filters */}
                {upcomingDeadlines.length > 0 && (() => {
                  // Apply filter to deadlines
                  const filteredDeadlines = upcomingDeadlines.filter(deadline => {
                    const daysUntil = Math.ceil((new Date(deadline.target_date) - today) / (1000 * 60 * 60 * 24));
                    switch (deadlineFilter) {
                      case 'urgent': return daysUntil <= 3;
                      case 'week': return daysUntil <= 7;
                      case 'month': return daysUntil <= 30;
                      default: return true; // 'all'
                    }
                  });
                  
                  return (
                    <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      {/* Header with filters */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-orange-500 flex-shrink-0" />
                          <span className="text-[10px] font-medium text-orange-600 dark:text-orange-400">
                            Deadlines ({filteredDeadlines.length})
                          </span>
                        </div>
                      </div>
                      
                      {/* Filter buttons */}
                      <div className="flex gap-1 mb-2 flex-wrap">
                        {[
                          { key: 'all', label: 'All' },
                          { key: 'urgent', label: '≤3 Days', color: 'red' },
                          { key: 'week', label: 'This Week', color: 'orange' },
                          { key: 'month', label: '30 Days', color: 'slate' }
                        ].map(filter => (
                          <button
                            key={filter.key}
                            onClick={() => setDeadlineFilter(filter.key)}
                            className={`text-[8px] px-1.5 py-0.5 rounded-full transition-all ${
                              deadlineFilter === filter.key
                                ? filter.color === 'red' 
                                  ? 'bg-red-500 text-white'
                                  : filter.color === 'orange'
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-slate-600 text-white dark:bg-slate-500'
                                : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600'
                            }`}
                          >
                            {filter.label}
                          </button>
                        ))}
                      </div>
                      
                      {/* Deadlines list */}
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {filteredDeadlines.length > 0 ? (
                          filteredDeadlines.map((deadline, idx) => {
                            const daysUntil = Math.ceil((new Date(deadline.target_date) - today) / (1000 * 60 * 60 * 24));
                            const isUrgent = daysUntil <= 3;
                            const isNear = daysUntil <= 7;
                            
                            return (
                              <div 
                                key={deadline.id || idx}
                                className={`flex items-center justify-between py-1 px-1.5 rounded ${idx === 0 ? 'bg-orange-100 dark:bg-orange-800/30' : ''}`}
                              >
                                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                  <span className={`text-[10px] font-bold w-4 ${isUrgent ? 'text-red-600' : isNear ? 'text-orange-600' : 'text-slate-500'}`}>
                                    {idx + 1}.
                                  </span>
                                  <span className="text-[10px] text-slate-700 dark:text-slate-300 truncate">
                                    {deadline.name}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <span className={`text-[8px] px-1 py-0.5 rounded ${
                                    isUrgent ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' :
                                    isNear ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400' :
                                    'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                                  }`}>
                                    {daysUntil === 0 ? 'Today' : daysUntil === 1 ? '1d' : `${daysUntil}d`}
                                  </span>
                                  <span className="text-[9px] font-medium text-orange-700 dark:text-orange-300 w-10 text-right">
                                    {format(new Date(deadline.target_date), 'M/d')}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-[9px] text-slate-500 text-center py-2">No deadlines match this filter</p>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
          
          {/* Scope Metrics Section */}
          {isScope && scopeData.items?.length > 0 && (() => {
            const metrics = scopeData.metrics || {};
            const items = scopeData.items || [];
            
            // Format hours for display
            const formatHours = (hours) => {
              if (!hours) return '0h';
              if (hours >= 40) return `${Math.round(hours / 40)}w`;
              if (hours >= 8) return `${Math.round(hours / 8)}d`;
              return `${Math.round(hours)}h`;
            };
            
            return (
              <div className="mb-4 space-y-3">
                {/* Progress Bar */}
                <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-green-700 dark:text-green-300">Feature Progress</span>
                    <span className="text-sm font-bold text-green-600 dark:text-green-400">{metrics.progress_percent || 0}%</span>
                  </div>
                  <div className="h-2 bg-green-100 dark:bg-green-900/40 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${metrics.progress_percent || 0}%` }}
                    />
                  </div>
                </div>
                
                {/* Time to MVP */}
                {metrics.mvp_remaining_hours > 0 && (
                  <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-medium text-blue-700 dark:text-blue-300">Time to MVP</span>
                      <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                        {formatHours(metrics.mvp_remaining_hours)} remaining
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[9px] text-blue-600/80 dark:text-blue-400/80">
                      <span>Projected: {formatHours(metrics.mvp_projected_hours)}</span>
                      <span>•</span>
                      <span>Actual: {formatHours(metrics.mvp_actual_hours)}</span>
                    </div>
                  </div>
                )}
                
                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-1.5">
                  <div className="text-center p-1.5 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-base font-bold text-green-600 dark:text-green-400">{metrics.completed || 0}</p>
                    <p className="text-[9px] text-green-700 dark:text-green-300">Done</p>
                  </div>
                  <div className="text-center p-1.5 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <p className="text-base font-bold text-yellow-600 dark:text-yellow-400">{metrics.in_progress || 0}</p>
                    <p className="text-[9px] text-yellow-700 dark:text-yellow-300">Active</p>
                  </div>
                  <div className="text-center p-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    <p className="text-base font-bold text-slate-500 dark:text-slate-400">{metrics.pending || 0}</p>
                    <p className="text-[9px] text-slate-500 dark:text-slate-400">Pending</p>
                  </div>
                  <div className="text-center p-1.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                    <p className="text-base font-bold text-emerald-600 dark:text-emerald-400">{metrics.total || 0}</p>
                    <p className="text-[9px] text-emerald-700 dark:text-emerald-300">Total</p>
                  </div>
                </div>
                
                {/* Features List - Clickable */}
                <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <span className="text-[10px] font-medium text-slate-600 dark:text-slate-400 mb-2 block">Recent Features (click to edit)</span>
                  <div className="space-y-1 max-h-28 overflow-y-auto">
                    {items.slice(0, 6).map((item, idx) => (
                      <div 
                        key={item.id || idx} 
                        className="flex items-center justify-between py-1 px-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700/30 cursor-pointer"
                        onClick={() => {
                          setSelectedScopeItem(item);
                          setEditingScopeItem(item);
                        }}
                      >
                        <span className="text-[10px] text-slate-700 dark:text-slate-300 truncate flex-1">{item.name}</span>
                        <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${
                          item.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' :
                          item.status === 'in_progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' :
                          'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                        }`}>
                          {item.status?.replace('_', ' ') || 'pending'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
          
          {/* Implementation Plan Metrics Section */}
          {pillar.id === 'implementation' && implementationData.items?.length > 0 && (() => {
            const metrics = implementationData.metrics || {};
            const items = implementationData.items || [];
            
            return (
              <div className="mb-4 space-y-3">
                {/* Progress Bar */}
                <div className="p-3 bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Implementation Progress</span>
                    <span className="text-sm font-bold text-purple-600 dark:text-purple-400">{metrics.progress_percent || 0}%</span>
                  </div>
                  <div className="h-2 bg-purple-100 dark:bg-purple-900/40 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-violet-500 rounded-full transition-all duration-500"
                      style={{ width: `${metrics.progress_percent || 0}%` }}
                    />
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-1.5">
                  <div className="text-center p-1.5 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-base font-bold text-green-600 dark:text-green-400">{metrics.completed || 0}</p>
                    <p className="text-[9px] text-green-700 dark:text-green-300">Done</p>
                  </div>
                  <div className="text-center p-1.5 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <p className="text-base font-bold text-purple-600 dark:text-purple-400">{metrics.in_progress || 0}</p>
                    <p className="text-[9px] text-purple-700 dark:text-purple-300">Active</p>
                  </div>
                  <div className="text-center p-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    <p className="text-base font-bold text-slate-500 dark:text-slate-400">{metrics.pending || 0}</p>
                    <p className="text-[9px] text-slate-500 dark:text-slate-400">Pending</p>
                  </div>
                  <div className="text-center p-1.5 bg-violet-50 dark:bg-violet-900/20 rounded-lg">
                    <p className="text-base font-bold text-violet-600 dark:text-violet-400">{metrics.total || 0}</p>
                    <p className="text-[9px] text-violet-700 dark:text-violet-300">Total</p>
                  </div>
                </div>
                
                {/* Implementation Items List */}
                <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <span className="text-[10px] font-medium text-slate-600 dark:text-slate-400 mb-2 block">Implementation Items</span>
                  <div className="space-y-1 max-h-28 overflow-y-auto">
                    {items.slice(0, 6).map((item, idx) => (
                      <div key={item.id || idx} className="flex items-center justify-between py-1 px-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700/30">
                        <span className="text-[10px] text-slate-700 dark:text-slate-300 truncate flex-1">{item.name}</span>
                        <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${
                          item.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' :
                          item.status === 'in_progress' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400' :
                          'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                        }`}>
                          {item.status?.replace('_', ' ') || 'pending'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
          
          {/* Data & Security Metrics Section */}
          {pillar.id === 'data_security' && dataSecurityData.items?.length > 0 && (() => {
            const metrics = dataSecurityData.metrics || {};
            const items = dataSecurityData.items || [];
            
            return (
              <div className="mb-4 space-y-3">
                {/* Progress Bar */}
                <div className="p-3 bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-cyan-700 dark:text-cyan-300">Security Review Progress</span>
                    <span className="text-sm font-bold text-cyan-600 dark:text-cyan-400">{metrics.review_percent || 0}%</span>
                  </div>
                  <div className="h-2 bg-cyan-100 dark:bg-cyan-900/40 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full transition-all duration-500"
                      style={{ width: `${metrics.review_percent || 0}%` }}
                    />
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-1.5">
                  <div className="text-center p-1.5 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-base font-bold text-green-600 dark:text-green-400">{metrics.reviewed || 0}</p>
                    <p className="text-[9px] text-green-700 dark:text-green-300">Reviewed</p>
                  </div>
                  <div className="text-center p-1.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <p className="text-base font-bold text-amber-600 dark:text-amber-400">{metrics.pending_review || 0}</p>
                    <p className="text-[9px] text-amber-700 dark:text-amber-300">Pending</p>
                  </div>
                  <div className="text-center p-1.5 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                    <p className="text-base font-bold text-cyan-600 dark:text-cyan-400">{metrics.with_data_requirements || 0}</p>
                    <p className="text-[9px] text-cyan-700 dark:text-cyan-300">Data Req</p>
                  </div>
                  <div className="text-center p-1.5 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                    <p className="text-base font-bold text-teal-600 dark:text-teal-400">{metrics.total || 0}</p>
                    <p className="text-[9px] text-teal-700 dark:text-teal-300">Total</p>
                  </div>
                </div>
                
                {/* Security Items List */}
                <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <span className="text-[10px] font-medium text-slate-600 dark:text-slate-400 mb-2 block">Security Items</span>
                  <div className="space-y-1 max-h-28 overflow-y-auto">
                    {items.slice(0, 6).map((item, idx) => (
                      <div key={item.id || idx} className="flex items-center justify-between py-1 px-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700/30">
                        <span className="text-[10px] text-slate-700 dark:text-slate-300 truncate flex-1">{item.name}</span>
                        <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${
                          item.status === 'reviewed' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' :
                          'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                        }`}>
                          {item.status || 'pending'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
          
          {/* Cost Pillar Metrics - Show cost summary instead of "No updates" */}
          {isCost && (
            <div className="space-y-3">
              {/* Total Spent Banner */}
              <div className="p-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg text-white">
                <p className="text-xs opacity-80">Total Spent to Date</p>
                <p className="text-2xl font-bold">${(costData.totals?.one_time || 0).toLocaleString()}</p>
              </div>
              
              {/* Cost Breakdown */}
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <p className="text-lg font-bold text-green-600">${(costData.totals?.one_time || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-green-700 dark:text-green-400">One-Time</p>
                </div>
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <p className="text-lg font-bold text-blue-600">${(costData.totals?.monthly_expenses || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-blue-700 dark:text-blue-400">Monthly</p>
                </div>
              </div>
              
              {/* Monthly Burn Rate */}
              <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                <p className="text-xs text-amber-700 dark:text-amber-400">Monthly Burn Rate</p>
                <p className="text-xl font-bold text-amber-600">${(costData.totals?.monthly_burn || 0).toLocaleString()}/mo</p>
              </div>
              
              {/* Recent Expenses (last 2) */}
              {costData.expenses?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] text-muted-foreground font-medium">Recent Expenses</p>
                  {costData.expenses.slice(0, 2).map(exp => (
                    <div key={exp.id} className="flex items-center justify-between p-1.5 bg-slate-50 dark:bg-slate-800 rounded text-xs">
                      <span className="truncate max-w-[140px]">{exp.name}</span>
                      <span className="font-bold">${exp.amount?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Generic Update Section - Skip for Cost pillar since we show metrics above */}
          {!isCost && pillar.latest_update ? (
            <div className="space-y-2">
              {canEdit && (
                <div className="flex justify-end -mt-1 -mr-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(pillar)}
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                </div>
              )}
              <div>
                <Label className="text-xs text-muted-foreground">What was worked on</Label>
                <p className="text-sm dark:text-white line-clamp-2">{pillar.latest_update.what_worked_on}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">What&apos;s next</Label>
                <p className="text-sm dark:text-white line-clamp-2">{pillar.latest_update.whats_next}</p>
              </div>
              {pillar.latest_update.blockers && (
                <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <Label className="text-xs text-red-600 dark:text-red-400">Blockers</Label>
                  <p className="text-sm text-red-700 dark:text-red-300 line-clamp-2">
                    {pillar.latest_update.blockers}
                  </p>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Updated {formatDistanceToNow(new Date(pillar.latest_update.created_at), { addSuffix: true })}
              </p>
            </div>
          ) : !isCost && !isTasks && (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">No updates yet</p>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    setSelectedPillar(pillar);
                    setEditingUpdate(null);
                    setUpdateForm({ what_worked_on: '', whats_next: '', blockers: '', links: [], todos: [] });
                    setShowUpdateDialog(true);
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Update
                </Button>
              )}
            </div>
          )}
          
          {/* Tasks Pillar Content */}
          {isTasks && (
            <div className="space-y-2">
              {/* Task Stats Banner */}
              <div className="p-2 bg-gradient-to-r from-purple-500 to-violet-500 rounded-lg text-white text-center">
                <p className="text-2xl font-bold">{tasksData.stats?.total || 0}</p>
                <p className="text-xs opacity-80">Active Tasks</p>
              </div>
              
              {/* Task Status Breakdown - Compact */}
              <div className="grid grid-cols-3 gap-1 text-center text-xs">
                <div className="p-1.5 bg-green-50 dark:bg-green-900/20 rounded">
                  <p className="font-bold text-green-600">{tasksData.stats?.completed || 0}</p>
                  <p className="text-[9px] text-green-700">Done</p>
                </div>
                <div className="p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <p className="font-bold text-blue-600">{tasksData.stats?.in_progress || 0}</p>
                  <p className="text-[9px] text-blue-700">Active</p>
                </div>
                <div className="p-1.5 bg-amber-50 dark:bg-amber-900/20 rounded">
                  <p className="font-bold text-amber-600">{tasksData.stats?.pending || 0}</p>
                  <p className="text-[9px] text-amber-700">Pending</p>
                </div>
              </div>
              
              {/* Recent Tasks - Compact List */}
              {tasksData.tasks?.length > 0 ? (
                <div className="space-y-1 text-xs">
                  {tasksData.tasks.slice(0, 3).map(task => (
                    <div key={task.id} className="flex items-center justify-between p-1 bg-slate-50 dark:bg-slate-800 rounded">
                      <div className="flex items-center gap-1 truncate">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          task.status === 'completed' ? 'bg-green-500' :
                          task.status === 'in_progress' ? 'bg-blue-500' : 'bg-amber-500'
                        }`} />
                        <span className={`truncate ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                          {task.title}
                        </span>
                      </div>
                      <span className="text-[9px] text-muted-foreground flex-shrink-0 ml-1">
                        {task.assigned_agent_name || task.assignee || ''}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-center text-muted-foreground">No tasks yet</p>
              )}
            </div>
          )}
          
          {/* Comments Section */}
          {hasComments && (
            <div className="mt-3 pt-3 border-t dark:border-slate-700">
              <ScrollArea className="h-32">
                <div className="space-y-2">
                  {(comments[pillar.id] || []).map(comment => (
                    <div key={comment.id} className="p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs">
                      <div className="flex items-center gap-2 mb-1">
                        <Avatar className="h-5 w-5">
                          <AvatarFallback className="text-[10px]">
                            {comment.user_name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium dark:text-white">{comment.user_name}</span>
                      </div>
                      <p className="dark:text-slate-300">{comment.content}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Add comment..."
                  value={newExecComment}
                  onChange={(e) => setNewExecComment(e.target.value)}
                  className="h-8 text-xs"
                  onKeyPress={(e) => e.key === 'Enter' && addComment(pillar.id)}
                />
                <Button size="sm" className="h-8" onClick={() => addComment(pillar.id)}>
                  <Send className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Default Hub View
  return (
    <Layout>
    <div className="p-6 max-w-full mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold dark:text-white">5 Pillars Execution Hub</h1>
          <p className="text-muted-foreground">
            Weekly cadence framework for business operations
            {isExecutiveView && <span className="ml-2 text-xs text-blue-500">(Executive View)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Edit Mode Toggle - Only for authorized users, not CEO/President */}
          {canEditLayout && (
            <Button
              variant={isEditMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setIsEditMode(!isEditMode);
                if (isEditMode) {
                  toast.success('Layout saved');
                }
              }}
            >
              {isEditMode ? (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Lock Layout
                </>
              ) : (
                <>
                  <Unlock className="h-4 w-4 mr-2" />
                  Edit Layout
                </>
              )}
            </Button>
          )}
          {isEditMode && canEditLayout && (
            <Button variant="outline" size="sm" onClick={resetLayout}>
              Reset
            </Button>
          )}
          {canComment && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowExecComments(!showExecComments)}
              className={showExecComments ? 'bg-blue-100 dark:bg-blue-900/30' : ''}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Comments
              {execComments.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                  {execComments.length}
                </span>
              )}
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/dev-calendar'} 
            className="gap-1"
          >
            <Calendar className="h-4 w-4" />
            Dev Calendar
          </Button>
          <Button variant="outline" onClick={() => setShowMeetingPrep(true)} className="gap-1">
            <Pencil className="h-4 w-4" />
            Prep Meeting
          </Button>
          <Button onClick={() => setShowPrepPicker(true)} className="bg-green-600 hover:bg-green-700">
            <Play className="h-4 w-4 mr-2" />
            Start Meeting
          </Button>
        </div>
      </div>

      {/* Comments Panel */}
      {showExecComments && canComment && (
        <div className="mb-4 p-4 bg-white dark:bg-slate-900 rounded-lg border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-500" />
              Executive Comments
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setShowExecComments(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Add Comment */}
          <div className="flex gap-2 mb-4">
            <textarea
              value={newExecComment}
              onChange={(e) => setNewExecComment(e.target.value)}
              placeholder="Add a comment or feedback..."
              className="flex-1 p-2 border rounded-md text-sm resize-none h-16 bg-background"
            />
            <Button 
              onClick={() => addExecComment('general')} 
              disabled={!newExecComment.trim()}
              className="self-end"
            >
              Post
            </Button>
          </div>
          
          {/* Comments List */}
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {execCommentsLoading ? (
              <div className="text-center py-4 text-muted-foreground">Loading comments...</div>
            ) : execComments.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                No comments yet. Start the conversation!
              </div>
            ) : (
              execComments.map(comment => (
                <div key={comment.id} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{comment.author_name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        comment.author_role === 'ceo' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                        comment.author_role === 'president' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      }`}>
                        {comment.author_role === 'ceo' ? 'CEO' : 
                         comment.author_role === 'president' ? 'President' : 'Admin'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {(userRole === 'super_admin' || userRole === 'admin') && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                        onClick={() => deleteExecComment(comment.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-300">{comment.content}</p>
                  
                  {/* Replies */}
                  {comment.replies?.length > 0 && (
                    <div className="mt-2 ml-4 space-y-2 border-l-2 border-slate-200 dark:border-slate-700 pl-3">
                      {comment.replies.map(reply => (
                        <div key={reply.id} className="p-2 bg-slate-100 dark:bg-slate-700 rounded">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-xs">{reply.author_name}</span>
                            <span className={`text-[10px] px-1 py-0.5 rounded ${
                              reply.author_role === 'ceo' ? 'bg-amber-100 text-amber-700' :
                              reply.author_role === 'president' ? 'bg-purple-100 text-purple-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {reply.author_role === 'ceo' ? 'CEO' : 
                               reply.author_role === 'president' ? 'President' : 'Admin'}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-400">{reply.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Reply Button */}
                  {replyingTo === comment.id ? (
                    <div className="mt-2 flex gap-2">
                      <input
                        type="text"
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder="Write a reply..."
                        className="flex-1 p-1.5 text-xs border rounded bg-background"
                        onKeyDown={(e) => e.key === 'Enter' && addExecReply(comment.id, comment.pillar_type)}
                      />
                      <Button size="sm" className="h-7 text-xs" onClick={() => addExecReply(comment.id, comment.pillar_type)}>
                        Reply
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setReplyingTo(null)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="mt-1 h-6 text-xs"
                      onClick={() => setReplyingTo(comment.id)}
                    >
                      Reply
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Dashboard Grid */}
      <div id="pillars-grid-container" className="w-full">
        <GridLayout
          className="layout"
          layout={gridLayout}
          cols={12}
          rowHeight={60}
          width={containerWidth}
          isDraggable={isEditMode && !isMobile}
          isResizable={isEditMode && !isMobile}
          onLayoutChange={onLayoutChange}
          draggableHandle=".drag-handle"
          margin={[16, 16]}
        >
          {/* Pillar Cards */}
          {pillars.map(pillar => (
            <div key={pillar.id} className={isEditMode ? 'drag-handle' : ''}>
              {renderPillarCard(pillar)}
            </div>
          ))}
          
          {/* Weekly Recap Widget */}
          <div key="weekly_recap">
            <Card className={`h-full ${isEditMode ? 'cursor-move' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  {isEditMode && <GripVertical className="h-4 w-4 text-muted-foreground drag-handle cursor-move" />}
                  <CardTitle className="text-base">Weekly Recap</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-0 overflow-auto">
                <WeeklyRecapSection compact />
              </CardContent>
            </Card>
          </div>
          
          {/* Recent Meetings Widget */}
          <div key="recent_meetings">
            <Card className={`h-full ${isEditMode ? 'cursor-move' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  {isEditMode && <GripVertical className="h-4 w-4 text-muted-foreground drag-handle cursor-move" />}
                  <CardTitle className="text-base">Recent Meetings</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-0 overflow-auto">
                {meetings.length > 0 ? (
                  <div className="space-y-2">
                    {meetings.slice(0, 5).map(meeting => (
                      <div
                        key={meeting.id}
                        className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                        onClick={() => navigate(`/five-pillars?meeting=${meeting.id}`)}
                      >
                        <div>
                          <p className="text-sm font-medium dark:text-white">{meeting.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(meeting.started_at), 'PP')}
                          </p>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {meeting.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No meetings yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </GridLayout>
      </div>

      {/* Edit Mode Indicator */}
      {isEditMode && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg flex items-center gap-2 z-50">
          <Maximize2 className="h-4 w-4" />
          <span className="text-sm font-medium">Drag to move • Drag corners to resize</span>
        </div>
      )}

      {/* Create/Edit Update Dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={(open) => {
        setShowUpdateDialog(open);
        if (!open) {
          setEditingUpdate(null);
          setUpdateForm({ what_worked_on: '', whats_next: '', blockers: '', links: [], todos: [] });
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingUpdate ? 'Edit' : 'Add'} Update - {selectedPillar?.name}</DialogTitle>
            <DialogDescription>
              {editingUpdate ? 'Edit the pillar update content' : 'Create a daily update for this pillar'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>What was worked on *</Label>
              <Textarea
                placeholder="Describe what was accomplished..."
                value={updateForm.what_worked_on}
                onChange={(e) => setUpdateForm(prev => ({ ...prev, what_worked_on: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>What&apos;s next *</Label>
              <Textarea
                placeholder="Describe upcoming work..."
                value={updateForm.whats_next}
                onChange={(e) => setUpdateForm(prev => ({ ...prev, whats_next: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Blockers (if any)</Label>
              <Textarea
                placeholder="Any blockers or issues..."
                value={updateForm.blockers}
                onChange={(e) => setUpdateForm(prev => ({ ...prev, blockers: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdateDialog(false)}>Cancel</Button>
            <Button
              onClick={createUpdate}
              disabled={!updateForm.what_worked_on.trim() || !updateForm.whats_next.trim()}
            >
              {editingUpdate ? 'Save Changes' : 'Create Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prep Picker - Select which prep to use when starting a meeting */}
      <Dialog open={showPrepPicker} onOpenChange={setShowPrepPicker}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Start Meeting</DialogTitle>
            <DialogDescription>Choose a meeting prep to load, or start fresh.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {savedPreps.length > 0 ? (
              savedPreps.map((prep) => (
                <div
                  key={prep.id}
                  className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                  onClick={() => {
                    setShowPrepPicker(false);
                    startMeetingWithPrep(prep.id);
                  }}
                >
                  <div>
                    <p className="text-sm font-medium dark:text-white">{prep.name || 'Untitled Prep'}</p>
                    <p className="text-xs text-muted-foreground">
                      {(prep.agenda || []).length} agenda items • Updated {prep.updated_at ? new Date(prep.updated_at).toLocaleDateString() : ''}
                    </p>
                  </div>
                  <Play className="h-4 w-4 text-green-600" />
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No saved preps yet. Use "Prep Meeting" to create one.</p>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowPrepPicker(false)}>Cancel</Button>
            <Button onClick={() => { setShowPrepPicker(false); startMeetingWithPrep(null); }} className="bg-green-600 hover:bg-green-700 gap-1">
              <Play className="h-4 w-4" /> Start Without Prep
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meeting Prep Dialog */}
      <Dialog open={showMeetingPrep} onOpenChange={setShowMeetingPrep}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />
              Prep Next Meeting
            </DialogTitle>
            <div className="flex items-center justify-between">
              <DialogDescription>
                Set up the agenda and talking points. Saves and auto-loads when you start the meeting.
              </DialogDescription>
              <Button variant="outline" size="sm" className="shrink-0 gap-1" onClick={() => {
                setMeetingPrep({ agenda: [], completed: [], issues: [], focus_this_week: [], discussion_topics: [], presenter_notes: '', name: '' });
              }}>
                <Plus className="h-3 w-3" /> New Prep
              </Button>
            </div>
          </DialogHeader>
          
          <div className="space-y-6 py-2">
            {/* Prep Name */}
            <div className="space-y-2">
              <Label className="font-semibold">Prep Name</Label>
              <Input 
                value={meetingPrep.name || ''} 
                onChange={(e) => setMeetingPrep(p => ({ ...p, name: e.target.value }))} 
                placeholder="e.g., Week of Feb 24 Meeting Prep"
                className="text-sm"
              />
            </div>

            {/* Agenda */}
            <div className="space-y-2">
              <Label className="font-semibold">📋 Meeting Agenda</Label>
              {(meetingPrep.agenda || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-sm font-bold text-primary w-6">{idx + 1}.</span>
                  <Input value={item} onChange={(e) => {
                    const u = [...meetingPrep.agenda]; u[idx] = e.target.value;
                    setMeetingPrep(p => ({ ...p, agenda: u }));
                  }} className="flex-1 text-sm" placeholder="Agenda item..." />
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                    setMeetingPrep(p => ({ ...p, agenda: p.agenda.filter((_, i) => i !== idx) }));
                  }}><X className="h-3 w-3" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="gap-1 w-full border-dashed" onClick={() => {
                setMeetingPrep(p => ({ ...p, agenda: [...(p.agenda || []), ''] }));
              }}><Plus className="h-3 w-3" /> Add Agenda Item</Button>
            </div>

            {/* Completed This Week */}
            <div className="space-y-2">
              <Label className="font-semibold text-green-600">✅ Completed This Week</Label>
              {(meetingPrep.completed || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input value={item} onChange={(e) => {
                    const u = [...meetingPrep.completed]; u[idx] = e.target.value;
                    setMeetingPrep(p => ({ ...p, completed: u }));
                  }} className="flex-1 text-sm" placeholder="What was completed..." />
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                    setMeetingPrep(p => ({ ...p, completed: p.completed.filter((_, i) => i !== idx) }));
                  }}><X className="h-3 w-3" /></Button>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="gap-1 text-green-600" onClick={() => {
                setMeetingPrep(p => ({ ...p, completed: [...(p.completed || []), ''] }));
              }}><Plus className="h-3 w-3" /> Add</Button>
            </div>

            {/* Issues */}
            <div className="space-y-2">
              <Label className="font-semibold text-red-600">⚠️ Issues to Discuss</Label>
              {(meetingPrep.issues || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input value={item} onChange={(e) => {
                    const u = [...meetingPrep.issues]; u[idx] = e.target.value;
                    setMeetingPrep(p => ({ ...p, issues: u }));
                  }} className="flex-1 text-sm" placeholder="Issue..." />
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                    setMeetingPrep(p => ({ ...p, issues: p.issues.filter((_, i) => i !== idx) }));
                  }}><X className="h-3 w-3" /></Button>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="gap-1 text-red-600" onClick={() => {
                setMeetingPrep(p => ({ ...p, issues: [...(p.issues || []), ''] }));
              }}><Plus className="h-3 w-3" /> Add</Button>
            </div>

            {/* This Week's Focus */}
            <div className="space-y-2">
              <Label className="font-semibold text-purple-600">🎯 This Week's Focus</Label>
              {(meetingPrep.focus_this_week || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input value={item} onChange={(e) => {
                    const u = [...meetingPrep.focus_this_week]; u[idx] = e.target.value;
                    setMeetingPrep(p => ({ ...p, focus_this_week: u }));
                  }} className="flex-1 text-sm" placeholder="Focus area..." />
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                    setMeetingPrep(p => ({ ...p, focus_this_week: p.focus_this_week.filter((_, i) => i !== idx) }));
                  }}><X className="h-3 w-3" /></Button>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="gap-1 text-purple-600" onClick={() => {
                setMeetingPrep(p => ({ ...p, focus_this_week: [...(p.focus_this_week || []), ''] }));
              }}><Plus className="h-3 w-3" /> Add</Button>
            </div>

            {/* Discussion Topics */}
            <div className="space-y-2">
              <Label className="font-semibold text-blue-600">💬 Discussion Topics</Label>
              {(meetingPrep.discussion_topics || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input value={item} onChange={(e) => {
                    const u = [...meetingPrep.discussion_topics]; u[idx] = e.target.value;
                    setMeetingPrep(p => ({ ...p, discussion_topics: u }));
                  }} className="flex-1 text-sm" placeholder="Topic to discuss..." />
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                    setMeetingPrep(p => ({ ...p, discussion_topics: p.discussion_topics.filter((_, i) => i !== idx) }));
                  }}><X className="h-3 w-3" /></Button>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="gap-1 text-blue-600" onClick={() => {
                setMeetingPrep(p => ({ ...p, discussion_topics: [...(p.discussion_topics || []), ''] }));
              }}><Plus className="h-3 w-3" /> Add</Button>
            </div>

            {/* Presenter Notes */}
            <div className="space-y-2">
              <Label className="font-semibold">📝 Presenter Notes (private)</Label>
              <Textarea
                value={meetingPrep.presenter_notes || ''}
                onChange={(e) => setMeetingPrep(p => ({ ...p, presenter_notes: e.target.value }))}
                placeholder="Personal notes for the meeting..."
                className="min-h-[80px] text-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMeetingPrep(false)}>Cancel</Button>
            <Button onClick={saveMeetingPrep} className="gap-1">
              <Save className="h-4 w-4" /> Save Meeting Prep
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Meeting Dialog removed - goes directly to welcome screen */}

            {/* ==================== SCOPE MODAL ==================== */}
      <EnhancedScopeModal
        open={showScopeModal}
        onOpenChange={setShowScopeModal}
        scopeData={scopeData}
        scopeLoading={scopeLoading}
        canEdit={canEdit}
        selectedItem={selectedScopeItem}
        pendingProgress={pendingScopeProgress}
        onSelectItem={(item) => {
          setSelectedScopeItem(item);
          setPendingScopeProgress(null);
        }}
        onSetPendingProgress={setPendingScopeProgress}
        onUpdateItem={(id, updates) => {
          updateScopeItem(id, updates);
          if (selectedScopeItem?.id === id) {
            setSelectedScopeItem({ ...selectedScopeItem, ...updates });
          }
        }}
        onDeleteItem={(id) => {
          deleteScopeItem(id);
          setSelectedScopeItem(null);
        }}
        onAddMvpItem={() => {
          setShowScopeModal(false); // Close scope modal first
          setTimeout(() => {
            setScopeForm({ 
              name: '', description: '', category: 'mvp', status: 'pending', hub: 'none',
              acceptance_criteria: '', technical_requirements: '', related_files: [],
              testing_instructions: '', test_scenarios: '', success_indicators: '',
              dependencies_notes: '', reference_links: [],
              api_keys_needed: '', env_variables: '', third_party_credentials: '',
              setup_instructions: '', config_notes: '',
              roles_permissions: '', data_access: '', auth_requirements: '',
              authorization_rules: '', data_validation: '', pii_handling: '',
              rate_limiting: '', audit_logging: '',
              wireframe_links: '', ui_components: '', responsive_requirements: '',
              accessibility_level: '', user_flow: '', error_states_ui: '',
              loading_states: '', empty_states: '',
              error_scenarios: '', error_messages: '', fallback_behaviors: '',
              retry_logic: '', timeout_handling: '', logging_requirements: '',
              graceful_degradation: ''
            });
            setScopeFormTab('definition');
            setShowAddScopeItem(true);
          }, 100);
        }}
        onAddFutureItem={() => {
          setShowScopeModal(false); // Close scope modal first
          setTimeout(() => {
            setScopeForm({ 
              name: '', description: '', category: 'future', status: 'pending', hub: 'none',
              acceptance_criteria: '', technical_requirements: '', related_files: [],
              testing_instructions: '', test_scenarios: '', success_indicators: '',
              dependencies_notes: '', reference_links: [],
              api_keys_needed: '', env_variables: '', third_party_credentials: '',
              setup_instructions: '', config_notes: '',
              roles_permissions: '', data_access: '', auth_requirements: '',
              authorization_rules: '', data_validation: '', pii_handling: '',
              rate_limiting: '', audit_logging: '',
              wireframe_links: '', ui_components: '', responsive_requirements: '',
              accessibility_level: '', user_flow: '', error_states_ui: '',
              loading_states: '', empty_states: '',
              error_scenarios: '', error_messages: '', fallback_behaviors: '',
              retry_logic: '', timeout_handling: '', logging_requirements: '',
              graceful_degradation: ''
            });
            setScopeFormTab('definition');
            setShowAddScopeItem(true);
          }, 100);
        }}
      />

{/* Add Scope Item Dialog - Comprehensive Feature Specification */}
      <Dialog open={showAddScopeItem} onOpenChange={setShowAddScopeItem}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden p-0 z-[60]" hideCloseButton>
          <div className="flex flex-col h-full max-h-[90vh]">
            {/* Header */}
            <div className="p-4 border-b bg-muted/30">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">
                    Add {scopeForm.category === 'mvp' ? 'MVP Item' : 'Future Feature'}
                  </h2>
                  <p className="text-sm text-muted-foreground">Complete feature specification for AI agents</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={openAIPromptDialog}
                    disabled={generatingSpec || !scopeForm.name.trim()}
                    className="gap-1 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/30 hover:border-purple-500/50"
                  >
                    {generatingSpec ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 text-purple-500" />
                        AI Generate
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowAddScopeItem(false)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Content with Tabs */}
            <div className="flex-1 overflow-hidden">
              <Tabs value={scopeFormTab} onValueChange={setScopeFormTab} className="h-full flex flex-col">
                <div className="px-4 pt-2 border-b">
                  <TabsList className="grid w-full grid-cols-7">
                    <TabsTrigger value="definition" className="flex items-center gap-1 text-xs">
                      <FileText className="h-3 w-3" />
                      Definition
                    </TabsTrigger>
                    <TabsTrigger value="technical" className="flex items-center gap-1 text-xs">
                      <FileCode className="h-3 w-3" />
                      Technical
                    </TabsTrigger>
                    <TabsTrigger value="testing" className="flex items-center gap-1 text-xs">
                      <TestTube className="h-3 w-3" />
                      Testing
                    </TabsTrigger>
                    <TabsTrigger value="setup" className="flex items-center gap-1 text-xs">
                      <Key className="h-3 w-3" />
                      Setup
                    </TabsTrigger>
                    <TabsTrigger value="security" className="flex items-center gap-1 text-xs">
                      <ShieldCheck className="h-3 w-3" />
                      Security
                    </TabsTrigger>
                    <TabsTrigger value="uiux" className="flex items-center gap-1 text-xs">
                      <LayoutGrid className="h-3 w-3" />
                      UI/UX
                    </TabsTrigger>
                    <TabsTrigger value="errors" className="flex items-center gap-1 text-xs">
                      <Bug className="h-3 w-3" />
                      Errors
                    </TabsTrigger>
                  </TabsList>
                </div>

                <ScrollArea className="flex-1 p-4">
                  {/* Definition Tab */}
                  <TabsContent value="definition" className="mt-0 space-y-4">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Name *</Label>
                        <Input
                          value={scopeForm.name}
                          onChange={(e) => setScopeForm({ ...scopeForm, name: e.target.value })}
                          placeholder="Feature or deliverable name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Hub</Label>
                        <Select
                          value={scopeForm.hub}
                          onValueChange={(value) => setScopeForm({ ...scopeForm, hub: value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select hub" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="Sales">Sales</SelectItem>
                            <SelectItem value="Operations">Operations</SelectItem>
                            <SelectItem value="Marketing">Marketing</SelectItem>
                            <SelectItem value="Finance">Finance</SelectItem>
                            <SelectItem value="Platform">Platform</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {scopeForm.category === 'mvp' && (
                        <div>
                          <Label>Status</Label>
                          <Select
                            value={scopeForm.status}
                            onValueChange={(value) => setScopeForm({ ...scopeForm, status: value })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label>Description</Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.description}
                          onChange={(e) => setScopeForm({ ...scopeForm, description: e.target.value })}
                          placeholder="What needs to be built? Describe the feature in detail..."
                          className="mt-1 min-h-[100px]"
                          rows={4}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Acceptance Criteria */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <ListChecks className="h-4 w-4 text-green-500" />
                          Acceptance Criteria
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.acceptance_criteria}
                          onChange={(e) => setScopeForm({ ...scopeForm, acceptance_criteria: e.target.value })}
                          placeholder="- [ ] User can...\n- [ ] System should...\n- [ ] Data must..."
                          className="mt-1 min-h-[80px] font-mono text-sm"
                          rows={4}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Checklist of conditions that must be met for completion</p>
                      </CollapsibleContent>
                    </Collapsible>
                  </TabsContent>

                  {/* Technical Tab */}
                  <TabsContent value="technical" className="mt-0 space-y-4">
                    {/* Technical Requirements */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <FileCode className="h-4 w-4 text-blue-500" />
                          Technical Requirements
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.technical_requirements}
                          onChange={(e) => setScopeForm({ ...scopeForm, technical_requirements: e.target.value })}
                          placeholder="Backend: Create API endpoint at /api/...\nFrontend: Add component to...\nDatabase: Create collection for..."
                          className="mt-1 min-h-[120px] font-mono text-sm"
                          rows={6}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Specific implementation details - endpoints, components, data structures</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Related Files */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label>Related Files / Components</Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={(scopeForm.related_files || []).join('\n')}
                          onChange={(e) => setScopeForm({ ...scopeForm, related_files: e.target.value.split('\n').filter(f => f.trim()) })}
                          placeholder="/app/frontend/src/pages/...\n/app/backend/routes/...\n/app/backend/models/..."
                          className="mt-1 font-mono text-sm"
                          rows={4}
                        />
                        <p className="text-xs text-muted-foreground mt-1">One file path per line - files the agent should reference or modify</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Dependencies */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-orange-500" />
                          Dependencies / Blockers
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.dependencies_notes}
                          onChange={(e) => setScopeForm({ ...scopeForm, dependencies_notes: e.target.value })}
                          placeholder="- Requires API from Feature X\n- Depends on database schema update\n- Blocked by: ..."
                          className="mt-1"
                          rows={3}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Reference Links */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <ExternalLink className="h-4 w-4 text-blue-500" />
                          Reference Links
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={(scopeForm.reference_links || []).join('\n')}
                          onChange={(e) => setScopeForm({ ...scopeForm, reference_links: e.target.value.split('\n').filter(l => l.trim()) })}
                          placeholder="https://figma.com/...\nhttps://docs.example.com/..."
                          className="mt-1 font-mono text-sm"
                          rows={3}
                        />
                      </CollapsibleContent>
                    </Collapsible>
                  </TabsContent>

                  {/* Testing Tab */}
                  <TabsContent value="testing" className="mt-0 space-y-4">
                    {/* Testing Instructions */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <TestTube className="h-4 w-4 text-purple-500" />
                          Testing Instructions
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.testing_instructions}
                          onChange={(e) => setScopeForm({ ...scopeForm, testing_instructions: e.target.value })}
                          placeholder="1. Navigate to /page\n2. Click on 'Button'\n3. Enter data: ...\n4. Verify that..."
                          className="mt-1 min-h-[120px] font-mono text-sm"
                          rows={6}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Step-by-step instructions for testing this feature</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Test Scenarios */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label>Test Scenarios / Edge Cases</Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.test_scenarios}
                          onChange={(e) => setScopeForm({ ...scopeForm, test_scenarios: e.target.value })}
                          placeholder="- Empty input validation\n- Large data handling\n- Network error recovery\n- Permission denied scenario"
                          className="mt-1 font-mono text-sm"
                          rows={4}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Success Indicators */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          Success Indicators
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.success_indicators}
                          onChange={(e) => setScopeForm({ ...scopeForm, success_indicators: e.target.value })}
                          placeholder="- Toast message shows 'Success'\n- Data appears in table\n- API returns 200 status"
                          className="mt-1"
                          rows={3}
                        />
                        <p className="text-xs text-muted-foreground mt-1">What does "working correctly" look like?</p>
                      </CollapsibleContent>
                    </Collapsible>
                  </TabsContent>

                  {/* Setup Tab */}
                  <TabsContent value="setup" className="mt-0 space-y-4">
                    {/* API Keys Needed */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <Key className="h-4 w-4 text-yellow-500" />
                          API Keys Required
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.api_keys_needed}
                          onChange={(e) => setScopeForm({ ...scopeForm, api_keys_needed: e.target.value })}
                          placeholder="- Stripe API Key (for payments)\n- SendGrid API Key (for emails)\n- Twilio SID and Auth Token (for SMS)"
                          className="mt-1 font-mono text-sm"
                          rows={4}
                        />
                        <p className="text-xs text-muted-foreground mt-1">List all external API keys needed for this feature</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Environment Variables */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <Settings className="h-4 w-4 text-blue-500" />
                          Environment Variables
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.env_variables}
                          onChange={(e) => setScopeForm({ ...scopeForm, env_variables: e.target.value })}
                          placeholder="STRIPE_SECRET_KEY=sk_test_...\nSENDGRID_API_KEY=SG...\nTWILIO_ACCOUNT_SID=AC..."
                          className="mt-1 font-mono text-sm"
                          rows={4}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Backend .env variables to add</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Third Party Credentials */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <Lock className="h-4 w-4 text-red-500" />
                          Third Party Credentials
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.third_party_credentials}
                          onChange={(e) => setScopeForm({ ...scopeForm, third_party_credentials: e.target.value })}
                          placeholder="- Builder Prime: Login credentials or API token\n- Google Business: Account access for reviews\n- Slack: Webhook URL for notifications"
                          className="mt-1"
                          rows={3}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Other credentials, accounts, or tokens required</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Setup Instructions */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <ClipboardList className="h-4 w-4 text-purple-500" />
                          Setup Instructions
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.setup_instructions}
                          onChange={(e) => setScopeForm({ ...scopeForm, setup_instructions: e.target.value })}
                          placeholder="1. Create Stripe account at stripe.com\n2. Get API keys from Dashboard > Developers\n3. Add keys to backend/.env\n4. Enable test mode for development"
                          className="mt-1 font-mono text-sm"
                          rows={5}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Step-by-step setup guide before development can begin</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Config Notes */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <Label>Additional Configuration Notes</Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.config_notes}
                          onChange={(e) => setScopeForm({ ...scopeForm, config_notes: e.target.value })}
                          placeholder="Any additional configuration, webhooks to set up, DNS records, etc."
                          className="mt-1"
                          rows={3}
                        />
                      </CollapsibleContent>
                    </Collapsible>
                  </TabsContent>

                  {/* Security & Data Tab */}
                  <TabsContent value="security" className="mt-0 space-y-4">
                    {/* Roles & Permissions */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                          <ShieldCheck className="h-3 w-3 text-purple-500" />
                          Roles & Permissions
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.roles_permissions}
                          onChange={(e) => setScopeForm({ ...scopeForm, roles_permissions: e.target.value })}
                          placeholder="Which user roles can access this feature?\ne.g., admin, super_admin, sales_rep\n\n- Admins: full CRUD access\n- Users: read-only access\n- Guests: no access"
                          className="mt-1"
                          rows={4}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Data Access */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                          <FileText className="h-3 w-3 text-blue-500" />
                          Data Access Requirements
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.data_access}
                          onChange={(e) => setScopeForm({ ...scopeForm, data_access: e.target.value })}
                          placeholder="What data models/collections are read/written?\ne.g.,\n- users: read\n- orders: read, write\n- payments: write only"
                          className="mt-1 font-mono text-sm"
                          rows={4}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Auth & Authorization */}
                    <div className="grid grid-cols-2 gap-4">
                      <Collapsible defaultOpen>
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-1">
                            <Key className="h-3 w-3 text-yellow-500" />
                            Authentication
                          </Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.auth_requirements}
                            onChange={(e) => setScopeForm({ ...scopeForm, auth_requirements: e.target.value })}
                            placeholder="e.g., JWT required, session-based, API key, public endpoint"
                            className="mt-1"
                            rows={3}
                          />
                        </CollapsibleContent>
                      </Collapsible>

                      <Collapsible defaultOpen>
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-1">
                            <Lock className="h-3 w-3 text-red-500" />
                            Authorization Rules
                          </Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.authorization_rules}
                            onChange={(e) => setScopeForm({ ...scopeForm, authorization_rules: e.target.value })}
                            placeholder="e.g., User must own resource, Admin bypass, Role-based checks"
                            className="mt-1"
                            rows={3}
                          />
                        </CollapsibleContent>
                      </Collapsible>
                    </div>

                    {/* Data Validation */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                          <ListChecks className="h-3 w-3 text-green-500" />
                          Data Validation Rules
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.data_validation}
                          onChange={(e) => setScopeForm({ ...scopeForm, data_validation: e.target.value })}
                          placeholder="Input validation requirements:\n- Email: valid format\n- Phone: regex pattern\n- Text fields: max 500 chars\n- Required fields: name, email"
                          className="mt-1"
                          rows={4}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* PII Handling */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3 text-orange-500" />
                          Privacy / PII Handling
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.pii_handling}
                          onChange={(e) => setScopeForm({ ...scopeForm, pii_handling: e.target.value })}
                          placeholder="Sensitive data considerations:\n- Mask SSN in UI\n- Encrypt at rest\n- GDPR compliant deletion\n- No PII in logs"
                          className="mt-1"
                          rows={3}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Rate Limiting & Audit */}
                    <div className="grid grid-cols-2 gap-4">
                      <Collapsible>
                        <div className="flex items-center justify-between">
                          <Label>Rate Limiting</Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.rate_limiting}
                            onChange={(e) => setScopeForm({ ...scopeForm, rate_limiting: e.target.value })}
                            placeholder="e.g., 100 req/min per user, 1000 req/min total"
                            className="mt-1"
                            rows={2}
                          />
                        </CollapsibleContent>
                      </Collapsible>

                      <Collapsible>
                        <div className="flex items-center justify-between">
                          <Label>Audit Logging</Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.audit_logging}
                            onChange={(e) => setScopeForm({ ...scopeForm, audit_logging: e.target.value })}
                            placeholder="e.g., Log CRUD operations, user ID, timestamp, IP address"
                            className="mt-1"
                            rows={2}
                          />
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  </TabsContent>

                  {/* UI/UX Specs Tab */}
                  <TabsContent value="uiux" className="mt-0 space-y-4">
                    {/* Wireframe Links */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                          <LayoutGrid className="h-3 w-3 text-indigo-500" />
                          Wireframe / Mockup Links
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.wireframe_links}
                          onChange={(e) => setScopeForm({ ...scopeForm, wireframe_links: e.target.value })}
                          placeholder="Links to Figma, mockups, or wireframe references...&#10;e.g., https://figma.com/file/xyz"
                          className="mt-1"
                          rows={3}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* UI Components */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                          <Layers className="h-3 w-3 text-blue-500" />
                          UI Components to Use
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.ui_components}
                          onChange={(e) => setScopeForm({ ...scopeForm, ui_components: e.target.value })}
                          placeholder="Which Shadcn/UI components to use:&#10;- Dialog for modals&#10;- Table for data lists&#10;- Form with validation&#10;- Tabs for sections"
                          className="mt-1"
                          rows={4}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Responsive & Accessibility */}
                    <div className="grid grid-cols-2 gap-4">
                      <Collapsible defaultOpen>
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-1">
                            <Smartphone className="h-3 w-3 text-green-500" />
                            Responsive Requirements
                          </Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.responsive_requirements}
                            onChange={(e) => setScopeForm({ ...scopeForm, responsive_requirements: e.target.value })}
                            placeholder="Mobile/tablet breakpoints:&#10;- Mobile: stack columns&#10;- Tablet: 2-col grid&#10;- Desktop: full layout"
                            className="mt-1"
                            rows={3}
                          />
                        </CollapsibleContent>
                      </Collapsible>

                      <Collapsible defaultOpen>
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-1">
                            <Accessibility className="h-3 w-3 text-purple-500" />
                            Accessibility Level
                          </Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.accessibility_level}
                            onChange={(e) => setScopeForm({ ...scopeForm, accessibility_level: e.target.value })}
                            placeholder="WCAG compliance:&#10;- WCAG 2.1 AA&#10;- Keyboard navigation&#10;- Screen reader support"
                            className="mt-1"
                            rows={3}
                          />
                        </CollapsibleContent>
                      </Collapsible>
                    </div>

                    {/* User Flow */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                          <ArrowRight className="h-3 w-3 text-cyan-500" />
                          User Flow Description
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.user_flow}
                          onChange={(e) => setScopeForm({ ...scopeForm, user_flow: e.target.value })}
                          placeholder="Step-by-step user journey:&#10;1. User clicks 'Create' button&#10;2. Modal opens with form&#10;3. User fills required fields&#10;4. Submit shows loading state&#10;5. Success toast and redirect"
                          className="mt-1"
                          rows={5}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* States */}
                    <div className="grid grid-cols-3 gap-4">
                      <Collapsible>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs">Error States</Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.error_states_ui}
                            onChange={(e) => setScopeForm({ ...scopeForm, error_states_ui: e.target.value })}
                            placeholder="Toast, inline, modal..."
                            className="mt-1"
                            rows={2}
                          />
                        </CollapsibleContent>
                      </Collapsible>

                      <Collapsible>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs">Loading States</Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.loading_states}
                            onChange={(e) => setScopeForm({ ...scopeForm, loading_states: e.target.value })}
                            placeholder="Skeleton, spinner..."
                            className="mt-1"
                            rows={2}
                          />
                        </CollapsibleContent>
                      </Collapsible>

                      <Collapsible>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs">Empty States</Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.empty_states}
                            onChange={(e) => setScopeForm({ ...scopeForm, empty_states: e.target.value })}
                            placeholder="Placeholder, CTA..."
                            className="mt-1"
                            rows={2}
                          />
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  </TabsContent>

                  {/* Error Handling Tab */}
                  <TabsContent value="errors" className="mt-0 space-y-4">
                    {/* Error Scenarios */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                          <Bug className="h-3 w-3 text-red-500" />
                          Expected Error Scenarios
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.error_scenarios}
                          onChange={(e) => setScopeForm({ ...scopeForm, error_scenarios: e.target.value })}
                          placeholder="What can go wrong?&#10;- Network timeout&#10;- Invalid input data&#10;- Resource not found&#10;- Permission denied&#10;- Rate limit exceeded"
                          className="mt-1"
                          rows={5}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Error Messages */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                          <XCircle className="h-3 w-3 text-orange-500" />
                          User-Facing Error Messages
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.error_messages}
                          onChange={(e) => setScopeForm({ ...scopeForm, error_messages: e.target.value })}
                          placeholder="Error message text for each scenario:&#10;- 'Unable to save. Please try again.'&#10;- 'Session expired. Please log in.'&#10;- 'You don't have permission to do this.'"
                          className="mt-1"
                          rows={4}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Retry & Timeout */}
                    <div className="grid grid-cols-2 gap-4">
                      <Collapsible>
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-1">
                            <RefreshCw className="h-3 w-3 text-blue-500" />
                            Retry Logic
                          </Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.retry_logic}
                            onChange={(e) => setScopeForm({ ...scopeForm, retry_logic: e.target.value })}
                            placeholder="Auto-retry requirements:&#10;- 3 retries max&#10;- Exponential backoff&#10;- 1s, 2s, 4s delays"
                            className="mt-1"
                            rows={3}
                          />
                        </CollapsibleContent>
                      </Collapsible>

                      <Collapsible>
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-1">
                            <Timer className="h-3 w-3 text-yellow-500" />
                            Timeout Handling
                          </Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.timeout_handling}
                            onChange={(e) => setScopeForm({ ...scopeForm, timeout_handling: e.target.value })}
                            placeholder="Timeout durations:&#10;- API calls: 30s&#10;- File uploads: 120s&#10;- Show timeout error after"
                            className="mt-1"
                            rows={3}
                          />
                        </CollapsibleContent>
                      </Collapsible>
                    </div>

                    {/* Fallback Behaviors */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3 text-amber-500" />
                          Fallback Behaviors
                        </Label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={scopeForm.fallback_behaviors}
                          onChange={(e) => setScopeForm({ ...scopeForm, fallback_behaviors: e.target.value })}
                          placeholder="What to do when things fail:&#10;- Show cached data if API fails&#10;- Disable feature gracefully&#10;- Redirect to error page&#10;- Offer offline mode"
                          className="mt-1"
                          rows={4}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Logging & Graceful Degradation */}
                    <div className="grid grid-cols-2 gap-4">
                      <Collapsible>
                        <div className="flex items-center justify-between">
                          <Label>Logging Requirements</Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.logging_requirements}
                            onChange={(e) => setScopeForm({ ...scopeForm, logging_requirements: e.target.value })}
                            placeholder="What to log:&#10;- Request/response&#10;- Stack traces&#10;- User context"
                            className="mt-1"
                            rows={3}
                          />
                        </CollapsibleContent>
                      </Collapsible>

                      <Collapsible>
                        <div className="flex items-center justify-between">
                          <Label>Graceful Degradation</Label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <Textarea
                            value={scopeForm.graceful_degradation}
                            onChange={(e) => setScopeForm({ ...scopeForm, graceful_degradation: e.target.value })}
                            placeholder="How feature degrades:&#10;- If payment fails: queue&#10;- If AI fails: manual mode&#10;- If sync fails: local cache"
                            className="mt-1"
                            rows={3}
                          />
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-muted/30 flex justify-between">
              <Button variant="outline" onClick={() => setShowAddScopeItem(false)}>
                Cancel
              </Button>
              <Button onClick={addScopeItem} disabled={!scopeForm.name}>
                Add {scopeForm.category === 'mvp' ? 'MVP Item' : 'Feature'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Prompt Dialog */}
      <Dialog open={showAIPromptDialog} onOpenChange={setShowAIPromptDialog}>
        <DialogContent className="max-w-2xl z-[70]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              AI Feature Specification Generator
            </DialogTitle>
            <DialogDescription>
              Describe the feature you want to build. Be as detailed as possible - include user flows, 
              UI elements, data requirements, and any specific behavior. The AI will generate comprehensive 
              specifications based on your description and Rhino-1's architecture.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-1">Feature: {scopeForm.name}</p>
              <p className="text-xs text-muted-foreground">Hub: {scopeForm.hub === 'none' ? 'General' : scopeForm.hub}</p>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Feature Description & Requirements</Label>
              <Textarea
                value={aiPrompt}
                onChange={(e) => setAIPrompt(e.target.value)}
                placeholder={`Describe what this feature should do. For example:

"I want a lead scoring system that automatically ranks leads based on:
- Their source (Google Ads vs organic)
- How quickly they responded to our outreach
- Their project size and budget
- Their location (prioritize Jacksonville market)

The score should appear on the lead card in the Sales Hub and update in real-time. 
Include a settings panel to adjust the scoring weights.
Must integrate with our existing Builder Prime data..."`}
                className="mt-2 min-h-[200px]"
                rows={10}
              />
              <p className="text-xs text-muted-foreground mt-1">
                The more detail you provide, the better the AI can generate accurate specifications.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAIPromptDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={generateFeatureSpec}
              disabled={generatingSpec}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            >
              {generatingSpec ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating Specifications...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate with AI
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== COST MODAL ==================== */}
      <Dialog open={showCostModal} onOpenChange={setShowCostModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-amber-600" />
              RHINO-1 Project Costs
            </DialogTitle>
            <DialogDescription>Track expenses, salaries, and budget</DialogDescription>
          </DialogHeader>
          
          {costLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Total Spent - Running Total (One-Time costs only - represents actual money spent) */}
              <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">Total Spent to Date</p>
                      <p className="text-4xl font-bold">
                        ${(costData.totals?.one_time || 0).toLocaleString()}
                      </p>
                      <p className="text-xs opacity-75 mt-1">One-time costs only (monthly tracked separately)</p>
                    </div>
                    <DollarSign className="h-12 w-12 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              {/* Cost Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-green-600">${costData.totals?.one_time?.toLocaleString() || 0}</p>
                    <p className="text-xs text-muted-foreground">One-Time Costs</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">${costData.totals?.monthly_expenses?.toLocaleString() || 0}</p>
                    <p className="text-xs text-muted-foreground">Monthly Expenses</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">${costData.totals?.monthly_salary?.toLocaleString() || 0}</p>
                    <p className="text-xs text-muted-foreground">Monthly Salary</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 dark:bg-amber-900/20">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">${costData.totals?.monthly_burn?.toLocaleString() || 0}</p>
                    <p className="text-xs text-muted-foreground">Monthly Burn Rate</p>
                  </CardContent>
                </Card>
              </div>

              {/* Expenses Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg">Expenses</h3>
                  {canEdit && (
                    <Button size="sm" onClick={() => setShowAddExpense(true)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Expense
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {costData.expenses?.length > 0 ? (
                    costData.expenses.map(expense => (
                      <div key={expense.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{expense.name}</span>
                            <Badge variant="outline" className="text-xs capitalize">{expense.category}</Badge>
                            {expense.frequency !== 'one_time' && (
                              <Badge className="text-xs bg-blue-100 text-blue-700">{expense.frequency}</Badge>
                            )}
                          </div>
                          {expense.description && (
                            <p className="text-sm text-muted-foreground mt-1">{expense.description}</p>
                          )}
                          <p className="text-xs text-muted-foreground">{expense.date}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg">${expense.amount?.toLocaleString()}</span>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteExpense(expense.id)}
                              className="h-7 w-7 p-0 text-red-500"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No expenses recorded</p>
                  )}
                </div>
              </div>

              <Separator />

              {/* Salaries Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg">Salaries & Contractors</h3>
                  {canEdit && (
                    <Button size="sm" variant="outline" onClick={() => setShowAddSalary(true)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Salary
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {costData.salaries?.length > 0 ? (
                    costData.salaries.map(salary => (
                      <div key={salary.id} className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{salary.name}</span>
                            {salary.role && <Badge variant="outline" className="text-xs">{salary.role}</Badge>}
                            {!salary.is_active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Since {salary.start_date} {salary.end_date && `• Ended ${salary.end_date}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg">${salary.amount?.toLocaleString()}/mo</span>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteSalary(salary.id)}
                              className="h-7 w-7 p-0 text-red-500"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No salary entries</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Expense Dialog */}
      <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={expenseForm.name}
                onChange={(e) => setExpenseForm({ ...expenseForm, name: e.target.value })}
                placeholder="e.g., Emergent AI Platform"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={expenseForm.category}
                  onValueChange={(value) => setExpenseForm({ ...expenseForm, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="development">Development</SelectItem>
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                    <SelectItem value="services">Third-party Services</SelectItem>
                    <SelectItem value="emergent">Emergent</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frequency</Label>
                <Select
                  value={expenseForm.frequency}
                  onValueChange={(value) => setExpenseForm({ ...expenseForm, frequency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One-Time</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                placeholder="Notes about this expense..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddExpense(false)}>Cancel</Button>
            <Button onClick={addExpense} disabled={!expenseForm.name || !expenseForm.amount}>Add Expense</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Salary Dialog */}
      <Dialog open={showAddSalary} onOpenChange={setShowAddSalary}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Salary Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={salaryForm.name}
                onChange={(e) => setSalaryForm({ ...salaryForm, name: e.target.value })}
                placeholder="Person's name or contractor"
              />
            </div>
            <div>
              <Label>Role (optional)</Label>
              <Input
                value={salaryForm.role}
                onChange={(e) => setSalaryForm({ ...salaryForm, role: e.target.value })}
                placeholder="e.g., Developer, PM"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monthly Amount ($)</Label>
                <Input
                  type="number"
                  value={salaryForm.amount}
                  onChange={(e) => setSalaryForm({ ...salaryForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={salaryForm.start_date}
                  onChange={(e) => setSalaryForm({ ...salaryForm, start_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSalary(false)}>Cancel</Button>
            <Button onClick={addSalary} disabled={!salaryForm.name || !salaryForm.amount}>Add Salary</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

            {/* ==================== TASKS MODAL ==================== */}
      <Dialog open={showTasksModal} onOpenChange={setShowTasksModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-purple-600" />
              Dev Tasks
            </DialogTitle>
            <DialogDescription>
              Track development tasks from BizOps meetings
            </DialogDescription>
          </DialogHeader>
          
          {tasksLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Task Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{tasksData.stats?.total || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Tasks</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{tasksData.stats?.completed || 0}</p>
                    <p className="text-xs text-muted-foreground">Completed</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{tasksData.stats?.in_progress || 0}</p>
                    <p className="text-xs text-muted-foreground">In Progress</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">{tasksData.stats?.pending || 0}</p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </CardContent>
                </Card>
              </div>

              {/* Add Task Button */}
              {canEdit && (
                <Button onClick={() => setShowAddTask(true)} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Task
                </Button>
              )}

              {/* Tasks List */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm">All Tasks</h3>
                {tasksData.tasks?.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-800">
                        <tr>
                          <th className="text-left p-3 font-medium">Title</th>
                          <th className="text-left p-3 font-medium">Assigned To</th>
                          <th className="text-left p-3 font-medium">Start</th>
                          <th className="text-left p-3 font-medium">Due</th>
                          <th className="text-left p-3 font-medium">Status</th>
                          <th className="text-right p-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {tasksData.tasks.map(task => (
                          <tr key={task.id} className={`${task.status === 'completed' ? 'bg-green-50/50 dark:bg-green-900/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => updateTaskStatus(task.id, task.status === 'completed' ? 'pending' : 'completed')}
                                >
                                  {task.status === 'completed' ? (
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <Circle className="h-4 w-4 text-slate-300" />
                                  )}
                                </button>
                                <span className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                                  {task.title}
                                </span>
                                {task.scope_item_id && (
                                  <Link2 className="h-3 w-3 text-purple-500" title="Linked to scope" />
                                )}
                                {task.agent_instructions && (
                                  <Bot className="h-3 w-3 text-blue-500" title="Has agent instructions" />
                                )}
                                <Badge variant={
                                  task.priority === 'high' ? 'destructive' :
                                  task.priority === 'critical' ? 'destructive' :
                                  task.priority === 'medium' ? 'default' : 'secondary'
                                } className="text-[10px] ml-1">
                                  {task.priority}
                                </Badge>
                              </div>
                            </td>
                            <td className="p-3 text-muted-foreground">
                              {task.assigned_agent_name || task.assignee || '—'}
                            </td>
                            <td className="p-3 text-muted-foreground">
                              {task.date || task.start_date ? new Date(task.date || task.start_date).toLocaleDateString() : '—'}
                            </td>
                            <td className="p-3 text-muted-foreground">
                              {task.due_date ? new Date(task.due_date).toLocaleDateString() : '—'}
                            </td>
                            <td className="p-3">
                              <Select
                                value={task.status}
                                onValueChange={(v) => updateTaskStatus(task.id, v)}
                              >
                                <SelectTrigger className="w-28 h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="scheduled">Scheduled</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-3 text-right">
                              {canEdit && (
                                <div className="flex items-center justify-end gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => openTaskForEdit(task)}
                                    title="Edit task"
                                  >
                                    <Pencil className="h-4 w-4 text-blue-500" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => deleteTask(task.id)}>
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No tasks yet. Add your first task!</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <AgentTaskModal
        open={showAddTask}
        onOpenChange={(open) => {
          setShowAddTask(open);
          if (!open) setEditingTask(null);
        }}
        task={editingTask}
        onSave={saveAgentTask}
        getHeaders={getHeaders}
      />

            {/* ==================== IMPLEMENTATION PLAN MODAL ==================== */}
      <ImplementationModal
        open={showImplementationModal}
        onOpenChange={setShowImplementationModal}
        implementationData={implementationData}
        loading={false}
        selectedItem={selectedImplementationItem}
        onSelectItem={setSelectedImplementationItem}
        onUpdateItem={updateImplementationItem}
        canEdit={canEdit}
      />

      {/* ==================== DATA & SECURITY MODAL ==================== */}
      <DataSecurityModal
        open={showDataSecurityModal}
        onOpenChange={setShowDataSecurityModal}
        dataSecurityData={dataSecurityData}
        loading={false}
        selectedItem={selectedDataSecurityItem}
        onSelectItem={setSelectedDataSecurityItem}
        onUpdateItem={updateDataSecurityItem}
        canEdit={canEdit}
      />

{/* ==================== TIMELINE MODAL ==================== */}
      <Dialog open={showTimelineModal} onOpenChange={setShowTimelineModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              RHINO-1 Project Timeline
            </DialogTitle>
            <DialogDescription>
              Visual timeline to MVP with feature milestones
            </DialogDescription>
          </DialogHeader>
          
          {timelineLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Timeline Header with Editable Dates */}
              <div className="flex items-center justify-between flex-wrap gap-4">
                {editingDates ? (
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground">Start:</Label>
                      <Input
                        type="date"
                        value={dateForm.project_start}
                        onChange={(e) => setDateForm({ ...dateForm, project_start: e.target.value })}
                        className="h-8 w-40"
                      />
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground">MVP:</Label>
                      <Input
                        type="date"
                        value={dateForm.mvp_deadline}
                        onChange={(e) => setDateForm({ ...dateForm, mvp_deadline: e.target.value })}
                        className="h-8 w-40"
                      />
                    </div>
                    <Button size="sm" onClick={updateProjectDates}>
                      <Check className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingDates(false)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="text-sm">
                      <span className="text-muted-foreground">Start:</span>{' '}
                      <span className="font-medium">{timelineData.project_start ? format(parseLocalDate(timelineData.project_start), 'MMM d, yyyy') : 'Not set'}</span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <div className="text-sm">
                      <span className="text-muted-foreground">MVP Target:</span>{' '}
                      <Badge className="bg-blue-600 text-white ml-1">
                        {timelineData.mvp_deadline ? format(parseLocalDate(timelineData.mvp_deadline), 'MMM d, yyyy') : 'Not set'}
                      </Badge>
                    </div>
                    {canEdit && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => {
                          setDateForm({
                            project_start: timelineData.project_start || '',
                            mvp_deadline: timelineData.mvp_deadline || ''
                          });
                          setEditingDates(true);
                        }}
                        className="h-7 px-2"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary">
                    {timelineData.completed_count}/{timelineData.total_count} Milestones
                  </Badge>
                  {/* Critical Path Toggle */}
                  {criticalPathIds.length > 1 && (
                    <Button 
                      size="sm" 
                      variant={showCriticalPath ? "default" : "outline"}
                      onClick={() => setShowCriticalPath(!showCriticalPath)}
                      className={`gap-1 ${showCriticalPath ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                    >
                      <AlertTriangle className="h-4 w-4" />
                      {showCriticalPath ? 'Hide' : 'Show'} Critical Path ({criticalPathIds.length})
                    </Button>
                  )}
                  {/* Dependency Toggle */}
                  <Button 
                    size="sm" 
                    variant={showDependencies ? "default" : "outline"}
                    onClick={() => setShowDependencies(!showDependencies)}
                    className="gap-1"
                  >
                    <GitBranch className="h-4 w-4" />
                    {showDependencies ? 'Hide' : 'Show'} Dependencies
                  </Button>
                  {/* Expand/Collapse All Milestones */}
                  {timelineData.milestones?.some(m => m.sub_milestones?.length > 0) && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        const parentIds = timelineData.milestones
                          .filter(m => m.sub_milestones?.length > 0)
                          .map(m => m.id);
                        const allExpanded = parentIds.every(id => expandedMilestones.has(id));
                        if (allExpanded) {
                          // Collapse all
                          setExpandedMilestones(new Set());
                        } else {
                          // Expand all
                          setExpandedMilestones(new Set(parentIds));
                        }
                      }}
                      className="gap-1"
                    >
                      {(() => {
                        const parentIds = timelineData.milestones
                          ?.filter(m => m.sub_milestones?.length > 0)
                          .map(m => m.id) || [];
                        const allExpanded = parentIds.length > 0 && parentIds.every(id => expandedMilestones.has(id));
                        return allExpanded ? (
                          <>
                            <ChevronUp className="h-4 w-4" />
                            Collapse All
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4" />
                            Expand All
                          </>
                        );
                      })()}
                    </Button>
                  )}
                  {canEdit && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setShowAddTestPeriod(true)}>
                        <Clock className="h-4 w-4 mr-1" />
                        Add Testing Period
                      </Button>
                      <Button size="sm" onClick={() => setShowAddMilestone(true)}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add Milestone
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Gantt Chart Timeline */}
              <div className={`border rounded-lg relative ${showDependencies ? 'overflow-visible' : 'overflow-hidden'}`}>
                {/* Month Headers */}
                <div className="flex bg-slate-100 dark:bg-slate-800 border-b">
                  <div className="w-64 flex-shrink-0 p-2 border-r font-medium text-sm">
                    Milestone / Progress
                  </div>
                  <div className="flex-1 relative">
                    <div className="flex h-10">
                      {/* Generate month columns */}
                      {(() => {
                        const months = [];
                        if (timelineData.project_start && timelineData.mvp_deadline) {
                          const start = parseLocalDate(timelineData.project_start);
                          const end = parseLocalDate(timelineData.mvp_deadline);
                          let current = new Date(start.getFullYear(), start.getMonth(), 1);
                          while (current <= end) {
                            months.push(new Date(current));
                            current.setMonth(current.getMonth() + 1);
                          }
                        }
                        return months.map((month, idx) => (
                          <div 
                            key={idx} 
                            className="flex-1 border-r last:border-r-0 flex items-center justify-center text-xs font-medium text-muted-foreground"
                          >
                            {format(month, 'MMM yyyy')}
                          </div>
                        ));
                      })()}
                    </div>
                    {/* Today marker line */}
                    {timelineData.project_start && timelineData.mvp_deadline && (
                      <div 
                        className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-50"
                        style={{ left: `${getTimelinePosition(new Date().toISOString().split('T')[0])}%` }}
                      >
                        <div className="absolute -top-0 left-1/2 -translate-x-1/2 text-[9px] font-bold text-red-500 bg-white dark:bg-slate-900 px-1 rounded">
                          TODAY
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Testing Periods Section with Collapse Toggle */}
                {timelineData.testing_periods?.length > 0 && (
                  <div className="border-b-2 border-dashed">
                    {/* Testing Periods Header with Collapse Button */}
                    <div className="flex items-center bg-purple-50 dark:bg-purple-900/20 border-b">
                      <button
                        onClick={() => setShowTestingPeriods(!showTestingPeriods)}
                        className="w-64 flex-shrink-0 p-2 border-r flex items-center gap-2 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                      >
                        {showTestingPeriods ? (
                          <ChevronDown className="h-4 w-4 text-purple-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-purple-500" />
                        )}
                        <span className="text-purple-500">🧪</span>
                        <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                          Testing Phases ({timelineData.testing_periods.length})
                        </span>
                      </button>
                      <div className="flex-1 p-1">
                        <span className="text-xs text-purple-600 dark:text-purple-400">
                          {showTestingPeriods ? 'Click to collapse' : 'Click to expand testing periods'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Testing Periods Rows (Collapsible) */}
                    {showTestingPeriods && timelineData.testing_periods.map((period, idx) => {
                      const startPos = getTimelinePosition(period.start_date);
                      const endPos = getTimelinePosition(period.end_date);
                      const width = Math.max(endPos - startPos, 2);
                      
                      return (
                        <div key={period.id} className="flex hover:bg-slate-50 dark:hover:bg-slate-800/50 group">
                          <div className="w-64 flex-shrink-0 p-2 border-r flex items-center gap-2 pl-8">
                            <span className="text-sm truncate">{period.name}</span>
                          </div>
                          <div className="flex-1 relative py-2 px-1">
                            <div 
                              className="absolute h-6 bg-purple-400 dark:bg-purple-600 rounded-full opacity-80 flex items-center px-2 text-white text-[10px] font-medium cursor-pointer hover:opacity-100 transition-opacity"
                              style={{ left: `${startPos}%`, width: `${width}%`, top: '50%', transform: 'translateY(-50%)' }}
                              title={`${period.name}: ${format(parseLocalDate(period.start_date), 'MMM d')} - ${format(parseLocalDate(period.end_date), 'MMM d')}`}
                            >
                              <span className="truncate">{period.name}</span>
                            </div>
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteTestingPeriod(period.id)}
                                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-red-500"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditTestPeriod(period)}
                                className="absolute right-8 top-1/2 -translate-y-1/2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-blue-500"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Milestone Rows with Sub-milestone Support */}
                {timelineData.milestones?.map((milestone, idx) => {
                  const hasStartDate = milestone.start_date;
                  const startPos = hasStartDate ? getTimelinePosition(milestone.start_date) : getTimelinePosition(milestone.target_date) - 2;
                  const endPos = getTimelinePosition(milestone.target_date);
                  const width = Math.max(endPos - startPos, 3);
                  const phaseConfig = PHASE_CONFIG[milestone.phase_type] || PHASE_CONFIG.development;
                  const isHovered = hoveredMilestone === milestone.id;
                  const isOnCriticalPath = showCriticalPath && criticalPathIds.includes(milestone.id);
                  const subMilestones = milestone.sub_milestones || [];
                  const hasSubMilestones = subMilestones.length > 0;
                  const isExpanded = expandedMilestones.has(milestone.id);
                  
                  // For MVP milestone, show OVERALL project progress (all milestones)
                  // For other milestones, show sub-milestone progress
                  // Only the actual "MVP" milestone (by name) shows aggregate project progress
                  const isMVPMilestone = milestone.is_mvp_milestone === true && milestone.name?.toLowerCase().trim() === 'mvp';
                  const progress = isMVPMilestone 
                    ? getOverallProjectProgress() 
                    : getSubMilestoneProgress(subMilestones);
                  const showProgress = isMVPMilestone || hasSubMilestones;
                  
                  // Determine bar color based on status and critical path
                  const getBarStyle = () => {
                    if (milestone.status === 'completed') return 'bg-green-500';
                    if (milestone.status === 'delayed') return 'bg-red-500';
                    if (isOnCriticalPath) return 'bg-orange-500 ring-2 ring-orange-300 ring-offset-1';
                    if (milestone.status === 'in_progress') return phaseConfig.color;
                    return phaseConfig.lightColor;
                  };
                  
                  return (
                    <React.Fragment key={milestone.id}>
                      {/* Parent Milestone Row */}
                      <div 
                        className={`flex hover:bg-slate-50 dark:hover:bg-slate-800/50 group ${idx % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50/50 dark:bg-slate-800/30'} ${isOnCriticalPath ? 'bg-orange-50 dark:bg-orange-900/20' : ''}`}
                        onMouseEnter={() => setHoveredMilestone(milestone.id)}
                        onMouseLeave={() => setHoveredMilestone(null)}
                      >
                        {/* Left Panel - Name and Progress Info */}
                        <div className="w-64 flex-shrink-0 p-2 border-r">
                          <div className="flex items-center gap-2">
                            {/* Expand/Collapse Button - Show for milestones with sub-tasks (everyone can view)
                                OR for milestones without sub-tasks (only admins can adjust progress) */}
                            {!isMVPMilestone && (hasSubMilestones || canEdit) ? (
                              <button
                                onClick={() => toggleMilestoneExpansion(milestone.id)}
                                className="p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                title={hasSubMilestones ? 'Show/hide sub-tasks' : 'Adjust progress'}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-slate-500" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-slate-500" />
                                )}
                              </button>
                            ) : !isMVPMilestone ? (
                              <div className="w-5" /> // Spacer for non-admin viewing milestones without sub-tasks
                            ) : (
                              <div className="w-5" /> // Spacer for MVP milestone
                            )}
                            {isOnCriticalPath && <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0" />}
                            <span>{phaseConfig.icon}</span>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${isOnCriticalPath ? 'text-orange-600 dark:text-orange-400' : ''}`}>
                                {milestone.name}
                                {isMVPMilestone && <span className="ml-1 text-[10px] text-blue-500">(Project)</span>}
                              </p>
                            </div>
                            {milestone.status === 'completed' && <Check className="h-4 w-4 text-green-500 flex-shrink-0" />}
                          </div>
                          {/* Progress Bar for Parent Milestones with sub-tasks OR MVP (aggregate display) */}
                          {showProgress && (
                            <div className="flex items-center gap-2 mt-1 pl-6">
                              <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${progress.percent >= 100 ? 'bg-green-500' : 'bg-blue-500'} transition-all duration-300`}
                                  style={{ width: `${progress.percent}%` }}
                                />
                              </div>
                              <span className={`text-[10px] font-bold w-10 text-right ${progress.percent >= 50 ? 'text-green-600' : 'text-blue-500'}`}>
                                {progress.percent}%
                              </span>
                              <span className="text-[9px] text-muted-foreground">
                                ({progress.completed}/{progress.total})
                              </span>
                            </div>
                          )}
                          {/* Progress indicator for milestones without sub-tasks (show their own progress) */}
                          {!hasSubMilestones && !isMVPMilestone && (
                            <div className="flex items-center gap-2 mt-1 pl-6">
                              <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${(milestone.progress || 0) >= 100 ? 'bg-green-500' : 'bg-blue-500'} transition-all duration-300`}
                                  style={{ width: `${milestone.progress || 0}%` }}
                                />
                              </div>
                              <span className={`text-[10px] font-bold w-10 text-right ${(milestone.progress || 0) >= 50 ? 'text-green-600' : 'text-blue-500'}`}>
                                {milestone.progress || 0}%
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 relative py-2 px-1">
                          {/* Milestone Bar with Progress Overlay */}
                          <div 
                            className={`absolute h-6 ${getBarStyle()} rounded-full flex items-center justify-between px-2 cursor-pointer transition-all duration-200 ${isHovered ? 'h-7 shadow-lg z-10' : ''} overflow-hidden`}
                            style={{ left: `${startPos}%`, width: `${width}%`, top: '50%', transform: 'translateY(-50%)' }}
                            onClick={() => setSelectedMilestone(milestone)}
                            data-testid={`milestone-bar-${milestone.id}`}
                          >
                            {/* Progress Bar Overlay (Green) - shown for MVP or milestones with sub-tasks */}
                            {showProgress && progress.percent > 0 && (
                              <div 
                                className={`absolute top-0 left-0 h-full ${isMVPMilestone ? 'bg-green-500' : 'bg-green-500/90'} rounded-l-full transition-all duration-500`}
                                style={{ width: `${progress.percent}%` }}
                              />
                            )}
                            {/* Progress Bar Overlay for milestones WITHOUT sub-tasks (use their own progress) */}
                            {!hasSubMilestones && !isMVPMilestone && (milestone.progress || 0) > 0 && milestone.status !== 'completed' && (
                              <div 
                                className="absolute top-0 left-0 h-full bg-green-500/90 rounded-l-full transition-all duration-500"
                                style={{ width: `${milestone.progress || 0}%` }}
                              />
                            )}
                            {/* Start dot */}
                            {hasStartDate && (
                              <div className="w-2 h-2 bg-white rounded-full flex-shrink-0 z-10" />
                            )}
                            {/* Label inside bar if wide enough */}
                            {width > 10 && (
                              <span className="text-white text-[10px] font-medium truncate mx-1 flex-1 z-10 drop-shadow-sm">
                                {milestone.name}
                                {showProgress && ` (${progress.percent}%)`}
                                {!hasSubMilestones && !isMVPMilestone && ` (${milestone.progress || 0}%)`}
                              </span>
                            )}
                            {/* End dot */}
                            <div className="w-3 h-3 bg-white rounded-full flex-shrink-0 flex items-center justify-center z-10">
                              {milestone.status === 'completed' && <Check className="h-2 w-2 text-green-600" />}
                            </div>
                          </div>
                          
                          {/* Hover tooltip */}
                          {isHovered && (
                            <div className="absolute left-1/2 -translate-x-1/2 -top-20 bg-slate-900 dark:bg-slate-700 text-white text-xs p-2 rounded shadow-lg z-50 whitespace-nowrap pointer-events-none">
                              <p className="font-medium">{milestone.name}</p>
                              <p className="text-slate-300 text-[10px]">Click to view details</p>
                              <p className="text-slate-300">
                                {milestone.start_date && `${format(parseLocalDate(milestone.start_date), 'MMM d')} → `}
                                {format(parseLocalDate(milestone.target_date), 'MMM d, yyyy')}
                              </p>
                              {showProgress && (
                                <p className="text-green-400">
                                  {isMVPMilestone ? 'Overall Project Progress' : 'Progress'}: {progress.completed}/{progress.total} ({progress.percent}%)
                                </p>
                              )}
                              <p className={`${milestone.status === 'completed' ? 'text-green-400' : milestone.status === 'delayed' ? 'text-red-400' : 'text-blue-400'}`}>
                                {milestone.status.charAt(0).toUpperCase() + milestone.status.slice(1).replace('_', ' ')}
                              </p>
                            </div>
                          )}
                          
                          {/* Edit controls on hover */}
                          {canEdit && (
                            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditMilestone(milestone)}
                                className="h-6 w-6 p-0 text-blue-500 bg-white dark:bg-slate-800"
                                title="Edit milestone"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Select
                                value={milestone.status}
                                onValueChange={(value) => updateMilestone(milestone.id, { status: value })}
                              >
                                <SelectTrigger className="h-6 w-24 text-[10px] bg-white dark:bg-slate-800">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="delayed">Delayed</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteMilestone(milestone.id)}
                                className="h-6 w-6 p-0 text-red-500 bg-white dark:bg-slate-800"
                                title="Delete milestone"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Sub-Milestone Rows (Expanded) */}
                      {isExpanded && subMilestones.map((subMilestone, subIdx) => {
                        const subHasStartDate = subMilestone.start_date;
                        const subStartPos = subHasStartDate ? getTimelinePosition(subMilestone.start_date) : getTimelinePosition(subMilestone.target_date) - 1;
                        const subEndPos = getTimelinePosition(subMilestone.target_date);
                        const subWidth = Math.max(subEndPos - subStartPos, 2);
                        const subPhaseConfig = PHASE_CONFIG[subMilestone.phase_type] || PHASE_CONFIG.development;
                        const subIsHovered = hoveredMilestone === subMilestone.id;
                        
                        const getSubBarStyle = () => {
                          if (subMilestone.status === 'completed') return 'bg-green-400';
                          if (subMilestone.status === 'delayed') return 'bg-red-400';
                          if (subMilestone.status === 'in_progress') return subPhaseConfig.color;
                          return subPhaseConfig.lightColor;
                        };
                        
                        return (
                          <div 
                            key={subMilestone.id}
                            className="flex hover:bg-blue-50 dark:hover:bg-blue-900/10 group bg-slate-50/30 dark:bg-slate-800/20"
                            onMouseEnter={() => setHoveredMilestone(subMilestone.id)}
                            onMouseLeave={() => setHoveredMilestone(null)}
                          >
                            {/* Left panel with name and ALWAYS VISIBLE progress slider */}
                            <div className="w-64 flex-shrink-0 p-2 border-r border-l-2 border-blue-200 dark:border-blue-800 pl-8">
                              <div className="flex items-center gap-2 mb-1">
                                <CornerDownRight className="h-3 w-3 text-slate-400 flex-shrink-0" />
                                <span className="text-xs">{subPhaseConfig.icon}</span>
                                <p className="text-xs font-medium truncate flex-1">{subMilestone.name}</p>
                                {subMilestone.status === 'completed' && <Check className="h-3 w-3 text-green-500 flex-shrink-0" />}
                              </div>
                              {/* Progress Slider with Save Button - ADMIN ONLY */}
                              {canEdit && subMilestone.status !== 'completed' ? (
                                <ProgressSliderWithSave
                                  value={subMilestone.progress || 0}
                                  onSave={(newProgress) => {
                                    updateMilestone(subMilestone.id, { 
                                      progress: newProgress,
                                      status: newProgress >= 100 ? 'completed' : 
                                              newProgress > 0 ? 'in_progress' : subMilestone.status
                                    });
                                  }}
                                />
                              ) : (
                                /* Read-only progress bar for non-admins OR completed items */
                                <div className="flex items-center gap-2 pl-5">
                                  <div className={`flex-1 h-2 ${subMilestone.status === 'completed' ? 'bg-green-200 dark:bg-green-800' : 'bg-slate-200 dark:bg-slate-700'} rounded-full overflow-hidden`}>
                                    <div 
                                      className={`h-full ${subMilestone.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'}`} 
                                      style={{ width: `${subMilestone.status === 'completed' ? 100 : (subMilestone.progress || 0)}%` }} 
                                    />
                                  </div>
                                  <span className={`text-[10px] font-bold w-8 text-right ${subMilestone.status === 'completed' ? 'text-green-600' : 'text-blue-500'}`}>
                                    {subMilestone.status === 'completed' ? 100 : (subMilestone.progress || 0)}%
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 relative py-2 px-1">
                              {/* Sub-Milestone Bar (smaller) with progress overlay */}
                              <div 
                                className={`absolute h-4 ${getSubBarStyle()} rounded-full flex items-center justify-between px-1.5 cursor-pointer transition-all duration-200 ${subIsHovered ? 'h-5 shadow-md z-10' : ''} opacity-80 overflow-hidden`}
                                style={{ left: `${subStartPos}%`, width: `${subWidth}%`, top: '50%', transform: 'translateY(-50%)' }}
                                onClick={() => setSelectedMilestone(subMilestone)}
                                data-testid={`sub-milestone-bar-${subMilestone.id}`}
                              >
                                {/* Progress Bar Overlay (Green) */}
                                {(subMilestone.progress || 0) > 0 && subMilestone.status !== 'completed' && (
                                  <div 
                                    className="absolute top-0 left-0 h-full bg-green-500/80 rounded-l-full transition-all duration-300"
                                    style={{ width: `${subMilestone.progress || 0}%` }}
                                  />
                                )}
                                {/* Start dot */}
                                {subHasStartDate && (
                                  <div className="w-1.5 h-1.5 bg-white rounded-full flex-shrink-0 z-10" />
                                )}
                                {/* Label inside bar if wide enough */}
                                {subWidth > 12 && (
                                  <span className="text-white text-[8px] font-medium truncate mx-0.5 flex-1 z-10">
                                    {subMilestone.name} ({subMilestone.progress || 0}%)
                                  </span>
                                )}
                                {/* End dot */}
                                <div className="w-2 h-2 bg-white rounded-full flex-shrink-0 flex items-center justify-center z-10">
                                  {subMilestone.status === 'completed' && <Check className="h-1.5 w-1.5 text-green-600" />}
                                </div>
                              </div>
                              
                              {/* Sub-milestone hover tooltip */}
                              {subIsHovered && (
                                <div className="absolute left-1/2 -translate-x-1/2 -top-16 bg-slate-900 dark:bg-slate-700 text-white text-xs p-2 rounded shadow-lg z-50 whitespace-nowrap pointer-events-none">
                                  <p className="font-medium text-[11px]">{subMilestone.name}</p>
                                  <p className="text-slate-300 text-[10px]">
                                    Sub-task of {milestone.name}
                                  </p>
                                  <p className="text-slate-300 text-[10px]">
                                    {subMilestone.start_date && `${format(parseLocalDate(subMilestone.start_date), 'MMM d')} → `}
                                    {format(parseLocalDate(subMilestone.target_date), 'MMM d, yyyy')}
                                  </p>
                                  <p className="text-green-400 text-[10px]">Progress: {subMilestone.progress || 0}%</p>
                                </div>
                              )}
                              
                              {/* Edit/Delete controls on hover for sub-milestones */}
                              {canEdit && (
                                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditMilestone(subMilestone)}
                                    className="h-5 w-5 p-0 text-blue-500 bg-white dark:bg-slate-800"
                                    title="Edit sub-task"
                                  >
                                    <Pencil className="h-2.5 w-2.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteMilestone(subMilestone.id)}
                                    className="h-5 w-5 p-0 text-red-500 bg-white dark:bg-slate-800"
                                    title="Delete sub-task"
                                  >
                                    <Trash2 className="h-2.5 w-2.5" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Progress Slider Row for milestones WITHOUT sub-tasks (when expanded) - ADMIN ONLY */}
                      {isExpanded && !hasSubMilestones && !isMVPMilestone && canEdit && (
                        <div 
                          className="flex bg-blue-50/50 dark:bg-blue-900/10"
                        >
                          <div className="w-64 flex-shrink-0 p-2 border-r border-l-2 border-blue-300 dark:border-blue-700 pl-8">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs">📊</span>
                              <p className="text-xs font-medium">Adjust Progress</p>
                            </div>
                            {/* Progress Slider with Save Button */}
                            {milestone.status !== 'completed' ? (
                              <ProgressSliderWithSave
                                value={milestone.progress || 0}
                                onSave={(newProgress) => {
                                  updateMilestone(milestone.id, { 
                                    progress: newProgress,
                                    status: newProgress >= 100 ? 'completed' : 
                                            newProgress > 0 ? 'in_progress' : milestone.status
                                  });
                                }}
                              />
                            ) : (
                              <div className="flex items-center gap-2 pl-5">
                                <div className="flex-1 h-2 bg-green-200 dark:bg-green-800 rounded-full overflow-hidden">
                                  <div className="h-full bg-green-500" style={{ width: '100%' }} />
                                </div>
                                <span className="text-[10px] font-bold w-8 text-right text-green-600">100%</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 relative py-2 px-1 flex items-center">
                            <p className="text-xs text-muted-foreground ml-2">
                              Drag slider to set progress • Click ✓ to save
                            </p>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Dependency Arrows SVG Overlay */}
                {showDependencies && timelineData.milestones?.some(m => m.dependencies?.length > 0) && (
                  <svg 
                    className="absolute pointer-events-none overflow-visible" 
                    style={{ 
                      left: '192px', // w-48 = 192px
                      top: '40px', // Header height
                      width: 'calc(100% - 192px)',
                      height: `${(timelineData.testing_periods?.length || 0) * 40 + timelineData.milestones.length * 40}px`,
                      zIndex: 50
                    }}
                  >
                    <defs>
                      <marker
                        id="dependency-arrow"
                        markerWidth="10"
                        markerHeight="10"
                        refX="9"
                        refY="5"
                        orient="auto"
                      >
                        <path d="M0,0 L0,10 L10,5 z" fill="#8b5cf6" />
                      </marker>
                      <marker
                        id="critical-path-arrow"
                        markerWidth="10"
                        markerHeight="10"
                        refX="9"
                        refY="5"
                        orient="auto"
                      >
                        <path d="M0,0 L0,10 L10,5 z" fill="#f97316" />
                      </marker>
                    </defs>
                    {timelineData.milestones?.map(milestone => {
                      if (!milestone.dependencies?.length) return null;
                      return milestone.dependencies.map(depId => {
                        const depMilestone = timelineData.milestones.find(m => m.id === depId);
                        if (!depMilestone) return null;
                        
                        const sourceIdx = timelineData.milestones.findIndex(m => m.id === depId);
                        const targetIdx = timelineData.milestones.findIndex(m => m.id === milestone.id);
                        const testPeriodsOffset = (timelineData.testing_periods?.length || 0) * 40;
                        
                        // Check if this dependency is on the critical path
                        const isCriticalPathDep = showCriticalPath && 
                          criticalPathIds.includes(depId) && 
                          criticalPathIds.includes(milestone.id) &&
                          criticalPathIds.indexOf(milestone.id) === criticalPathIds.indexOf(depId) + 1;
                        
                        // Y positions (center of each row, 40px row height)
                        const sourceY = testPeriodsOffset + sourceIdx * 40 + 20;
                        const targetY = testPeriodsOffset + targetIdx * 40 + 20;
                        
                        // X positions as percentages
                        const sourceEndPos = getTimelinePosition(depMilestone.target_date);
                        const targetStartPos = milestone.start_date 
                          ? getTimelinePosition(milestone.start_date) 
                          : Math.max(getTimelinePosition(milestone.target_date) - 5, 0);
                        
                        // Build path with bezier curves (single line to avoid SVG warnings)
                        const controlX1 = Math.min(sourceEndPos + 8, 100);
                        const controlX2 = Math.max(targetStartPos - 8, 0);
                        const pathD = sourceY === targetY 
                          ? `M ${sourceEndPos}% ${sourceY} L ${targetStartPos}% ${targetY}`
                          : `M ${sourceEndPos}% ${sourceY} C ${controlX1}% ${sourceY}, ${controlX2}% ${targetY}, ${targetStartPos}% ${targetY}`;
                        
                        const arrowColor = isCriticalPathDep ? '#f97316' : '#8b5cf6';
                        const arrowMarker = isCriticalPathDep ? 'url(#critical-path-arrow)' : 'url(#dependency-arrow)';
                        
                        return (
                          <g key={`dep-${depId}-${milestone.id}`}>
                            {/* Connection line */}
                            <path
                              d={pathD}
                              stroke={arrowColor}
                              strokeWidth={isCriticalPathDep ? '3.5' : '2.5'}
                              fill="none"
                              strokeDasharray={isCriticalPathDep ? '0' : '8,4'}
                              markerEnd={arrowMarker}
                            />
                            {/* Small circle at source end */}
                            <circle 
                              cx={`${sourceEndPos}%`} 
                              cy={sourceY} 
                              r={isCriticalPathDep ? '6' : '5'}
                              fill={arrowColor}
                            />
                          </g>
                        );
                      });
                    })}
                  </svg>
                )}

                {/* Empty state */}
                {timelineData.milestones?.length === 0 && timelineData.testing_periods?.length === 0 && (
                  <div className="p-8 text-center">
                    <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">No milestones or testing periods defined</p>
                    {canEdit && (
                      <Button className="mt-4" onClick={() => setShowAddMilestone(true)}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add First Milestone
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Phase Legend */}
              <div className="flex items-center justify-center gap-4 text-xs flex-wrap mt-4">
                {Object.entries(PHASE_CONFIG).map(([key, config]) => (
                  <div key={key} className="flex items-center gap-1">
                    <div className={`w-3 h-3 rounded ${config.color}`} />
                    <span>{config.label}</span>
                  </div>
                ))}
                <div className="flex items-center gap-1 border-l pl-4 ml-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span>Completed</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span>Delayed</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-6 h-3 rounded bg-purple-400" />
                  <span>Testing Period</span>
                </div>
                <div className="flex items-center gap-1 border-l pl-4 ml-2">
                  <svg width="24" height="12" className="flex-shrink-0">
                    <line x1="0" y1="6" x2="20" y2="6" stroke="#8b5cf6" strokeWidth="2" strokeDasharray="4,2" />
                    <polygon points="20,3 20,9 24,6" fill="#8b5cf6" />
                  </svg>
                  <span>Dependency</span>
                </div>
                {criticalPathIds.length > 1 && (
                  <div className="flex items-center gap-1">
                    <svg width="24" height="12" className="flex-shrink-0">
                      <line x1="0" y1="6" x2="20" y2="6" stroke="#f97316" strokeWidth="3" />
                      <polygon points="20,3 20,9 24,6" fill="#f97316" />
                    </svg>
                    <span className="text-orange-600 font-medium">Critical Path</span>
                  </div>
                )}
              </div>

              {/* Critical Path Progress Info */}
              {showCriticalPath && criticalPathIds.length > 1 && (
                <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                    <h4 className="font-semibold text-orange-700 dark:text-orange-400">Critical Path Analysis</h4>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Chain Length</p>
                      <p className="font-medium text-orange-600">{criticalPathIds.length} milestones</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Completed</p>
                      <p className="font-medium text-green-600">
                        {timelineData.milestones?.filter(m => criticalPathIds.includes(m.id) && m.status === 'completed').length || 0} / {criticalPathIds.length}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Start Date</p>
                      <p className="font-medium">
                        {(() => {
                          const firstMilestone = timelineData.milestones?.find(m => m.id === criticalPathIds[0]);
                          return firstMilestone?.start_date ? format(parseLocalDate(firstMilestone.start_date), 'MMM d, yyyy') : 'N/A';
                        })()}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">End Date</p>
                      <p className="font-medium">
                        {(() => {
                          const lastMilestone = timelineData.milestones?.find(m => m.id === criticalPathIds[criticalPathIds.length - 1]);
                          return lastMilestone?.target_date ? format(parseLocalDate(lastMilestone.target_date), 'MMM d, yyyy') : 'N/A';
                        })()}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground mb-1">Path Sequence:</p>
                    <div className="flex items-center gap-1 flex-wrap text-xs">
                      {criticalPathIds.map((id, idx) => {
                        const m = timelineData.milestones?.find(ms => ms.id === id);
                        if (!m) return null;
                        return (
                          <React.Fragment key={id}>
                            <span className={`px-2 py-0.5 rounded ${m.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                              {m.name}
                            </span>
                            {idx < criticalPathIds.length - 1 && <ArrowRight className="h-3 w-3 text-orange-400" />}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Testing Periods Section - Collapsible */}
              {timelineData.testing_periods?.length > 0 && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowTestingPeriodsSection(!showTestingPeriodsSection)}
                    className="w-full flex items-center justify-between mb-3 p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                  >
                    <h3 className="font-semibold flex items-center gap-2">
                      {showTestingPeriodsSection ? (
                        <ChevronDown className="h-4 w-4 text-purple-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-purple-500" />
                      )}
                      <Clock className="h-4 w-4 text-purple-500" />
                      Testing Periods ({timelineData.testing_periods.length})
                    </h3>
                    <span className="text-xs text-purple-600 dark:text-purple-400">
                      {showTestingPeriodsSection ? 'Click to collapse' : 'Click to expand'}
                    </span>
                  </button>
                  {showTestingPeriodsSection && (
                    <div className="space-y-2">
                      {timelineData.testing_periods.map(period => (
                        <div 
                          key={period.id} 
                          className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded bg-purple-400" />
                            <div>
                              <p className="font-medium text-sm">{period.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {format(parseLocalDate(period.start_date), 'MMM d')} - {format(parseLocalDate(period.end_date), 'MMM d, yyyy')}
                              </p>
                              {period.description && (
                                <p className="text-xs text-muted-foreground mt-1">{period.description}</p>
                              )}
                            </div>
                          </div>
                          {canEdit && (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); openEditTestPeriod(period); }}
                                className="h-7 w-7 p-0 text-blue-500"
                                title="Edit testing period"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); deleteTestingPeriod(period.id); }}
                                className="h-7 w-7 p-0 text-red-500"
                                title="Delete testing period"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Milestone List - Collapsible */}
              {timelineData.milestones?.length > 0 && (
                <div className="mt-6">
                  <button
                    onClick={() => setShowAllMilestonesSection(!showAllMilestonesSection)}
                    className="w-full flex items-center justify-between mb-3 p-2 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  >
                    <h3 className="font-semibold flex items-center gap-2">
                      {showAllMilestonesSection ? (
                        <ChevronDown className="h-4 w-4 text-slate-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-slate-500" />
                      )}
                      <ListChecks className="h-4 w-4 text-blue-500" />
                      All Milestones ({timelineData.milestones.length})
                    </h3>
                    <span className="text-xs text-muted-foreground">
                      {showAllMilestonesSection ? 'Click to collapse' : 'Click to expand'}
                    </span>
                  </button>
                  
                  {showAllMilestonesSection && (
                    <>
                      {canEdit && (
                        <div className="flex justify-end mb-3">
                          <Button 
                            size="sm" 
                            variant={reorderMode ? "default" : "outline"}
                            onClick={() => setReorderMode(!reorderMode)}
                            className="gap-1"
                          >
                            <ArrowUpDown className="h-4 w-4" />
                            {reorderMode ? 'Done Reordering' : 'Reorder'}
                          </Button>
                        </div>
                      )}
                  
                      {reorderMode ? (
                        /* Reorder Mode - Drag and Drop */
                        <DndContext
                          sensors={dndSensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleMilestoneDragEnd}
                        >
                          <SortableContext
                            items={timelineData.milestones.map(m => m.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-2 bg-slate-100 dark:bg-slate-900">
                              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                                <GripVertical className="h-3 w-3" />
                                Drag milestones to reorder them on the Gantt chart
                              </p>
                              {timelineData.milestones.map(milestone => {
                                const phaseConfig = PHASE_CONFIG[milestone.phase_type] || PHASE_CONFIG.development;
                                return (
                                  <SortableMilestoneRow
                                    key={milestone.id}
                                    milestone={milestone}
                                    phaseConfig={phaseConfig}
                                    canEdit={canEdit}
                                    onEdit={openEditMilestone}
                                    onDelete={deleteMilestone}
                                    onUpdate={updateMilestone}
                                  />
                                );
                              })}
                            </div>
                          </SortableContext>
                        </DndContext>
                      ) : (
                    /* Normal Mode - View with Edit Controls - Hierarchical with Collapsible Parents */
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {timelineData.milestones.map(milestone => {
                        const phaseConfig = PHASE_CONFIG[milestone.phase_type] || PHASE_CONFIG.development;
                        const subMilestones = milestone.sub_milestones || [];
                        const hasSubMilestones = subMilestones.length > 0;
                        const isExpandedInList = expandedMilestonesInList.has(milestone.id);
                        const progress = getSubMilestoneProgress(subMilestones);
                        
                        return (
                          <div key={milestone.id}>
                            {/* Parent Milestone */}
                            <div 
                              className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                              onMouseEnter={() => setHoveredMilestone(milestone.id)}
                              onMouseLeave={() => setHoveredMilestone(null)}
                            >
                              <div className="flex items-center gap-3">
                                {/* Expand/Collapse button for parent milestones with sub-tasks */}
                                {hasSubMilestones ? (
                                  <button
                                    onClick={() => toggleMilestoneInListExpansion(milestone.id)}
                                    className="p-0.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded transition-colors"
                                  >
                                    {isExpandedInList ? (
                                      <ChevronDown className="h-4 w-4 text-slate-500" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-slate-500" />
                                    )}
                                  </button>
                                ) : (
                                  <div className="w-5" />
                                )}
                                <span className="text-sm">{phaseConfig.icon}</span>
                                <div className={`w-3 h-3 rounded-full ${getMilestoneColor(milestone.status)}`} />
                                <div>
                                  <p className="font-medium text-sm">{milestone.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {milestone.start_date && `${format(parseLocalDate(milestone.start_date), 'MMM d')} → `}
                                    {format(parseLocalDate(milestone.target_date), 'MMM d, yyyy')}
                                    {milestone.hub && ` • ${milestone.hub}`}
                                    <span className="ml-2 text-[10px] opacity-70">({phaseConfig.label})</span>
                                    {hasSubMilestones && (
                                      <span className="ml-2 text-[10px] text-blue-500">
                                        ({subMilestones.length} sub-task{subMilestones.length > 1 ? 's' : ''} • {progress.percent}%)
                                      </span>
                                    )}
                                    {!hasSubMilestones && (milestone.progress || 0) > 0 && (
                                      <span className="ml-2 text-[10px] text-green-500">
                                        ({milestone.progress}%)
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              {canEdit && (
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-1">
                                    <Input
                                      type="date"
                                      defaultValue={milestone.start_date || ''}
                                      onBlur={(e) => {
                                        if (e.target.value !== (milestone.start_date || '')) {
                                          updateMilestone(milestone.id, { start_date: e.target.value });
                                        }
                                      }}
                                      className="h-7 w-32 text-xs"
                                      placeholder="Start"
                                      title="Start Date"
                                    />
                                    <span className="text-xs text-muted-foreground">→</span>
                                    <Input
                                      type="date"
                                      defaultValue={milestone.target_date}
                                      onBlur={(e) => {
                                        if (e.target.value !== milestone.target_date) {
                                          updateMilestone(milestone.id, { target_date: e.target.value });
                                        }
                                      }}
                                      className="h-7 w-32 text-xs"
                                      title="Target Date"
                                    />
                                  </div>
                                  <Select
                                    value={milestone.phase_type || 'development'}
                                    onValueChange={(value) => updateMilestone(milestone.id, { phase_type: value })}
                                  >
                                    <SelectTrigger className="h-7 w-24 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="planning">Planning</SelectItem>
                                      <SelectItem value="design">Design</SelectItem>
                                      <SelectItem value="development">Development</SelectItem>
                                      <SelectItem value="internal_testing">Int. Test</SelectItem>
                                      <SelectItem value="staff_testing">Staff Test</SelectItem>
                                      <SelectItem value="deploy_ready">Deploy</SelectItem>
                                      <SelectItem value="full_testing">Full Test</SelectItem>
                                      <SelectItem value="mvp_ready">MVP Ready</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Select
                                    value={milestone.status}
                                    onValueChange={(value) => updateMilestone(milestone.id, { status: value })}
                                  >
                                    <SelectTrigger className="h-7 w-24 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="pending">Pending</SelectItem>
                                      <SelectItem value="in_progress">In Progress</SelectItem>
                                      <SelectItem value="completed">Completed</SelectItem>
                                      <SelectItem value="delayed">Delayed</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      // Pre-fill form with this milestone as parent
                                      setMilestoneForm({
                                        ...milestoneForm,
                                        parent_id: milestone.id,
                                        hub: milestone.hub || 'none'
                                      });
                                      setShowAddMilestone(true);
                                    }}
                                    className="h-7 w-7 p-0 text-green-500"
                                    title="Add sub-task"
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditMilestone(milestone)}
                                    className="h-7 w-7 p-0 text-blue-500"
                                    title="Edit milestone"
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteMilestone(milestone.id)}
                                    className="h-7 w-7 p-0 text-red-500"
                                    title="Delete milestone"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                            
                            {/* Sub-milestones (nested) - Only show when expanded */}
                            {hasSubMilestones && isExpandedInList && (
                              <div className="ml-6 mt-1 space-y-1 border-l-2 border-slate-200 dark:border-slate-700 pl-4">
                                {subMilestones.map(subMilestone => {
                                  const subPhaseConfig = PHASE_CONFIG[subMilestone.phase_type] || PHASE_CONFIG.development;
                                  return (
                                    <div 
                                      key={subMilestone.id}
                                      className="flex items-center justify-between p-2 bg-slate-50/50 dark:bg-slate-800/50 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-sm"
                                      onMouseEnter={() => setHoveredMilestone(subMilestone.id)}
                                      onMouseLeave={() => setHoveredMilestone(null)}
                                    >
                                      <div className="flex items-center gap-2">
                                        <CornerDownRight className="h-3 w-3 text-muted-foreground" />
                                        <span className="text-xs">{subPhaseConfig.icon}</span>
                                        <div className={`w-2 h-2 rounded-full ${getMilestoneColor(subMilestone.status)}`} />
                                        <div>
                                          <p className="font-medium text-xs">
                                            {subMilestone.name}
                                            {(subMilestone.progress || 0) > 0 && (
                                              <span className="ml-1 text-green-500">({subMilestone.progress}%)</span>
                                            )}
                                          </p>
                                          <p className="text-[10px] text-muted-foreground">
                                            {subMilestone.start_date && `${format(parseLocalDate(subMilestone.start_date), 'MMM d')} → `}
                                            {format(parseLocalDate(subMilestone.target_date), 'MMM d, yyyy')}
                                          </p>
                                        </div>
                                      </div>
                                      {canEdit && (
                                        <div className="flex items-center gap-1">
                                          {/* Progress Slider for sub-milestone */}
                                          {subMilestone.status !== 'completed' && (
                                            <div className="flex items-center gap-1 mr-2">
                                              <Slider
                                                value={[subMilestone.progress || 0]}
                                                onValueCommit={(values) => {
                                                  updateMilestone(subMilestone.id, { 
                                                    progress: values[0],
                                                    status: values[0] >= 100 ? 'completed' : 
                                                            values[0] > 0 ? 'in_progress' : subMilestone.status
                                                  });
                                                }}
                                                max={100}
                                                step={5}
                                                className="w-16 h-2"
                                              />
                                              <span className="text-[9px] w-6">{subMilestone.progress || 0}%</span>
                                            </div>
                                          )}
                                          <Select
                                            value={subMilestone.status}
                                            onValueChange={(value) => updateMilestone(subMilestone.id, { status: value })}
                                          >
                                            <SelectTrigger className="h-6 w-20 text-[10px]">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="pending">Pending</SelectItem>
                                              <SelectItem value="in_progress">In Progress</SelectItem>
                                              <SelectItem value="completed">Completed</SelectItem>
                                              <SelectItem value="delayed">Delayed</SelectItem>
                                            </SelectContent>
                                          </Select>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => openEditMilestone(subMilestone)}
                                            className="h-6 w-6 p-0 text-blue-500"
                                            title="Edit sub-task"
                                          >
                                            <Pencil className="h-2.5 w-2.5" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => deleteMilestone(subMilestone.id)}
                                            className="h-6 w-6 p-0 text-red-500"
                                            title="Delete sub-task"
                                          >
                                            <Trash2 className="h-2.5 w-2.5" />
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                    </>
                  )}
                </div>
              )}

              {timelineData.milestones?.length === 0 && (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No milestones defined yet</p>
                  {canEdit && (
                    <Button className="mt-4" onClick={() => setShowAddMilestone(true)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add First Milestone
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Milestone Dialog */}
      <Dialog open={showAddMilestone} onOpenChange={(open) => {
        if (!open) {
          setEditingMilestone(null);
          setMilestoneForm({ name: '', description: '', start_date: '', target_date: '', status: 'pending', phase_type: 'development', hub: 'none', is_mvp_milestone: false, dependencies: [], parent_id: null, phase_dates: defaultPhaseDates });
        }
        setShowAddMilestone(open);
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingMilestone ? 'Edit Milestone' : 'Add Timeline Milestone'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div>
              <Label>Milestone Name</Label>
              <Input
                value={milestoneForm.name}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })}
                placeholder="e.g., Sales Hub Launch"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={milestoneForm.description}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
                placeholder="Details about this milestone..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date (optional)</Label>
                <Input
                  type="date"
                  value={milestoneForm.start_date}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, start_date: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">When work begins on this milestone</p>
              </div>
              <div>
                <Label>Target/End Date</Label>
                <Input
                  type="date"
                  value={milestoneForm.target_date}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, target_date: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">Expected completion date</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phase Type</Label>
                <Select
                  value={milestoneForm.phase_type}
                  onValueChange={(value) => setMilestoneForm({ ...milestoneForm, phase_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planning">📋 Planning</SelectItem>
                    <SelectItem value="design">🎨 Design</SelectItem>
                    <SelectItem value="development">💻 Development</SelectItem>
                    <SelectItem value="internal_testing">🧪 Internal Testing</SelectItem>
                    <SelectItem value="staff_testing">👥 Staff Testing</SelectItem>
                    <SelectItem value="deploy_ready">🚀 Deploy Ready</SelectItem>
                    <SelectItem value="full_testing">✅ Full Testing</SelectItem>
                    <SelectItem value="mvp_ready">🎯 MVP Ready</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Determines color in Gantt chart</p>
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={milestoneForm.status}
                  onValueChange={(value) => setMilestoneForm({ ...milestoneForm, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="delayed">Delayed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Hub (optional)</Label>
              <Select
                value={milestoneForm.hub}
                onValueChange={(value) => setMilestoneForm({ ...milestoneForm, hub: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select hub" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="Sales">Sales</SelectItem>
                  <SelectItem value="Operations">Operations</SelectItem>
                  <SelectItem value="Marketing">Marketing</SelectItem>
                  <SelectItem value="Finance">Finance</SelectItem>
                  <SelectItem value="Platform">Platform</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Parent Milestone - for sub-milestones */}
            <div>
              <Label>Parent Milestone (optional)</Label>
              <Select
                value={milestoneForm.parent_id || 'none'}
                onValueChange={(value) => setMilestoneForm({ ...milestoneForm, parent_id: value === 'none' ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parent milestone (for sub-milestones)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Top-level milestone)</SelectItem>
                  {(timelineData?.all_milestones || timelineData?.milestones || [])
                    .filter(m => !m.parent_id && m.id !== editingMilestone?.id)
                    .map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Make this a sub-task under a parent milestone</p>
            </div>
            
            {/* Phase Dates Section */}
            <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-800/50">
              <Label className="flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4" />
                Phase Timeline (optional)
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                Define start and end dates for each development phase within this milestone
              </p>
              <div className="space-y-3">
                {[
                  { key: 'planning', label: 'Planning', icon: '📋' },
                  { key: 'design', label: 'Design', icon: '🎨' },
                  { key: 'development', label: 'Development', icon: '💻' },
                  { key: 'internal_testing', label: 'Internal Testing', icon: '🧪' },
                  { key: 'staff_testing', label: 'Staff Testing', icon: '👥' },
                  { key: 'deploy_ready', label: 'Deploy Ready', icon: '🚀' },
                  { key: 'full_testing', label: 'Full Testing', icon: '✅' }
                ].map(phase => (
                  <div key={phase.key} className="grid grid-cols-[140px_1fr_1fr] gap-2 items-center">
                    <div className="flex items-center gap-2 text-sm">
                      <span>{phase.icon}</span>
                      <span className="font-medium">{phase.label}</span>
                    </div>
                    <Input
                      type="date"
                      value={milestoneForm.phase_dates?.[phase.key]?.start || ''}
                      onChange={(e) => setMilestoneForm({
                        ...milestoneForm,
                        phase_dates: {
                          ...milestoneForm.phase_dates,
                          [phase.key]: {
                            ...milestoneForm.phase_dates?.[phase.key],
                            start: e.target.value
                          }
                        }
                      })}
                      className="h-8 text-xs"
                      placeholder="Start"
                    />
                    <Input
                      type="date"
                      value={milestoneForm.phase_dates?.[phase.key]?.end || ''}
                      onChange={(e) => setMilestoneForm({
                        ...milestoneForm,
                        phase_dates: {
                          ...milestoneForm.phase_dates,
                          [phase.key]: {
                            ...milestoneForm.phase_dates?.[phase.key],
                            end: e.target.value
                          }
                        }
                      })}
                      className="h-8 text-xs"
                      placeholder="End"
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Tip: Only fill in phases relevant to this milestone. Leave empty phases blank.
              </p>
            </div>
            
            {/* Dependencies Section */}
            {timelineData.milestones?.filter(m => m.id !== editingMilestone?.id).length > 0 && (
              <div>
                <Label className="flex items-center gap-2">
                  <GitBranch className="h-4 w-4" />
                  Dependencies (optional)
                </Label>
                <p className="text-xs text-muted-foreground mb-2">Select milestones that must complete before this one</p>
                <div className="border rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
                  {timelineData.milestones
                    ?.filter(m => m.id !== editingMilestone?.id)
                    .map(m => {
                      const isChecked = milestoneForm.dependencies?.includes(m.id);
                      return (
                        <label key={m.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 dark:hover:bg-slate-800 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setMilestoneForm({ 
                                  ...milestoneForm, 
                                  dependencies: [...(milestoneForm.dependencies || []), m.id] 
                                });
                              } else {
                                setMilestoneForm({ 
                                  ...milestoneForm, 
                                  dependencies: milestoneForm.dependencies?.filter(d => d !== m.id) || []
                                });
                              }
                            }}
                            className="h-4 w-4"
                          />
                          <span className="text-sm">{PHASE_CONFIG[m.phase_type]?.icon || '💻'}</span>
                          <span className="text-sm flex-1">{m.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {m.target_date && format(parseLocalDate(m.target_date), 'MMM d')}
                          </span>
                        </label>
                      );
                    })}
                </div>
                {milestoneForm.dependencies?.length > 0 && (
                  <p className="text-xs text-blue-500 mt-1">
                    {milestoneForm.dependencies.length} dependency selected - arrows will connect on Gantt chart
                  </p>
                )}
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_mvp_milestone"
                checked={milestoneForm.is_mvp_milestone}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, is_mvp_milestone: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="is_mvp_milestone" className="cursor-pointer">
                Major MVP Milestone (highlighted on timeline)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddMilestone(false);
              setEditingMilestone(null);
              setMilestoneForm({ name: '', description: '', start_date: '', target_date: '', status: 'pending', phase_type: 'development', hub: 'none', is_mvp_milestone: false, dependencies: [] });
            }}>Cancel</Button>
            <Button 
              onClick={editingMilestone ? saveEditedMilestone : addMilestone} 
              disabled={!milestoneForm.name || !milestoneForm.target_date}
            >
              {editingMilestone ? 'Save Changes' : 'Add Milestone'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Testing Period Dialog */}
      <Dialog open={showAddTestPeriod} onOpenChange={(open) => {
        if (!open) {
          setEditingTestPeriod(null);
          setTestPeriodForm({ name: '', start_date: '', end_date: '', description: '' });
        }
        setShowAddTestPeriod(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-500" />
              {editingTestPeriod ? 'Edit Testing Period' : 'Add Testing Period'}
            </DialogTitle>
            <DialogDescription>
              {editingTestPeriod ? 'Modify this testing/QA period' : 'Define a testing/QA period to show on the timeline'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Testing Period Name</Label>
              <Input
                value={testPeriodForm.name}
                onChange={(e) => setTestPeriodForm({ ...testPeriodForm, name: e.target.value })}
                placeholder="e.g., Alpha Testing, Beta Testing, UAT"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={testPeriodForm.start_date}
                  onChange={(e) => setTestPeriodForm({ ...testPeriodForm, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={testPeriodForm.end_date}
                  onChange={(e) => setTestPeriodForm({ ...testPeriodForm, end_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={testPeriodForm.description}
                onChange={(e) => setTestPeriodForm({ ...testPeriodForm, description: e.target.value })}
                placeholder="Testing scope and objectives..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddTestPeriod(false);
              setEditingTestPeriod(null);
              setTestPeriodForm({ name: '', start_date: '', end_date: '', description: '' });
            }}>Cancel</Button>
            <Button 
              onClick={editingTestPeriod ? saveEditedTestPeriod : addTestingPeriod} 
              disabled={!testPeriodForm.name || !testPeriodForm.start_date || !testPeriodForm.end_date}
            >
              {editingTestPeriod ? 'Save Changes' : 'Add Testing Period'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Milestone Detail View Dialog */}
      <Dialog open={!!selectedMilestone} onOpenChange={(open) => !open && setSelectedMilestone(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedMilestone && (() => {
            const milestone = selectedMilestone;
            const phaseConfig = PHASE_CONFIG[milestone.phase_type] || PHASE_CONFIG.development;
            const isOnCriticalPath = criticalPathIds.includes(milestone.id);
            const dependencyMilestones = milestone.dependencies?.map(depId => 
              timelineData.milestones?.find(m => m.id === depId)
            ).filter(Boolean) || [];
            const dependentMilestones = timelineData.milestones?.filter(m => 
              m.dependencies?.includes(milestone.id)
            ) || [];
            
            // Calculate days remaining or overdue
            const today = new Date();
            const targetDate = parseLocalDate(milestone.target_date);
            const daysRemaining = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
            
            return (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{phaseConfig.icon}</span>
                      <div>
                        <DialogTitle className="text-xl flex items-center gap-2">
                          {milestone.name}
                          {isOnCriticalPath && (
                            <Badge variant="destructive" className="bg-orange-500 text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Critical Path
                            </Badge>
                          )}
                          {milestone.is_mvp_milestone && (
                            <Badge className="bg-blue-500 text-xs">
                              <Flag className="h-3 w-3 mr-1" />
                              MVP
                            </Badge>
                          )}
                        </DialogTitle>
                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                          <Badge variant="outline">{phaseConfig.label}</Badge>
                          {milestone.hub && <Badge variant="secondary">{milestone.hub} Hub</Badge>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={
                          milestone.status === 'completed' ? 'default' :
                          milestone.status === 'delayed' ? 'destructive' :
                          milestone.status === 'in_progress' ? 'secondary' : 'outline'
                        }
                        className={milestone.status === 'completed' ? 'bg-green-500' : ''}
                      >
                        {milestone.status === 'completed' && <Check className="h-3 w-3 mr-1" />}
                        {milestone.status.charAt(0).toUpperCase() + milestone.status.slice(1).replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                  {/* Description */}
                  {milestone.description && (
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-2">Description</h4>
                      <p className="text-sm">{milestone.description}</p>
                    </div>
                  )}

                  {/* Timeline Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">Start Date</p>
                      <p className="font-medium">
                        {milestone.start_date ? format(parseLocalDate(milestone.start_date), 'MMM d, yyyy') : 'Not set'}
                      </p>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">Target Date</p>
                      <p className="font-medium">{format(parseLocalDate(milestone.target_date), 'MMM d, yyyy')}</p>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">Days Remaining</p>
                      <p className={`font-medium ${daysRemaining < 0 ? 'text-red-500' : daysRemaining <= 7 ? 'text-orange-500' : 'text-green-500'}`}>
                        {milestone.status === 'completed' ? '✓ Complete' : 
                         daysRemaining < 0 ? `${Math.abs(daysRemaining)} days overdue` : 
                         daysRemaining === 0 ? 'Due today' : 
                         `${daysRemaining} days`}
                      </p>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">Duration</p>
                      <p className="font-medium">
                        {milestone.start_date ? 
                          `${Math.ceil((parseLocalDate(milestone.target_date) - parseLocalDate(milestone.start_date)) / (1000 * 60 * 60 * 24))} days` : 
                          'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Phase Timeline */}
                  {milestone.phase_dates && Object.values(milestone.phase_dates).some(p => p?.start || p?.end) && (
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Phase Timeline
                      </h4>
                      <div className="space-y-2">
                        {[
                          { key: 'planning', label: 'Planning', icon: '📋' },
                          { key: 'design', label: 'Design', icon: '🎨' },
                          { key: 'development', label: 'Development', icon: '💻' },
                          { key: 'internal_testing', label: 'Internal Testing', icon: '🧪' },
                          { key: 'staff_testing', label: 'Staff Testing', icon: '👥' },
                          { key: 'deploy_ready', label: 'Deploy Ready', icon: '🚀' },
                          { key: 'full_testing', label: 'Full Testing', icon: '✅' }
                        ].filter(phase => 
                          milestone.phase_dates?.[phase.key]?.start || milestone.phase_dates?.[phase.key]?.end
                        ).map(phase => {
                          const phaseData = milestone.phase_dates?.[phase.key];
                          return (
                            <div key={phase.key} className="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                              <span className="text-lg">{phase.icon}</span>
                              <span className="font-medium text-sm w-32">{phase.label}</span>
                              <div className="flex-1 flex items-center gap-2 text-sm">
                                {phaseData?.start && (
                                  <span>{format(parseLocalDate(phaseData.start), 'MMM d')}</span>
                                )}
                                {phaseData?.start && phaseData?.end && (
                                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                )}
                                {phaseData?.end && (
                                  <span>{format(parseLocalDate(phaseData.end), 'MMM d, yyyy')}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Dependencies */}
                  {(dependencyMilestones.length > 0 || dependentMilestones.length > 0) && (
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                        <GitBranch className="h-4 w-4" />
                        Dependencies
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {dependencyMilestones.length > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-2">Depends On ({dependencyMilestones.length})</p>
                            <div className="space-y-1">
                              {dependencyMilestones.map(dep => (
                                <div 
                                  key={dep.id} 
                                  className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                                  onClick={() => setSelectedMilestone(dep)}
                                >
                                  <span>{PHASE_CONFIG[dep.phase_type]?.icon || '💻'}</span>
                                  <span className="text-sm flex-1">{dep.name}</span>
                                  <Badge variant={dep.status === 'completed' ? 'default' : 'outline'} className={`text-[10px] ${dep.status === 'completed' ? 'bg-green-500' : ''}`}>
                                    {dep.status === 'completed' ? '✓' : dep.status}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {dependentMilestones.length > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-2">Required By ({dependentMilestones.length})</p>
                            <div className="space-y-1">
                              {dependentMilestones.map(dep => (
                                <div 
                                  key={dep.id} 
                                  className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                                  onClick={() => setSelectedMilestone(dep)}
                                >
                                  <span>{PHASE_CONFIG[dep.phase_type]?.icon || '💻'}</span>
                                  <span className="text-sm flex-1">{dep.name}</span>
                                  <Badge variant={dep.status === 'completed' ? 'default' : 'outline'} className={`text-[10px] ${dep.status === 'completed' ? 'bg-green-500' : ''}`}>
                                    {dep.status === 'completed' ? '✓' : dep.status}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Progress Indicator */}
                  {milestone.start_date && (
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-3">Progress</h4>
                      <div className="relative">
                        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          {(() => {
                            const start = parseLocalDate(milestone.start_date);
                            const end = parseLocalDate(milestone.target_date);
                            const total = end - start;
                            const elapsed = today - start;
                            let progress = milestone.status === 'completed' ? 100 : Math.min(100, Math.max(0, (elapsed / total) * 100));
                            return (
                              <div 
                                className={`h-full transition-all duration-500 ${
                                  milestone.status === 'completed' ? 'bg-green-500' :
                                  milestone.status === 'delayed' ? 'bg-red-500' :
                                  progress > 80 ? 'bg-orange-500' : 'bg-blue-500'
                                }`}
                                style={{ width: `${progress}%` }}
                              />
                            );
                          })()}
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>{format(parseLocalDate(milestone.start_date), 'MMM d')}</span>
                          <span>{format(parseLocalDate(milestone.target_date), 'MMM d')}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter className="mt-6">
                  {canEdit && (
                    <Button variant="outline" onClick={() => {
                      setSelectedMilestone(null);
                      openEditMilestone(milestone);
                    }}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit Milestone
                    </Button>
                  )}
                  <Button onClick={() => setSelectedMilestone(null)}>
                    Close
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

export default FivePillarsHub;
